import { logs, traces, sem, otel } from "@repo/core/universal";
// @ts-ignore yes, some imports could be not used 🙄
import { ensureEndsWith, nanoid, type Serializable, type SerializableObject, type BigNumber, type AsyncOrSync, type Condition, keyOrDie, indexOrDie, firstOrDie, definedOrDie, serialize } from "@repo/utils";
import cookie from "cookie";
export function createClient(options: {
    baseURL: string;
    fetch?: typeof fetch;
    headers?: Record<string, string>;
    query?: Record<string, string>;
    timeoutMs?: number;
    fetchOptions?: RequestInit;
    tryParseResponse?: (response: Response, cookies: Record<string, string | undefined>) => AsyncOrSync<[
        true,
        unknown
    ] | [
        false
    ]>;
}) {
    function handler<T extends {
        params?: Record<string, string>;
        query?: Record<string, string | string[] | undefined>;
        headers?: Record<string, string | string[]>;
        cookie?: Record<string, string>;
        jsonBody?: Serializable;
        textBody?: string;
        formDataBody?: FormData;
    }, R>(method: string, pathPattern: string) {
        return async (args: T & {
            tryParseResponse?: (response: Response, cookies: Record<string, string | undefined>) => AsyncOrSync<[
                true,
                unknown
            ] | [
                false
            ]>;
        }): Promise<R> => {
            const patternRegExp = /\{([^}]+)\}/g;
            const placeholders = Array.from(pathPattern.matchAll(patternRegExp));
            const parts: string[] = [];
            let j = 0;
            for (const p of placeholders) {
                parts.push(pathPattern.slice(j, p.index));
                parts.push(keyOrDie(args.params ?? {}, indexOrDie(p, 1)));
                j = p.index + firstOrDie(p).length;
            }
            parts.push(pathPattern.slice(j));
            const path = parts.join("");
            const url = path !== "/" ? new URL(path.slice(1), ensureEndsWith(options.baseURL, "/")) : new URL(options.baseURL);
            logs.debug `Path: ${path}, url ${url.toString()}`;
            const query1 = options.query ?? {};
            const query2 = args.query ?? {};
            url.search = new URLSearchParams(Object.entries({ ...query1, ...query2 }).reduce<[
                string,
                string
            ][]>((acc, [k, v]) => {
                if (v !== undefined) {
                    if (Array.isArray(v)) {
                        for (const x of v) {
                            acc.push([k, x]);
                        }
                    }
                    else {
                        acc.push([k, v]);
                    }
                }
                return acc;
            }, [])).toString();
            const headers = new Headers([
                ...Object.entries({
                    ...("jsonBody" in args ? { "Content-Type": "application/json" } : {}),
                    ...options.headers,
                    ...args.headers,
                } as Record<string, string | string[]>).flatMap(([k, v]) => typeof v === "string" ? [[k, v]] : v.map(y => [k, y])),
                ...(args.cookie ? Object.entries(args.cookie).map(([k, v]) => ["cookie", cookie.serialize(k, v)]) : []),
            ] as [
                string,
                string
            ][]);
            const id = nanoid();
            logs.debug `Executing HTTP request ${id} ${{ url, args, options }}`;
            return traces.span("fetch", {
                kind: otel.SpanKind.CLIENT,
                attributes: {
                    [sem.ATTR_HTTP_REQUEST_METHOD]: method,
                },
            }, async (span) => {
                span.setAttribute(sem.ATTR_URL_PATH, path);
                logs.info `HTTP request: ${method} ${path}`;
                logs.debug `Executing HTTP request ${id} ${{ url, args, options }}`;
                const body = "jsonBody" in args ? serialize(definedOrDie(args.jsonBody)) : args.formDataBody ?? args.textBody;
                try {
                    const response = await (options.fetch ?? fetch)(url.toString(), {
                        method,
                        headers,
                        body,
                        signal: AbortSignal.timeout(options.timeoutMs ?? 10000),
                        ...options.fetchOptions,
                    });
                    logs.debug `Got HTTP response ${id} ${{ status: response.status }}`;
                    span.setAttribute(sem.ATTR_HTTP_RESPONSE_STATUS_CODE, response.status);
                    logs.info `HTTP response: ${response.status} ${response.statusText}`;
                    const cookies = response.headers.getSetCookie().reduce((acc, cur) => {
                        const parsed = cookie.parse(cur);
                        return { ...acc, ...parsed };
                    }, {} as Record<string, string | undefined>);
                    let data;
                    const result = await (args.tryParseResponse ?? options.tryParseResponse)?.(response, cookies);
                    if (result && result[0]) {
                        data = result[1];
                    }
                    else {
                        if (response.status < 200 || response.status > 206) {
                            throw new Error(`Failed backend HTTP request: ${response.status} ${await response.text()}`);
                        }
                        data = response.headers.get("content-type")?.includes("json") ? await response.json() : await response.text();
                    }
                    return data as R;
                }
                catch (e) {
                    if (e instanceof Error) {
                        logs.error(e) `Failed backend HTTP request ${id}`;
                    }
                    throw e;
                }
            });
        };
    }
    return {
        /** DELETE /api/admin/organizations/{organizationId}/abstract-sources/{id} */
        "Delete abstract entity source": handler<{
            params: {
                "organizationId": string;
                "id": `abs_${string}`;
            };
        }, Res["Delete abstract entity source"]>("DELETE", "/api/admin/organizations/{organizationId}/abstract-sources/{id}"),
        /** GET /api/abstract-sources/{sourceId}/entities/{id} */
        "Get abstract entity": handler<{
            params: {
                "sourceId": `abs_${string}`;
                "id": `abe_${string}`;
            };
        }, Res["Get abstract entity"]>("GET", "/api/abstract-sources/{sourceId}/entities/{id}"),
        /** GET /api/admin/assistant-cases/{caseId}/file/download */
        "Download assistant case file": handler<{
            params: {
                "caseId": `acs_${string}`;
            };
            query?: {
                "redirect"?: "true";
            };
        }, Res["Download assistant case file"]>("GET", "/api/admin/assistant-cases/{caseId}/file/download"),
        /** GET /api/admin/inboxes */
        "Search inboxes": handler<{
            query?: {
                "organizationId"?: string;
            };
        }, Res["Search inboxes"]>("GET", "/api/admin/inboxes"),
        /** GET /api/admin/inboxes/{inboxId}/exchange-threads */
        "Get exchange thread": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            query: {
                "externalId": string;
            };
        }, Res["Get exchange thread"]>("GET", "/api/admin/inboxes/{inboxId}/exchange-threads"),
        /** GET /api/admin/inboxes/{inboxId}/external-attachments */
        "Get external attachment": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            query: {
                "externalMessageId": string;
                "externalAttachmentId": string;
            };
        }, Res["Get external attachment"]>("GET", "/api/admin/inboxes/{inboxId}/external-attachments"),
        /** GET /api/admin/inboxes/{inboxId}/external-threads */
        "Get external thread": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            query: {
                "id": `thr_${string}`;
            } | {
                "externalId": string;
            };
        }, Res["Get external thread"]>("GET", "/api/admin/inboxes/{inboxId}/external-threads"),
        /** GET /api/admin/inboxes/{inboxId}/folders */
        "Get inbox folders": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
        }, Res["Get inbox folders"]>("GET", "/api/admin/inboxes/{inboxId}/folders"),
        /** GET /api/admin/mailbox */
        "Mailbox view": handler<{
            query: {
                "q"?: string;
                "inboxId"?: string;
                "page": string;
            };
        }, Res["Mailbox view"]>("GET", "/api/admin/mailbox"),
        /** GET /api/admin/organizations/{organizationId}/abstract-sources */
        "List abstract entity sources": handler<{
            params: {
                "organizationId": string;
            };
        }, Res["List abstract entity sources"]>("GET", "/api/admin/organizations/{organizationId}/abstract-sources"),
        /** GET /api/admin/organizations/{organizationId}/abstract-sources/{id} */
        "Get abstract entity source": handler<{
            params: {
                "organizationId": string;
                "id": `abs_${string}`;
            };
        }, Res["Get abstract entity source"]>("GET", "/api/admin/organizations/{organizationId}/abstract-sources/{id}"),
        /** GET /api/admin/organizations/{organizationId}/erp/po/{number} */
        "Fetch Purchase Order from ERP": handler<{
            params: {
                "organizationId": string;
                "number": string;
            };
        }, Res["Fetch Purchase Order from ERP"]>("GET", "/api/admin/organizations/{organizationId}/erp/po/{number}"),
        /** GET /api/admin/organizations/{organizationId}/erp/po/{number}/raw */
        "Fetch Purchase Order from ERP (RAW)": handler<{
            params: {
                "organizationId": string;
                "number": string;
            };
            query?: {
                "forceJson"?: "true";
            };
        }, Res["Fetch Purchase Order from ERP (RAW)"]>("GET", "/api/admin/organizations/{organizationId}/erp/po/{number}/raw"),
        /** GET /api/admin/organizations/{organizationId}/erp/supplier/email */
        "Get Supplier email": handler<{
            params: {
                "organizationId": string;
            };
            query?: {
                "poNumber"?: string;
                "supplierId"?: string;
            };
        }, Res["Get Supplier email"]>("GET", "/api/admin/organizations/{organizationId}/erp/supplier/email"),
        /** GET /api/admin/organizations/{organizationId}/oc/cases/po/{threadProcessingId} */
        "Get Raw OC PO cases": handler<{
            params: {
                "organizationId": string;
                "threadProcessingId": string;
            };
        }, Res["Get Raw OC PO cases"]>("GET", "/api/admin/organizations/{organizationId}/oc/cases/po/{threadProcessingId}"),
        /** GET /api/admin/organizations/{organizationId}/oc/cases/thread/{threadId} */
        "Get Raw OC thread cases": handler<{
            params: {
                "organizationId": string;
                "threadId": `thr_${string}`;
            };
        }, Res["Get Raw OC thread cases"]>("GET", "/api/admin/organizations/{organizationId}/oc/cases/thread/{threadId}"),
        /** GET /api/admin/organizations/{organizationId}/settings */
        "Get Organization Settings": handler<{
            params: {
                "organizationId": string;
            };
            query?: {
                "key"?: string;
                "inboxId"?: `inb_${string}`;
                "userId"?: string;
            };
        }, Res["Get Organization Settings"]>("GET", "/api/admin/organizations/{organizationId}/settings"),
        /** GET /api/approvals/{approvalTaskId} */
        "Get approval task": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            query: {
                "token": string;
            };
        }, Res["Get approval task"]>("GET", "/api/approvals/{approvalTaskId}"),
        /** GET /api/approvals/{approvalTaskId}/assistant */
        "Approval assistant view": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            query: {
                "token": string;
            };
        }, Res["Approval assistant view"]>("GET", "/api/approvals/{approvalTaskId}/assistant"),
        /** GET /api/approvals/{approvalTaskId}/thread */
        "Get thread for approval task": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            query: {
                "token": string;
            };
        }, Res["Get thread for approval task"]>("GET", "/api/approvals/{approvalTaskId}/thread"),
        /** GET /api/approvals/meta */
        "Get approval meta": handler<{
            query: {
                "token": string;
            };
        }, Res["Get approval meta"]>("GET", "/api/approvals/meta"),
        /** GET /api/assistant/{threadId} */
        "Assistant view": handler<{
            params: {
                "threadId": `thr_${string}` | string;
            };
            query?: {
                "processingId"?: string;
            };
        }, Res["Assistant view"]>("GET", "/api/assistant/{threadId}"),
        /** GET /api/assistant/meta */
        "Get assistant meta": handler<{}, Res["Get assistant meta"]>("GET", "/api/assistant/meta"),
        /** GET /api/auth/email-policy */
        "Get auth email policy": handler<{
            query: {
                "email": string;
            };
        }, Res["Get auth email policy"]>("GET", "/api/auth/email-policy"),
        /** GET /api/cases/acknowledgements */
        "Get cases acknowledgements": handler<{
            query?: {
                "type"?: string;
            };
        }, Res["Get cases acknowledgements"]>("GET", "/api/cases/acknowledgements"),
        /** GET /api/cases/acknowledgements/{caseId} */
        "Get case content": handler<{
            params: {
                "caseId": `acs_${string}`;
            };
        }, Res["Get case content"]>("GET", "/api/cases/acknowledgements/{caseId}"),
        /** GET /api/dashboard */
        "Get dashboard data": handler<{
            query: {
                "by"?: "date_id" | "week_start" | "month_start";
                "from": string;
                "to": string;
            };
        }, Res["Get dashboard data"]>("GET", "/api/dashboard"),
        /** GET /api/dashboard/meta */
        "Get dashboard meta": handler<{}, Res["Get dashboard meta"]>("GET", "/api/dashboard/meta"),
        /** GET /api/dashboard/so/transaction/{transactionId} */
        "Get SO transaction view": handler<{
            params: {
                "transactionId": `tra_${string}`;
            };
        }, Res["Get SO transaction view"]>("GET", "/api/dashboard/so/transaction/{transactionId}"),
        /** GET /api/dashboard/so/transaction/{transactionId}/diff */
        "Get sales order diff data": handler<{
            params: {
                "transactionId": `tra_${string}`;
            };
        }, Res["Get sales order diff data"]>("GET", "/api/dashboard/so/transaction/{transactionId}/diff"),
        /** GET /api/dashboard/v2 */
        "Get dashboard data v2": handler<{
            query: {
                "by"?: "date_id" | "week_start" | "month_start";
                "from": string;
                "to": string;
                "inboxIds"?: string;
                "partnerName"?: string;
            };
        }, Res["Get dashboard data v2"]>("GET", "/api/dashboard/v2"),
        /** GET /api/dashboard/v2/oc-transactions */
        "Get dashboard OC transactions v2": handler<{
            query: {
                "from": string;
                "to": string;
                "inboxIds"?: string;
                "partnerName"?: string;
            };
        }, Res["Get dashboard OC transactions v2"]>("GET", "/api/dashboard/v2/oc-transactions"),
        /** GET /api/dashboard/v2/transactions */
        "Get dashboard transactions v2": handler<{
            query: {
                "from": string;
                "to": string;
                "inboxIds"?: string;
                "partnerName"?: string;
            };
        }, Res["Get dashboard transactions v2"]>("GET", "/api/dashboard/v2/transactions"),
        /** GET /api/documents */
        "Get document content": handler<{
            query: {
                "id": `doc_${string}`;
            } | {
                "externalId": string;
            };
        }, Res["Get document content"]>("GET", "/api/documents"),
        /** GET /api/organizations/{organizationId}/erp/po/{number}/approvers */
        "Pre resolve approvers": handler<{
            params: {
                "organizationId": string;
                "number": string;
            };
            query?: {
                "withReplacement"?: string;
            };
        }, Res["Pre resolve approvers"]>("GET", "/api/organizations/{organizationId}/erp/po/{number}/approvers"),
        /** GET /api/organizations/{organizationId}/inboxes/{inboxId}/threads/{threadId} */
        "Get thread by id": handler<{
            params: {
                "organizationId": string;
                "inboxId": `inb_${string}`;
                "threadId": `thr_${string}`;
            };
        }, Res["Get thread by id"]>("GET", "/api/organizations/{organizationId}/inboxes/{inboxId}/threads/{threadId}"),
        /** GET /api/organizations/{organizationId}/sources */
        "Get entity sources": handler<{
            params: {
                "organizationId": string;
            };
            query?: {
                "entity"?: string;
            };
        }, Res["Get entity sources"]>("GET", "/api/organizations/{organizationId}/sources"),
        /** GET /api/organizations/{organizationId}/sources/{entitySourceId} */
        "Get entity fields": handler<{
            params: {
                "organizationId": string;
                "entitySourceId": `src_${string}`;
            };
        }, Res["Get entity fields"]>("GET", "/api/organizations/{organizationId}/sources/{entitySourceId}"),
        /** GET /api/organizations/{organizationId}/threads */
        "Get threads by organizationId": handler<{
            params: {
                "organizationId": string;
            };
            query?: {
                "q"?: string;
                "offset"?: string;
                "limit"?: string;
            };
        }, Res["Get threads by organizationId"]>("GET", "/api/organizations/{organizationId}/threads"),
        /** GET /api/salesorder/customers/search */
        "Search customers by name, partner number, or billing address": handler<{
            query: {
                "q": string;
                "inboxId": `inb_${string}`;
            };
        }, Res["Search customers by name, partner number, or billing address"]>("GET", "/api/salesorder/customers/search"),
        /** GET /api/salesorder/duplicate */
        "Get sales order duplicate": handler<{
            query: {
                "customerAccountId": string;
                "orderReference": string;
                "transactionType": "ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER";
                "excludeSuggestedTransactionId": string;
            };
        }, Res["Get sales order duplicate"]>("GET", "/api/salesorder/duplicate"),
        /** GET /api/salesorder/relevant-transaction-email-parts/{transactionId} */
        "Get relevant transaction email parts": handler<{
            params: {
                "transactionId": `tra_${string}`;
            };
        }, Res["Get relevant transaction email parts"]>("GET", "/api/salesorder/relevant-transaction-email-parts/{transactionId}"),
        /** GET /api/salesorder/results/{threadId} */
        "Get sales order results for thread": handler<{
            params: {
                "threadId": `thr_${string}` | string;
            };
            query?: {
                "workflowId"?: `wkf_${string}`;
                "transactionId"?: `tra_${string}`;
            };
        }, Res["Get sales order results for thread"]>("GET", "/api/salesorder/results/{threadId}"),
        /** GET /api/threads/{threadId}/pipeline */
        "Get pipeline type for thread": handler<{
            params: {
                "threadId": `thr_${string}` | string;
            };
        }, Res["Get pipeline type for thread"]>("GET", "/api/threads/{threadId}/pipeline"),
        /** GET /api/uify/{inboxId}/attachments/{attachmentId} */
        "Uify - Get attachment by id": handler<{
            params: {
                "inboxId": `inb_${string}`;
                "attachmentId": `att_${string}`;
            };
            headers: {
                "x-uify-secret": string;
            };
        }, Res["Uify - Get attachment by id"]>("GET", "/api/uify/{inboxId}/attachments/{attachmentId}"),
        /** GET /api/uify/{inboxId}/external-threads */
        "Uify - Get external thread by id": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            headers: {
                "x-uify-secret": string;
            };
            query: {
                "id": `thr_${string}`;
            } | {
                "externalId": string;
            };
        }, Res["Uify - Get external thread by id"]>("GET", "/api/uify/{inboxId}/external-threads"),
        /** GET /api/uify/{inboxId}/threads/{threadId} */
        "Uify - Get thread by id": handler<{
            params: {
                "inboxId": `inb_${string}`;
                "threadId": string;
            };
            headers: {
                "x-uify-secret": string;
            };
        }, Res["Uify - Get thread by id"]>("GET", "/api/uify/{inboxId}/threads/{threadId}"),
        /** GET /api/uify/documents */
        "Uify - Get document content": handler<{
            headers: {
                "x-uify-secret": string;
            };
            query: {
                "id": `doc_${string}`;
            } | {
                "externalId": string;
            };
        }, Res["Uify - Get document content"]>("GET", "/api/uify/documents"),
        /** GET /api/uify/salesorder/organizations/{orgId}/threads/{threadId} */
        "Uify - Get sales order results by thread id": handler<{
            params: {
                "orgId": string;
                "threadId": `thr_${string}`;
            };
            headers: {
                "x-uify-secret": string;
            };
        }, Res["Uify - Get sales order results by thread id"]>("GET", "/api/uify/salesorder/organizations/{orgId}/threads/{threadId}"),
        /** GET /api/workbench/{caseId} */
        "Get Case view": handler<{
            params: {
                "caseId": string;
            };
        }, Res["Get Case view"]>("GET", "/api/workbench/{caseId}"),
        /** GET /api/workbench/tags */
        "Get case tags": handler<{}, Res["Get case tags"]>("GET", "/api/workbench/tags"),
        /** POST /api/accessTokens/reissue */
        "Re-issue access token": handler<{
            query: {
                "token": string;
            };
        }, Res["Re-issue access token"]>("POST", "/api/accessTokens/reissue"),
        /** POST /api/admin/{organizationId}/erp/po */
        "Save PostgresErp purchaser order": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "organizationId": string;
                "number": string;
                "order": {
                    "head": {
                        "id": string;
                        "number": string;
                        "date": string;
                        "currency": string | null;
                        "supplierId": string | null;
                        "paymentTermsId": string | null;
                        "deliveryTermsId": string | null;
                        "status": string | null;
                        "responsibleBuyer1": string | null;
                        "responsibleBuyer2": string | null;
                        "responsibleBuyer3": string | null;
                        "purchaserOrg": string | null;
                        "purchaserGroup": string | null;
                        "docCategory": string | null;
                        "docType": string | null;
                        "companyCode": string | null;
                    };
                    "deliveryTerms": Record<string, {
                        "id": string;
                        "incoterms": string;
                        "location": string | null;
                    }>;
                    "paymentTerms": Record<string, {
                        "id": string;
                        "text": string;
                    }>;
                    "items": {
                        "id": string;
                        "status": string | null;
                        "poId": string;
                        "poNumber": string;
                        "positionNumber": string;
                        "ocReference": string | null;
                        "materialId": string | null;
                        "description1": string | null;
                        "description2": string | null;
                        "description3": string | null;
                        "supplierSku": string | null;
                        "deliveryAddress": string | null;
                        "quantity": number | null;
                        "quantityUom": string | null;
                        "unitPrice": number | null;
                        "priceUom": string | null;
                        "priceUnit": number | null;
                        "uomConversionCoefficient": number;
                        "netTotalPrice": number | null;
                        "grossTotalPrice": number | null;
                        "contract": string | null;
                        "requisitioner": string | null;
                        "requisitionNumber"?: string | null;
                        "requisitionItem"?: string | null;
                        "dispo": string | null;
                        "plant": string | null;
                        "confirmationControlKey": string | null;
                    }[];
                    "confirmations": Record<string, {
                        "positionNumber": string;
                        "serialNumber": number;
                        "deliveryDate": string | null;
                        "quantity": number | null;
                        "reference": string | null;
                    }[]>;
                    "schedules": Record<string, {
                        "id": string;
                        "poItemId": string;
                        "scheduleNumber": string;
                        "positionNumber": string;
                        "deliveryDate": string;
                        "deliveryDate2": string | null;
                        "quantity": number;
                        "requisitionNumber"?: string | null;
                        "requisitionItem"?: string | null;
                    }[]>;
                    "deliveryAddresses": Record<string, {
                        "id": string;
                        "addressLine1": string | null;
                        "addressLine2": string | null;
                        "addressLine3": string | null;
                        "addressLine4": string | null;
                        "street": string | null;
                        "houseNumber": string | null;
                        "zip": string | null;
                        "city": string | null;
                        "county": string | null;
                        "country": string | null;
                        "code": string | null;
                        "type": "billing" | "delivery";
                    }>;
                    "extras": {
                        "header": {
                            "id": string;
                            "externalId": {
                                "type": "Sap";
                                "PricingDocument": string;
                                "PricingDocumentItem": string | null;
                                "PricingProcedureCounter": string;
                                "PricingProcedureStep": string;
                                "ConditionType": string;
                            };
                            "calculation": {
                                "class": "percentage";
                                "rate": BigNumberValue | null;
                                "appliedTo": "gross" | "net";
                            } | {
                                "class": "fixedAmount";
                                "amount": BigNumberValue | null;
                                "distributed": boolean;
                            } | {
                                "class": "quantityBased";
                                "quantity": BigNumberValue;
                                "quantityUom": string;
                                "unitPrice": BigNumberValue;
                                "priceUom": string;
                                "perAmount": BigNumberValue;
                                "uomConversionCoefficient": BigNumberValue;
                            };
                        }[];
                        "items": Record<string, {
                            "id": string;
                            "externalId": {
                                "type": "Sap";
                                "PricingDocument": string;
                                "PricingDocumentItem": string | null;
                                "PricingProcedureCounter": string;
                                "PricingProcedureStep": string;
                                "ConditionType": string;
                            };
                            "calculation": {
                                "class": "percentage";
                                "rate": BigNumberValue | null;
                                "appliedTo": "gross" | "net";
                            } | {
                                "class": "fixedAmount";
                                "amount": BigNumberValue | null;
                                "distributed": boolean;
                            } | {
                                "class": "quantityBased";
                                "quantity": BigNumberValue;
                                "quantityUom": string;
                                "unitPrice": BigNumberValue;
                                "priceUom": string;
                                "perAmount": BigNumberValue;
                                "uomConversionCoefficient": BigNumberValue;
                            };
                        }[]>;
                    };
                };
            };
        }, Res["Save PostgresErp purchaser order"]>("POST", "/api/admin/{organizationId}/erp/po"),
        /** POST /api/admin/inboxes */
        "Register inbox": handler<{
            jsonBody: {
                "email": string;
                "provider": "GMAIL" | "EXCHANGE" | "EWS";
                "authInfo": {
                    "type"?: "DataSource";
                    "dataSourceId": string;
                    "dataSourceEnv": "staging" | "production";
                    "organizationId": number;
                } | {
                    "type": "ENTRA_APP";
                    "tenantId": string;
                    "clientId": string;
                    "clientSecret": string;
                } | {
                    "type": "EWS";
                    "host": string;
                    "port": number;
                    "servername"?: string;
                    "username": string;
                    "password": string;
                    "domain"?: string;
                    "workstation"?: string;
                };
                "idempotencyKey": string;
                "useImpersonation": boolean;
                "organizationId": string;
            };
        }, Res["Register inbox"]>("POST", "/api/admin/inboxes"),
        /** POST /api/admin/inboxes/{inboxId}/folders */
        "Ensure inbox folder": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            jsonBody: {
                "name": string;
                "parentFolderId": string;
            };
        }, Res["Ensure inbox folder"]>("POST", "/api/admin/inboxes/{inboxId}/folders"),
        /** POST /api/admin/mailbox/threads/reprocess */
        "Reprocess thread": handler<{
            jsonBody: {
                "threadId": `thr_${string}` | string;
            };
        }, Res["Reprocess thread"]>("POST", "/api/admin/mailbox/threads/reprocess"),
        /** POST /api/admin/organization/{organizationId}/erp/po/reset */
        "Reset purchase order": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "numbers"?: string[];
            };
        }, Res["Reset purchase order"]>("POST", "/api/admin/organization/{organizationId}/erp/po/reset"),
        /** POST /api/admin/organization/{organizationId}/erp/po/reset/save */
        "Save purchase order reset": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "number": string;
                "data": {
                    "POHEADER": {
                        "INCOTERMS1"?: string;
                        "INCOTERMS2"?: string;
                        "PMNTTRMS"?: string;
                    };
                    "POITEM": {
                        "PO_ITEM": string;
                        "VEND_MAT"?: string;
                        "QUANTITY"?: number;
                        "NET_PRICE"?: number;
                        "ACKNOWL_NO"?: string;
                    }[];
                    "POSCHEDULE": {
                        "PO_ITEM": string;
                        "SCHED_LINE": string;
                        "DELIVERY_DATE"?: string;
                        "STAT_DATE"?: string;
                        "QUANTITY"?: number;
                    }[];
                };
            }[];
        }, Res["Save purchase order reset"]>("POST", "/api/admin/organization/{organizationId}/erp/po/reset/save"),
        /** POST /api/admin/organizations/{organizationId}/abstract-sources */
        "Create abstract entity source": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "entity": string;
                "definition": {
                    "source": {
                        "type": "S3_EXPORT";
                        "bucket": string;
                        "key": string;
                        "format": {
                            "type": "CSV";
                            "removeDuplicateLines"?: boolean;
                            "delimiter"?: string;
                        };
                    } | {
                        "type": "ODATA";
                        "name": string;
                        "path"?: string;
                        "query"?: Record<string, string>;
                        "map": string | string[];
                        "syncState"?: string | string[];
                    } | {
                        "type": "MANUAL";
                    };
                    "fields": Record<string, {
                        "type": "string";
                        "behavior": "identity" | "reference" | "required" | "optional";
                        "indexed"?: boolean;
                    }>;
                };
            };
        }, Res["Create abstract entity source"]>("POST", "/api/admin/organizations/{organizationId}/abstract-sources"),
        /** POST /api/admin/organizations/{organizationId}/abstract-sources/{id}/entities */
        "Upsert abstract entities": handler<{
            params: {
                "organizationId": string;
                "id": `abs_${string}`;
            };
            jsonBody: {
                "entities": SerializableObject[];
            };
        }, Res["Upsert abstract entities"]>("POST", "/api/admin/organizations/{organizationId}/abstract-sources/{id}/entities"),
        /** POST /api/admin/organizations/{organizationId}/inboxes/{inboxId}/threads */
        "Upsert thread manually": handler<{
            params: {
                "organizationId": string;
                "inboxId": `inb_${string}`;
            };
            jsonBody: {
                "thread": {
                    "id": string;
                    "versionKey": string | number;
                    "messages": {
                        "id": string;
                        "subject": string | null;
                        "snippet": string | null;
                        "to": string[];
                        "cc": string[];
                        "bcc": string[];
                        "labelIds": string[];
                        "receivedAt": string;
                        "sent": boolean;
                        "senderName": string | null;
                        "senderEmail": string | null;
                        "content": string;
                        "parentFolderId": string | null;
                        "versionKey": (string | number) | null;
                        "internetMessageId"?: string | null;
                        "inReplyTo"?: string | null;
                        "conversationIndex"?: string | null;
                        "conversationIndexGuid"?: string | null;
                        "conversationIndexChildBlocks"?: string[] | null;
                        "threadTopic"?: string | null;
                        "references"?: string | null;
                        "threadIndex"?: string | null;
                        "itemId"?: string | null;
                        "raw"?: Serializable | null;
                        "attachments": {
                            "id"?: string;
                            "filename": string;
                            "contentType": string;
                            "size": number;
                            "data"?: string;
                        }[];
                    }[];
                };
            };
        }, Res["Upsert thread manually"]>("POST", "/api/admin/organizations/{organizationId}/inboxes/{inboxId}/threads"),
        /** POST /api/admin/organizations/{organizationId}/oc/cases */
        "Insert Raw OC workflow results": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "threadId": `thr_${string}`;
                "threadProcessing": {
                    "status": "pending" | "finished" | "error" | "erp-error";
                    "result": ({
                        "meta": {
                            "traceId": string;
                        };
                        "extractionResult": {
                            "confirmations": {
                                "header": {
                                    "number": string | null;
                                    "date": string;
                                    "currency": string | null;
                                    "billingAddress": string | null;
                                    "paymentTerms": string | null;
                                    "deliveryTerms": {
                                        "incoterms": string | null;
                                        "location": string | null;
                                        "additions"?: string | null;
                                    } | null;
                                };
                                "items": {
                                    "serialNumber": number;
                                    "positionNumber": string | null;
                                    "poNumber": string;
                                    "description": string | null;
                                    "articleNumbers": string[];
                                    "deliveryAddress": string | null;
                                    "deliverySchedules": {
                                        "quantity": number | null;
                                        "deliveryDate": string | null;
                                        "shipmentDate": string | null;
                                        "deliveryDateMentioned"?: boolean;
                                        "shipmentDateMentioned"?: boolean;
                                    }[];
                                    "costStructure": {
                                        "quantity": number | null;
                                        "quantityUom": string | null;
                                        "unitPrice": number | null;
                                        "priceUom": string | null;
                                        "priceUnit": number | null;
                                        "totalPrice": number | null;
                                    };
                                    "itemExtra": {
                                        "serialNumber": number;
                                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                        "description": string;
                                        "amount": number;
                                        "calculation": {
                                            "class": "percentage";
                                            "rate": BigNumberValue | null;
                                            "appliedTo": "gross" | "net";
                                        } | {
                                            "class": "fixedAmount";
                                            "amount": BigNumberValue | null;
                                            "distributed": boolean;
                                        } | {
                                            "class": "quantityBased";
                                            "quantity": BigNumberValue;
                                            "quantityUom": string;
                                            "unitPrice": BigNumberValue;
                                            "priceUom": string;
                                            "perAmount": BigNumberValue;
                                            "uomConversionCoefficient": BigNumberValue;
                                        };
                                    }[];
                                }[];
                                "headerExtra": {
                                    "serialNumber": number;
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "description": string;
                                    "amount": number;
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[];
                            }[];
                        };
                    } | {
                        "meta": {
                            "traceId": string;
                        };
                        "error": {
                            "message": string;
                        } | ({
                            "type": "noConfirmationExtracted";
                        } | {
                            "type": "unsupportedFileType";
                        } | {
                            "type": "workflowFailed";
                            "failure": {
                                "type": "NotImplemented";
                                "details": [
                                ];
                            } | {
                                "type": "ERPRequestError";
                                "details": [
                                    {
                                        "first"?: boolean;
                                        "status": number;
                                        "message": string;
                                    }
                                ];
                            } | {
                                "type": "ERPReadBusinessError";
                                "details": [
                                    {
                                        "errors": {
                                            "code": string;
                                            "message": string;
                                        }[];
                                    }
                                ];
                            } | {
                                "type": "ERPModifyBusinessError";
                                "details": [
                                    {
                                        "errors": {
                                            "code": string;
                                            "message": string;
                                        }[];
                                        "first": boolean;
                                    }
                                ];
                            } | {
                                "type": "Unknown";
                                "details": [
                                    {
                                        "type": string | null;
                                        "message": string | null;
                                    }
                                ];
                            };
                        });
                    }) | null;
                };
                "poProcessings": {
                    "number": string;
                    "status": "pending" | "review" | "error" | "confirming" | "confirmed" | "approving" | "declined" | "cancelled" | "erp-error";
                    "result": ({
                        "result": OrderConfirmationProcessingResultSchema;
                        "masterPriceWhitelist"?: {
                            "positions": Record<string, {
                                "updatable": boolean;
                                "id"?: string;
                            }>;
                        };
                        "decisions"?: Record<string, AssistantDecisionSchema>;
                        "masterPriceDecisions"?: Record<string, {
                            "type": "accepted";
                            "value": BigNumberValue | null;
                            "id"?: string;
                        } | {
                            "type": "rejected";
                        }>;
                        "update"?: {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                        "corrections"?: Record<string, (string | null) | (boolean | null) | (string | null) | {
                            "amount": {
                                "amount": BigNumberValue | null;
                                "currency": string | null;
                            } | null;
                            "quantity": {
                                "amount": BigNumberValue | null;
                                "unit": string | null;
                            } | null;
                            "extra"?: {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                                "name"?: string;
                            }[];
                        } | {
                            "amount": BigNumberValue | null;
                            "currency": string | null;
                        } | {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        } | (("open" | "partiallyConfirmed" | "confirmed") | null) | {
                            "id": string | null;
                            "source": "paymentTerms";
                        } | {
                            "incoterms1": string | null;
                            "incoterms2": string | null;
                        } | {
                            "schedules": {
                                "quantity": {
                                    "amount": BigNumberValue | null;
                                    "unit": string | null;
                                };
                                "deliveryDate": string | null;
                                "reference": string | null;
                                "status"?: string;
                            }[];
                            "combineMode"?: "partial" | "reduction";
                            "confirmations": {
                                "quantity": {
                                    "amount": BigNumberValue | null;
                                    "unit": string | null;
                                };
                                "deliveryDate": string | null;
                                "reference": string | null;
                            }[];
                        } | {
                            "lines": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                                "name"?: string;
                            }[];
                        }>;
                        "triggeredApprovalThresholds"?: (({
                            "role": string | {
                                "email": ("self") | string;
                            };
                        } & ({
                            "type": "alwaysWithin";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "anyChange";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "absoluteDifference";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "absoluteValue";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "percentage";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        })) & {
                            "key": string;
                            "before": Serializable;
                            "after": Serializable;
                        })[];
                        "triggeredAsOrderedThresholds"?: {
                            "threshold": {
                                "type": "alwaysWithin";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                                "active": boolean;
                            } | {
                                "type": "anyChange";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                                "active": boolean;
                            } | {
                                "type": "absoluteDifference";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            } | {
                                "type": "absoluteValue";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            } | {
                                "type": "percentage";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            };
                            "key": string;
                            "before": Serializable;
                            "after": Serializable;
                            "beyond": boolean;
                        }[];
                        "triggeredAutoAcceptThresholds"?: {
                            "threshold": {
                                "type": "alwaysWithin";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                                "active": boolean;
                            } | {
                                "type": "anyChange";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                                "active": boolean;
                            } | {
                                "type": "absoluteDifference";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            } | {
                                "type": "absoluteValue";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            } | {
                                "type": "percentage";
                                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                                "direction": "positive" | "negative" | "both";
                                "value": number;
                                "active": boolean;
                            };
                            "key": string;
                            "before": Serializable;
                            "after": Serializable;
                            "beyond": boolean;
                        }[];
                        "resolvedApprovers"?: {
                            "key": string;
                            "entity": Serializable;
                            "email": string | null;
                            "role": string | {
                                "email": ("self") | string;
                            };
                        }[];
                        "failure"?: {
                            "type": "NotImplemented";
                            "details": [
                            ];
                        } | {
                            "type": "ERPRequestError";
                            "details": [
                                {
                                    "first"?: boolean;
                                    "status": number;
                                    "message": string;
                                }
                            ];
                        } | {
                            "type": "ERPReadBusinessError";
                            "details": [
                                {
                                    "errors": {
                                        "code": string;
                                        "message": string;
                                    }[];
                                }
                            ];
                        } | {
                            "type": "ERPModifyBusinessError";
                            "details": [
                                {
                                    "errors": {
                                        "code": string;
                                        "message": string;
                                    }[];
                                    "first": boolean;
                                }
                            ];
                        } | {
                            "type": "Unknown";
                            "details": [
                                {
                                    "type": string | null;
                                    "message": string | null;
                                }
                            ];
                        };
                        "postProcessingState": "review" | "approval" | "submission" | "failure";
                    } | {
                        "error": {
                            "type": "noPurchaseOrderFound";
                            "number": string;
                        } | {
                            "type": "supplierProcessingDisabled";
                        } | {
                            "type": "workflowFailed";
                            "failure": {
                                "type": "NotImplemented";
                                "details": [
                                ];
                            } | {
                                "type": "ERPRequestError";
                                "details": [
                                    {
                                        "first"?: boolean;
                                        "status": number;
                                        "message": string;
                                    }
                                ];
                            } | {
                                "type": "ERPReadBusinessError";
                                "details": [
                                    {
                                        "errors": {
                                            "code": string;
                                            "message": string;
                                        }[];
                                    }
                                ];
                            } | {
                                "type": "ERPModifyBusinessError";
                                "details": [
                                    {
                                        "errors": {
                                            "code": string;
                                            "message": string;
                                        }[];
                                        "first": boolean;
                                    }
                                ];
                            } | {
                                "type": "Unknown";
                                "details": [
                                    {
                                        "type": string | null;
                                        "message": string | null;
                                    }
                                ];
                            };
                        };
                    }) | null;
                }[];
            };
        }, Res["Insert Raw OC workflow results"]>("POST", "/api/admin/organizations/{organizationId}/oc/cases"),
        /** POST /api/admin/organizations/{organizationId}/settings */
        "Upsert Organization Settings": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "key": string;
                "value": Serializable;
                "inboxId": `inb_${string}` | null;
                "userId": string | null;
            }[];
        }, Res["Upsert Organization Settings"]>("POST", "/api/admin/organizations/{organizationId}/settings"),
        /** POST /api/admin/organizations/{organizationId}/settings/specific */
        "Upsert Specific Organization Settings": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "inboxId"?: `inb_${string}`;
            } & ({
                "key": "po_settings";
                "value": {
                    "fields"?: {
                        "poNumber": {
                            "extraction"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                        };
                        "reference": {
                            "extraction"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                            "comparison": {
                                "defaultValue": string;
                            };
                        };
                        "paymentTerms"?: {
                            "extraction"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                            "comparison"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                            };
                        };
                        "deliveryTerms"?: {
                            "extraction"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                            "comparison"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                            };
                            "deliveryTerms2": "location" | "locationSensitive" | "meaning" | "description" | "ignore";
                            "useDefault": boolean;
                        };
                        "deliveryAddress"?: {
                            "extraction"?: {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                        };
                        "aswoDeliveryState"?: {};
                        "extraItems"?: {
                            "extraction": ({
                                "type": "mergeToUnitPrice";
                                "mergingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                                "movingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                                "mergingHeaderTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                                "headerTypeWhitelist"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                            } | {
                                "type": "straight";
                            }) & {
                                "instructions"?: string | {
                                    "promptId": string;
                                };
                                "validators"?: ({
                                    "type": "condition";
                                    "condition": Condition;
                                    "error": string;
                                })[];
                            };
                            "review": {
                                "required": boolean;
                            };
                        };
                    };
                    "suppliers"?: {
                        "source": {
                            "type": "no";
                        } | {
                            "type": "entitySource";
                        } | {
                            "type": "sapBPODataV2";
                            "connection": {
                                "baseUrl": string;
                                "query"?: Record<string, string>;
                                "headers"?: Record<string, string>;
                            };
                            "auth"?: {
                                "type": "BASIC";
                                "username": string;
                                "password": string;
                            } | {
                                "type": "OAUTH2_CLIENT_CREDENTIALS";
                                "tokenUrl": string;
                                "clientId": string;
                                "clientSecret": string;
                                "scope": string;
                            };
                        };
                    };
                    "reclamation"?: {
                        "update": {
                            "type": "discard";
                        } | {
                            "type": "partial";
                        };
                        "feedback": {
                            "type": "no";
                        } | {
                            "type": "supplierEmail";
                            "fallbackEmail": string;
                            "overrideSupplierEmail"?: string;
                            "languages"?: ("DE" | "EN")[];
                        } | {
                            "type": "comment";
                            "language": "DE" | "EN";
                        };
                    };
                    "autoSubmission"?: {
                        "active": boolean;
                        "asOrderedThresholds"?: ({
                            "type": "alwaysWithin";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "anyChange";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "absoluteDifference";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "absoluteValue";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "percentage";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        })[];
                        "autoAcceptThresholds"?: ({
                            "type": "alwaysWithin";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "anyChange";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "absoluteDifference";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "absoluteValue";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "percentage";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        })[];
                        "acceptMissing": boolean;
                        "acceptNew": boolean;
                        "acceptMasterPrice": boolean;
                    };
                    "review"?: {
                        "recordConfirmations"?: {
                            "confirmationNumberPattern"?: string;
                            "confirmConfirmationLines"?: boolean;
                        };
                    };
                    "approval"?: {
                        "roles": Record<string, {
                            "resolve": MasterDataQuery | MasterDataQuery[];
                            "toEmail"?: string;
                            "authorization"?: ({
                                "type": "query";
                                "if"?: Condition;
                                "query": MasterDataQuery | MasterDataQuery[];
                                "condition": Condition;
                            } | {
                                "type": "condition";
                                "if"?: Condition;
                                "condition"?: Condition;
                            })[];
                        }>;
                        "thresholds"?: ({
                            "role": string | {
                                "email": ("self") | string;
                            };
                        } & ({
                            "type": "alwaysWithin";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "anyChange";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                            "active": boolean;
                        } | {
                            "type": "absoluteDifference";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "absoluteValue";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        } | {
                            "type": "percentage";
                            "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                            "direction": "positive" | "negative" | "both";
                            "value": number;
                            "active": boolean;
                        }))[];
                        "firstReminderAfter"?: number;
                        "nextReminderAfter"?: number;
                        "confirmBeforeApproval"?: boolean | {
                            "confirmationNumberPattern"?: string;
                            "confirmConfirmationLines"?: boolean;
                        };
                        "confirmIntermediateApproval"?: boolean;
                        "auto"?: boolean;
                        "sendEmail"?: boolean;
                    };
                    "shipmentDateStrategy"?: {
                        "type": "addDays";
                        "amount": number;
                    } | {
                        "type": "ignore";
                    } | {
                        "type": "addDaysBySupplier";
                        "defaultAdditionalDeliveryDays": number;
                        "defaultAdditionalShipmentDays": number;
                    };
                    "weekNumberAbsoluteDay"?: number;
                    "defaultConfirmationControlKey"?: string;
                    "inboxManagement"?: {
                        "folders"?: ({
                            "inboxId": `inb_${string}`;
                            "type": "default";
                        } | {
                            "inboxId": `inb_${string}`;
                            "type": "rules";
                            "rules": Record<"review" | "approval" | "processed" | "error" | "cancelled", {
                                "condition"?: Condition;
                                "path": string;
                            }[]>;
                        })[];
                        "labels"?: ({
                            "type": "default";
                            "inboxId": `inb_${string}`;
                        } | {
                            "type": "rules";
                            "inboxId": `inb_${string}`;
                            "sourceFolderIds"?: string[];
                            "rules": Record<"review" | "approval" | "reclamation" | "acceptedAuto" | "acceptedTolerance" | "acceptedEdited" | "error" | "cancelled", {
                                "entitySource": string;
                                "keyTemplate": string;
                                "labelTemplate": string;
                            } | {
                                "template": string;
                            }>;
                        })[];
                    };
                    "readOnly"?: boolean;
                    "comparisonWorkflow"?: "v1" | "v2";
                    "minCurrencyPrecision"?: number;
                    "minQuantityPrecision"?: number;
                    "partialConfirmationStrategy"?: "ignoreDeliverySchedules" | "splitPoItems" | "proper";
                    "itemAcknowledgeNumber"?: "first" | "last";
                    "submission"?: {
                        "scheduleLines"?: {
                            "deliveryDate": {
                                "type": "always" | "never";
                            };
                            "statDate": {
                                "first": "always" | "never";
                                "subsequent": "always" | "before" | "never";
                            };
                        };
                    };
                };
            } | {
                "key": "erp_integration";
                "value": {
                    "type": "SAP";
                    "connection": {
                        "baseUrl": string;
                        "query"?: Record<string, string>;
                        "headers"?: Record<string, string>;
                    };
                    "auth": {
                        "type": "BASIC";
                        "username": string;
                        "password": string;
                    } | {
                        "type": "OAUTH2_CLIENT_CREDENTIALS";
                        "tokenUrl": string;
                        "clientId": string;
                        "clientSecret": string;
                        "scope": string;
                    };
                    "getPurchaseOrder": {
                        "type": "BAPI_PO_GETDETAIL1";
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "responsePath": string[];
                        "override"?: {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "format"?: "screaming_snake" | "pascal";
                    } | {
                        "type": "BAPI_PO_CHANGE";
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "responsePath": string[];
                        "override"?: {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "format"?: "screaming_snake" | "pascal";
                    } | {
                        "type": "ODataV4";
                        "path": string;
                        "query"?: Record<string, string>;
                        "sessionIdCookie": string;
                    };
                    "changePurchaseOrder"?: {
                        "type": "BAPI_PO_CHANGE";
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "responsePath": string[];
                        "override"?: {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "format"?: "screaming_snake" | "pascal";
                    } | {
                        "type": "ODataV4";
                        "path": string;
                        "query"?: Record<string, string>;
                        "sessionIdCookie": string;
                    };
                    "confirmPurchaseOrder"?: {
                        "type": "BAPI_PO_CONFIRM1";
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "responsePath": string[];
                        "format"?: "screaming_snake" | "pascal";
                        "numberNode": "DOCUMENT_NO" | "PURCHASEORDER";
                        "override"?: {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                    };
                    "changePurchaseRequisition"?: {
                        "type": "BAPI_PR_CHANGE";
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "responsePath": string[];
                        "override"?: {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                        };
                    };
                    "pushAttachments"?: {
                        "format"?: "pdf" | "eml";
                    } & ({
                        "type": "formData";
                        "url": string;
                        "query"?: Record<string, string>;
                        "params": Record<string, string>;
                    } | {
                        "type": "BAPI";
                        "url": string;
                        "query"?: Record<string, string>;
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "numberNode": string;
                        "contentNode": string;
                    } | {
                        "type": "binary";
                        "method": string;
                        "url": string;
                        "headers"?: Record<string, string>;
                    } | {
                        "type": "sharepoint";
                        "auth": {
                            "tenantId": string;
                            "clientId": string;
                            "clientSecret": string;
                        };
                        "driveId": string;
                        "path": string;
                        "columns": Record<string, string | number | boolean>;
                    } | {
                        "type": "xml";
                        "connection": {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "auth"?: {
                            "type": "BASIC";
                            "username": string;
                            "password": string;
                        } | {
                            "type": "OAUTH2_CLIENT_CREDENTIALS";
                            "tokenUrl": string;
                            "clientId": string;
                            "clientSecret": string;
                            "scope": string;
                        };
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "bodyDefinition": Record<string, string>;
                    });
                    "scheduleLineDeliveryDateFormat"?: "dd.MM.yyyy" | "yyyy-MM-dd";
                    "extras"?: {
                        "definition"?: Record<string, {
                            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                            "placement": "item" | "header" | "both";
                            "calculation": {
                                "class": "percentage";
                                "appliedTo": "gross" | "net";
                            } | {
                                "class": "fixedAmount";
                                "distributed": boolean;
                            } | {
                                "class": "quantityBased";
                            };
                            "default"?: boolean;
                        }>;
                    };
                    "masterPrices"?: {
                        "type": "pricingConditionService";
                        "name": string;
                        "conditionTypes": string[];
                        "sessionCookieName": string;
                    };
                    "odataServices"?: Record<string, {
                        "connection": {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "auth"?: {
                            "type": "BASIC";
                            "username": string;
                            "password": string;
                        } | {
                            "type": "OAUTH2_CLIENT_CREDENTIALS";
                            "tokenUrl": string;
                            "clientId": string;
                            "clientSecret": string;
                            "scope": string;
                        };
                    }>;
                    "endpoints"?: Record<string, {
                        "connection": {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "auth"?: {
                            "type": "BASIC";
                            "username": string;
                            "password": string;
                        } | {
                            "type": "OAUTH2_CLIENT_CREDENTIALS";
                            "tokenUrl": string;
                            "clientId": string;
                            "clientSecret": string;
                            "scope": string;
                        };
                        "responseType": "XML";
                    }>;
                } | {
                    "type": "POSTGRES_ERP";
                } | {
                    "type": "MSDYNAMICS_ODATA_V4";
                    "connection": {
                        "url": string;
                        "query"?: Record<string, string>;
                    };
                    "auth": {
                        "type": "BASIC";
                        "username": string;
                        "password": string;
                    } | {
                        "type": "OAUTH2_CLIENT_CREDENTIALS";
                        "tokenUrl": string;
                        "clientId": string;
                        "clientSecret": string;
                        "scope": string;
                    };
                    "defaultCurrency"?: string;
                    "releaseApi"?: {
                        "url": string;
                    };
                    "pushAttachments"?: {
                        "format"?: "pdf" | "eml";
                    } & ({
                        "type": "formData";
                        "url": string;
                        "query"?: Record<string, string>;
                        "params": Record<string, string>;
                    } | {
                        "type": "BAPI";
                        "url": string;
                        "query"?: Record<string, string>;
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "numberNode": string;
                        "contentNode": string;
                    } | {
                        "type": "binary";
                        "method": string;
                        "url": string;
                        "headers"?: Record<string, string>;
                    } | {
                        "type": "sharepoint";
                        "auth": {
                            "tenantId": string;
                            "clientId": string;
                            "clientSecret": string;
                        };
                        "driveId": string;
                        "path": string;
                        "columns": Record<string, string | number | boolean>;
                    } | {
                        "type": "xml";
                        "connection": {
                            "baseUrl": string;
                            "query"?: Record<string, string>;
                            "headers"?: Record<string, string>;
                        };
                        "auth"?: {
                            "type": "BASIC";
                            "username": string;
                            "password": string;
                        } | {
                            "type": "OAUTH2_CLIENT_CREDENTIALS";
                            "tokenUrl": string;
                            "clientId": string;
                            "clientSecret": string;
                            "scope": string;
                        };
                        "requestBodyPath": {
                            "tag": string;
                            "attrs"?: Record<string, string>;
                        }[];
                        "bodyDefinition": Record<string, string>;
                    });
                } | {
                    "type": "ASWO";
                    "connection": {
                        "baseUrl": string;
                        "query"?: Record<string, string>;
                        "headers"?: Record<string, string>;
                    };
                    "auth": {
                        "type": "BASIC";
                        "username": string;
                        "password": string;
                    } | {
                        "type": "OAUTH2_CLIENT_CREDENTIALS";
                        "tokenUrl": string;
                        "clientId": string;
                        "clientSecret": string;
                        "scope": string;
                    };
                };
            } | {
                "key": "agent_config";
                "value": {
                    "props": Record<string, UniversalAgentNode>;
                    "messages": ({
                        "promptId": string;
                        "args"?: SerializableObject;
                    } | ({
                        "role": "system";
                        "content": string | {
                            "text": string;
                        };
                    } | {
                        "role": "user";
                        "content": ((string | {
                            "text": string;
                        }) | {
                            "externalId": string;
                            "required"?: boolean;
                        } | {
                            "inboxId": `inb_${string}`;
                            "attachmentId": `att_${string}`;
                            "required"?: boolean;
                        } | {
                            "bucket": string;
                            "key": string;
                            "required"?: boolean;
                        } | {
                            "data": string;
                            "contentType": string;
                            "required"?: boolean;
                        } | {
                            "toolId"?: string;
                            "toolName": string;
                            "toolResult": SerializableObject;
                        }) | {
                            "parts": ((string | {
                                "text": string;
                            }) | {
                                "externalId": string;
                                "required"?: boolean;
                            } | {
                                "inboxId": `inb_${string}`;
                                "attachmentId": `att_${string}`;
                                "required"?: boolean;
                            } | {
                                "bucket": string;
                                "key": string;
                                "required"?: boolean;
                            } | {
                                "data": string;
                                "contentType": string;
                                "required"?: boolean;
                            } | {
                                "toolId"?: string;
                                "toolName": string;
                                "toolResult": SerializableObject;
                            })[];
                        };
                    } | {
                        "role": "assistant";
                        "content": {
                            "parts": ({
                                "text": string;
                                "parsed"?: SerializableObject;
                                "thought"?: boolean;
                                "arbitrary"?: Record<string, Serializable>;
                            } | {
                                "toolName": string;
                                "toolArgs"?: SerializableObject;
                                "toolId"?: string;
                                "arbitrary"?: Record<string, Serializable>;
                            })[];
                        };
                    }))[];
                    "sources"?: Record<string, {
                        "title": string;
                        "sourceName": string;
                        "valueField": string;
                        "context": ("no") | ("all") | number;
                    }>;
                    "masterData"?: Record<string, {
                        "type": "ABSTRACT_ENTITY";
                        "id": `abs_${string}`;
                        "context": ("no") | ("all") | number;
                    } | {
                        "type": "ENTITY_SOURCE";
                        "entity": string;
                        "context": ("no") | ("all") | number;
                    }>;
                    "tools"?: (("resolveISOWeekDateTool") | {
                        "search": string;
                    })[];
                    "inbox"?: {
                        "folders"?: {
                            "if"?: Condition;
                            "path": string;
                        }[];
                    };
                    "processor"?: {
                        "type": "assistantCase";
                        "pathToData": (string | number)[];
                        "caseType": string;
                        "withAcknowledgement": boolean;
                        "file"?: (({
                            "filename": string;
                            "attrs"?: Record<string, string>;
                            "type": "attachment";
                            "attachmentId": string;
                        } | {
                            "filename": string;
                            "attrs"?: Record<string, string>;
                            "type": "eml";
                            "source": {
                                "messageId": string;
                            };
                        } | {
                            "filename": string;
                            "attrs"?: Record<string, string>;
                            "type": "xml";
                            "content": XmlNodeDefinition;
                        }) | {
                            "filename": string;
                            "attrs"?: Record<string, string>;
                            "type": "zip";
                            "files": (({
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "attachment";
                                "attachmentId": string;
                            } | {
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "eml";
                                "source": {
                                    "messageId": string;
                                };
                            } | {
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "xml";
                                "content": XmlNodeDefinition;
                            }) | ({
                                "array": string;
                            } & ({
                                "each": {
                                    "if"?: Condition;
                                    "item": {
                                        "filename": string;
                                        "attrs"?: Record<string, string>;
                                        "type": "attachment";
                                        "attachmentId": string;
                                    } | {
                                        "filename": string;
                                        "attrs"?: Record<string, string>;
                                        "type": "eml";
                                        "source": {
                                            "messageId": string;
                                        };
                                    } | {
                                        "filename": string;
                                        "attrs"?: Record<string, string>;
                                        "type": "xml";
                                        "content": XmlNodeDefinition;
                                    };
                                };
                            })))[];
                        }) & {
                            "bucket": string;
                            "key"?: string;
                        };
                        "review"?: {
                            "view": {
                                "body": UniversalAgentUIComponent[];
                                "footer"?: UniversalAgentUIComponent[];
                            };
                        };
                    };
                };
            } | {
                "key": "classify_settings";
                "value": {
                    "inboxRules"?: Record<("orderConfirmation" | "contractOrderConfirmation" | "other") | ("unclassified"), {
                        "folders"?: ({
                            "inboxId": `inb_${string}`;
                            "parentFolderId": string;
                            "name": string;
                            "sourceFolderIds"?: string[];
                        } | {
                            "inboxId": `inb_${string}`;
                            "rules": {
                                "condition"?: Condition;
                                "path": string;
                            }[];
                        })[];
                    }>;
                };
            });
        }, Res["Upsert Specific Organization Settings"]>("POST", "/api/admin/organizations/{organizationId}/settings/specific"),
        /** POST /api/admin/organizations/{organizationId}/source-entities */
        "Upsert source entities": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "source": {
                    "index": string;
                } | {
                    "entity": string;
                };
                "entities": SerializableObject[];
                "primaryKeys": string[];
            };
        }, Res["Upsert source entities"]>("POST", "/api/admin/organizations/{organizationId}/source-entities"),
        /** POST /api/admin/organizations/{organizationId}/sources */
        "Create entity source": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "entity": string;
                "config": {
                    "deduplicate"?: boolean | ("last-win") | ("merge");
                    "filter"?: Condition;
                } & ({
                    "type": "S3_EXPORT";
                    "bucket": string;
                    "key": string;
                    "format": {
                        "type": "CSV";
                        "removeDuplicateLines"?: boolean;
                        "delimiter"?: string;
                    };
                } | {
                    "type": "LEXINGTON_POC";
                } | {
                    "type": "ATP_POC";
                } | {
                    "type": "MANUAL";
                });
            };
        }, Res["Create entity source"]>("POST", "/api/admin/organizations/{organizationId}/sources"),
        /** POST /api/admin/organizations/{organizationId}/sources/{entitySourceId}/fields */
        "Create entity field": handler<{
            params: {
                "organizationId": string;
                "entitySourceId": `src_${string}`;
            };
            jsonBody: {
                "name": string;
                "config": {
                    "optional"?: boolean;
                    "description"?: string;
                    "llmSearch"?: boolean;
                    "comparisonInstructions"?: string;
                    "priority"?: number;
                } & ({
                    "type": "string";
                    "originalName": string;
                    "pk": boolean;
                    "fk"?: string;
                    "regex"?: string;
                } | {
                    "type": "integer";
                    "originalName": string;
                    "pk": boolean;
                    "fk"?: string;
                } | {
                    "type": "decimal";
                    "originalName": string;
                } | {
                    "type": "constant";
                    "value": string;
                    "pk": boolean;
                } | {
                    "type": "active";
                    "originalName": string;
                    "true": string;
                    "false": string;
                } | {
                    "type": "domain";
                    "originalName": string;
                } | {
                    "type": "country";
                    "originalName": string;
                } | {
                    "type": "boolean";
                    "originalName": string;
                });
            };
        }, Res["Create entity field"]>("POST", "/api/admin/organizations/{organizationId}/sources/{entitySourceId}/fields"),
        /** POST /api/admin/organizations/{organizationId}/universal-agent/validate */
        "Validate Universal agent data": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "threadId": `thr_${string}`;
                "inboxId"?: `inb_${string}`;
                "data": Serializable;
            };
        }, Res["Validate Universal agent data"]>("POST", "/api/admin/organizations/{organizationId}/universal-agent/validate"),
        /** POST /api/admin/uify-gemini-very-stable-endpoint */
        "LLM For Uify": handler<{
            headers: {
                "x-uify-secret": string;
            };
            jsonBody: {
                "model": "gpt-4o" | "gpt-4o-mini" | "gpt-4.1" | "gpt-4.1-mini" | "gpt-4.1-nano" | "o1" | "o3" | "o3-mini" | "o4-mini" | "gemini-2.5-pro" | "gemini-2.5-flash" | "gemini-2.0-flash" | "gemini-2.5-pro-preview-05-06" | "azure-gpt-4o";
                "title": string;
                "tags"?: string[];
                "messages": ({
                    "promptId": string;
                    "args"?: SerializableObject;
                } | ({
                    "role": "system";
                    "content": string | {
                        "text": string;
                    };
                } | {
                    "role": "user";
                    "content": ((string | {
                        "text": string;
                    }) | {
                        "externalId": string;
                    } | {
                        "inboxId": `inb_${string}`;
                        "attachmentId": `att_${string}`;
                    } | {
                        "bucket": string;
                        "key": string;
                    } | {
                        "data": string;
                        "contentType": string;
                    }) | {
                        "parts": ((string | {
                            "text": string;
                        }) | {
                            "externalId": string;
                        } | {
                            "inboxId": `inb_${string}`;
                            "attachmentId": `att_${string}`;
                        } | {
                            "bucket": string;
                            "key": string;
                        } | {
                            "data": string;
                            "contentType": string;
                        })[];
                    };
                } | {
                    "role": "assistant";
                    "content": {
                        "text": string;
                        "parsed"?: SerializableObject;
                    };
                } | {
                    "role": "toolRequest";
                    "content": {
                        "id"?: string;
                        "name": string;
                        "args": SerializableObject;
                    };
                } | {
                    "role": "toolResponse";
                    "content": {
                        "id"?: string;
                        "name": string;
                        "result": SerializableObject;
                    };
                }))[];
                "schema"?: SerializableObject;
                "tools"?: {
                    "name": string;
                    "description": string;
                    "args": SerializableObject;
                }[];
                "toolChoice"?: "none" | "auto" | "required";
                "temperature"?: number;
            };
        }, Res["LLM For Uify"]>("POST", "/api/admin/uify-gemini-very-stable-endpoint"),
        /** POST /api/admin/workflow */
        "Start workflow": handler<{
            jsonBody: {
                "workflowType": "autoSubmitResults";
                "options": {
                    "args": {
                        "orgId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "transactions": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        }[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "broseInvFI";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "runId"?: string;
                        "config"?: {
                            "model"?: "gpt-4o" | "gpt-4o-mini" | "gpt-4.1" | "gpt-4.1-mini" | "gpt-4.1-nano" | "o1" | "o3" | "o3-mini" | "o4-mini" | "gemini-2.5-pro" | "gemini-2.5-flash" | "gemini-2.0-flash" | "gemini-2.5-pro-preview-05-06" | "azure-gpt-4o";
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "broseInvMM";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "runId"?: string;
                        "config"?: {
                            "model"?: "gpt-4o" | "gpt-4o-mini" | "gpt-4.1" | "gpt-4.1-mini" | "gpt-4.1-nano" | "o1" | "o3" | "o3-mini" | "o4-mini" | "gemini-2.5-pro" | "gemini-2.5-flash" | "gemini-2.0-flash" | "gemini-2.5-pro-preview-05-06" | "azure-gpt-4o";
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "broseOC";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "runId"?: string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "broseQM";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "runId"?: string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "broseQMAttachment";
                "options": {
                    "args": {
                        "organizationId": string;
                        "inboxId": `inb_${string}`;
                        "attachmentId": `att_${string}`;
                        "runId"?: string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "cancelPurchaseOrderProcessing";
                "options": {
                    "args": {
                        "organizationId": string;
                        "processingId": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "changeThreadLabels";
                "options": {
                    "args": {
                        "threadId": `thr_${string}`;
                        "messageIds"?: `msg_${string}`[];
                        "add": string[];
                        "remove": string[];
                        "sourceFolderIds"?: string[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "classifySalesOrderThread";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "classifications": {
                            "name": string;
                            "description"?: string;
                            "pipeline"?: string;
                        }[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "classifyThread";
                "options": {
                    "args": {
                        "organizationId": string;
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "cleanupActivityPayloads";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "declinePurchaseOrderProcessing";
                "options": {
                    "args": {
                        "organizationId": string;
                        "processingId": number;
                        "decisions": Record<string, AssistantDecisionSchema>;
                        "masterPriceDecisions": Record<string, {
                            "type": "accepted";
                            "value": BigNumberValue | null;
                            "id"?: string;
                        } | {
                            "type": "rejected";
                        }>;
                        "update": {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                        "submitPartially"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectContact";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectCustomer";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectPositions";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectPositionsFromFullList";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectPositionsHybridSearch";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectPositionsKoestner";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectScheduleOfServicesReflexRequestPositions";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "index": number;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectScheduleOfServicesRequestPositions";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "index": number;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectScheduleOfServicesRequests";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectSellingEntity";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "detectTransactions";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "dispatchWorkflow";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "extractAttachmentContents";
                "options": {
                    "args": {
                        "attachmentIds": `att_${string}`[];
                        "excludeGeminiSupportedFormats": boolean;
                        "imageBytesThreshold": number;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "extractOrderConfirmations";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "extractOrderConfirmations2";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "handleInboxFolderEvent";
                "options": {
                    "args": {
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "dataSetId": string;
                        "event": "PIPELINE_STARTED" | "PIPELINE_FINISHED_SUCCESS" | "PIPELINE_FINISHED_ERROR" | "SUBMITTED_SUCCESS" | "SUBMITTED_ERROR" | "CANCELLED" | "CLASSIFICATION";
                        "folderChangedBefore": boolean;
                        "inboxId": `inb_${string}`;
                        "messageIds": `msg_${string}`[];
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIdsByTransactionId": Record<`tra_${string}`, `msg_${string}`[]>;
                        "parentFolderIdsByMessageId": Record<`msg_${string}`, string>;
                        "classification"?: string;
                        "classificationExtraction"?: SerializableObject;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "handleInboxLabelEvent";
                "options": {
                    "args": {
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "event": "PIPELINE_STARTED" | "PIPELINE_FINISHED_SUCCESS" | "PIPELINE_FINISHED_ERROR" | "SUBMITTED_SUCCESS" | "SUBMITTED_ERROR" | "CANCELLED" | "CLASSIFICATION";
                        "inboxId": `inb_${string}`;
                        "messageIds": `msg_${string}`[];
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIdsByTransactionId": Record<`tra_${string}`, `msg_${string}`[]>;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "handleOrderConfirmations";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "indexUtzProductsToElastic";
                "options": {
                    "args": {
                        "orgId": string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "initiateSalesOrderWorkflow";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "iterateEntitySources";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "iterateInboxes";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "iteratePendingApprovals";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "iteratePendingEmailFolderChanges";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "iterateSODataSync";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "manageInbox";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "migrateInboxMessages";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "delayMs"?: number;
                        "batchSize"?: number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "migrateMessageBatch";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadIds": `thr_${string}`[];
                        "delayMs"?: number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "migrateMessages";
                "options": {
                    "args": {
                        "delayMs"?: number;
                        "batchSize"?: number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "moveThread";
                "options": {
                    "args": ({
                        "threadId": `thr_${string}`;
                        "messageIds"?: `msg_${string}`[];
                        "sourceFolderIds"?: string[];
                    } & ({
                        "folderId": string;
                    } | {
                        "folderName": string;
                        "parentFolderId": string;
                    }))[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "processEmailFolderChange";
                "options": {
                    "args": {
                        "id": number;
                        "moveTrigger": "AUTOMATIC" | "MANUAL";
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "processOrderConfirmations";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadProcessingId": number;
                        "poProcessingIds": number[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "processOrderConfirmationsV2";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadProcessingId": number;
                        "poProcessingIds": number[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "processOrderConfirmationsV3";
                "options": {
                    "args": {
                        "organizationId": string;
                        "extraction": {
                            "confirmations": {
                                "header": {
                                    "number": string | null;
                                    "date": string;
                                    "currency": string | null;
                                    "billingAddress": string | null;
                                    "paymentTerms": string | null;
                                    "deliveryTerms": {
                                        "incoterms": string | null;
                                        "location": string | null;
                                        "additions"?: string | null;
                                    } | null;
                                };
                                "items": {
                                    "serialNumber": number;
                                    "positionNumber": string | null;
                                    "poNumber": string;
                                    "description": string | null;
                                    "articleNumbers": string[];
                                    "deliveryAddress": string | null;
                                    "deliverySchedules": {
                                        "quantity": number | null;
                                        "deliveryDate": string | null;
                                        "shipmentDate": string | null;
                                        "deliveryDateMentioned"?: boolean;
                                        "shipmentDateMentioned"?: boolean;
                                    }[];
                                    "costStructure": {
                                        "quantity": number | null;
                                        "quantityUom": string | null;
                                        "unitPrice": number | null;
                                        "priceUom": string | null;
                                        "priceUnit": number | null;
                                        "totalPrice": number | null;
                                    };
                                    "itemExtra": {
                                        "serialNumber": number;
                                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                        "description": string;
                                        "amount": number;
                                        "calculation": {
                                            "class": "percentage";
                                            "rate": BigNumberValue | null;
                                            "appliedTo": "gross" | "net";
                                        } | {
                                            "class": "fixedAmount";
                                            "amount": BigNumberValue | null;
                                            "distributed": boolean;
                                        } | {
                                            "class": "quantityBased";
                                            "quantity": BigNumberValue;
                                            "quantityUom": string;
                                            "unitPrice": BigNumberValue;
                                            "priceUom": string;
                                            "perAmount": BigNumberValue;
                                            "uomConversionCoefficient": BigNumberValue;
                                        };
                                    }[];
                                }[];
                                "headerExtra": {
                                    "serialNumber": number;
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "description": string;
                                    "amount": number;
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[];
                            }[];
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "processThread";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "newMessageIds"?: `msg_${string}`[];
                        "runId"?: string;
                        "isReprocess"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "pushAttachmentToSharePoint";
                "options": {
                    "args": {
                        "organizationId": string;
                        "poNumber": string;
                        "attachmentId": `att_${string}` | `msg_${string}`;
                        "config": {
                            "type": "sharepoint";
                            "auth": {
                                "tenantId": string;
                                "clientId": string;
                                "clientSecret": string;
                            };
                            "driveId": string;
                            "path": string;
                            "columns": Record<string, string | number | boolean>;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "pushAttachments";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                        "poNumber": string;
                        "config": {
                            "format"?: "pdf" | "eml";
                        } & ({
                            "type": "formData";
                            "url": string;
                            "query"?: Record<string, string>;
                            "params": Record<string, string>;
                        } | {
                            "type": "BAPI";
                            "url": string;
                            "query"?: Record<string, string>;
                            "requestBodyPath": {
                                "tag": string;
                                "attrs"?: Record<string, string>;
                            }[];
                            "numberNode": string;
                            "contentNode": string;
                        } | {
                            "type": "binary";
                            "method": string;
                            "url": string;
                            "headers"?: Record<string, string>;
                        } | {
                            "type": "sharepoint";
                            "auth": {
                                "tenantId": string;
                                "clientId": string;
                                "clientSecret": string;
                            };
                            "driveId": string;
                            "path": string;
                            "columns": Record<string, string | number | boolean>;
                        } | {
                            "type": "xml";
                            "connection": {
                                "baseUrl": string;
                                "query"?: Record<string, string>;
                                "headers"?: Record<string, string>;
                            };
                            "auth"?: {
                                "type": "BASIC";
                                "username": string;
                                "password": string;
                            } | {
                                "type": "OAUTH2_CLIENT_CREDENTIALS";
                                "tokenUrl": string;
                                "clientId": string;
                                "clientSecret": string;
                                "scope": string;
                            };
                            "requestBodyPath": {
                                "tag": string;
                                "attrs"?: Record<string, string>;
                            }[];
                            "bodyDefinition": Record<string, string>;
                        });
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "pushDocumentsToERP";
                "options": {
                    "args": {
                        "organizationId": string;
                        "inboxId": `inb_${string}`;
                        "dataSetId": string;
                        "suggestedTransactionId": `tra_${string}`;
                        "submittedTransactionId": `stx_${string}`;
                        "ctx": {
                            "currentDateTimeISO": string;
                            "currentDateISO": string;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "recomputeNextCaseState";
                "options": {
                    "args": {
                        "id": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "refreshCasesView";
                "options": {
                    "args": never[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "replyToOperationalBuyer";
                "options": {
                    "args": {
                        "organizationId": string;
                        "poProcessingId": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "replyToSupplier";
                "options": {
                    "args": {
                        "organizationId": string;
                        "poProcessingId": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "resolvePosition";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "detectedPosition": {
                            "positionIndex": number;
                            "originalPositionIndex": string | null;
                            "productDescription": string;
                            "articleNumbers": string[];
                            "customerListsMultiplePartialDeliveries": boolean;
                            "keySearchTerm1": string;
                            "keySearchTerm2": string;
                            "quantity": number;
                            "totalValue": number | null;
                            "expectedDeliveryDate": ({
                                "dateType": "NO_DATE_MENTIONED";
                            } | {
                                "dateType": "AS_SOON_AS_POSSIBLE";
                            } | {
                                "dateType": "DATE";
                                "isoDate": string;
                            } | {
                                "dateType": "CALENDAR_WEEK";
                                "year": number;
                                "week": number;
                            } | {
                                "dateType": "CALENDAR_MONTH";
                                "year": number;
                                "month": number;
                            }) | null;
                            "originalDeliveryDateExpression": string | null;
                        } & {
                            [x: string]: Serializable;
                        };
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                        "index": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "resolvePositionHybridSearch";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "detectedPosition": {
                            "articleNumbers": string[];
                            "productAttributes": Record<string, Serializable[] | null>;
                            "expectedDeliveryDate": ({
                                "dateType": "NO_DATE_MENTIONED";
                            } | {
                                "dateType": "AS_SOON_AS_POSSIBLE";
                            } | {
                                "dateType": "DATE";
                                "isoDate": string;
                            } | {
                                "dateType": "CALENDAR_WEEK";
                                "year": number;
                                "week": number;
                            } | {
                                "dateType": "CALENDAR_MONTH";
                                "year": number;
                                "month": number;
                            }) | null;
                            "originalDeliveryDateExpression": string | null;
                            "productDescription": string;
                            "originalPositionIndex": string | null;
                            "totalValue": number | null;
                        };
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                        "index": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "resolvePositionKoestner";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "detectedPosition": {
                            "positionIndex": number;
                            "originalPositionIndex": string | null;
                            "productDescription": string;
                            "articleNumbers": string[];
                            "searchTerms": string[];
                            "requestedCertificate": ("GENERAL_CERTIFICATE" | "2.2" | "3.1") | null;
                            "productTypes": ("QR" | "RR" | "H" | "GR" | "ARU" | "ERU" | "GEL" | "KON" | "SR" | "SRN" | "PR" | "PRN" | "RP" | "FL" | "L" | "T" | "TB" | "VK" | "RD" | "U" | "HEB" | "HEA" | "HEM" | "IPE" | "INP" | "UNP" | "UPE" | "UBAU" | "BL" | "ABL" | "ABLQ" | "ABLD" | "ABLQG" | "EBL" | "EBLQ" | "BLT")[];
                            "productFormat": ("2040" | "2060" | "KF" | "MF" | "GF") | null;
                            "material": ("Stainless Steel" | "Non-stainless steel" | "Aluminium" | "Brass") | null;
                            "materialGrade": ("S235" | "S355" | "C45" | "42CRMOS4+QT" | "DBS" | "DBS2" | "DBS3" | "AlMg3" | "Al99,5" | "DC01" | "DC02" | "DC03" | "DC04" | "DD11") | null;
                            "galvanized": boolean;
                            "steelType": ("BRIGHT_STEEL" | "SEAMLESS_STEEL") | null;
                            "requestsBlasting": boolean;
                            "requestsPrimer": boolean;
                            "dimensions": {
                                "value": number;
                                "unit": "MM" | "M" | "CM";
                            }[];
                            "requestedLength": {
                                "value": number;
                                "unit": "MM" | "M" | "CM";
                            } | null;
                            "diameterInInches": string | null;
                            "customerIndicatedFixedDimension": boolean;
                            "quantity": number;
                            "quantityUnit": "STK" | "KGM" | "MTR";
                            "originalQuantityUnit": string;
                            "price": number | null;
                            "priceUnit": ("STK" | "KGM" | "MTR") | null;
                            "originalPriceUnit": string;
                            "priceQuantity": number | null;
                            "totalValue": number | null;
                            "expectedDeliveryDate": ({
                                "dateType": "NO_DATE_MENTIONED";
                            } | {
                                "dateType": "AS_SOON_AS_POSSIBLE";
                            } | {
                                "dateType": "DATE";
                                "isoDate": string;
                            } | {
                                "dateType": "CALENDAR_WEEK";
                                "year": number;
                                "week": number;
                            } | {
                                "dateType": "CALENDAR_MONTH";
                                "year": number;
                                "month": number;
                            }) | null;
                            "originalDeliveryDateExpression": string | null;
                        };
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                        "index": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "runSalesOrderPipeline";
                "options": {
                    "args": {
                        "salesOrderInput": {
                            "inboxId": `inb_${string}`;
                            "threadId": `thr_${string}`;
                            "organizationId": string;
                            "shouldDeletePreviousWorkflows"?: boolean;
                            "shouldApplyOrderSplit"?: boolean;
                            "folderChangedBefore"?: boolean;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "salesOrderBasic";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "salesOrderKoestner";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "salesOrderPOC";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                        "init"?: {
                            "config": {
                                "orgId": string;
                                "name": string;
                                "slug": string;
                                "goLiveDate": string | null;
                                "domain": string;
                                "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                                "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                                "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                                "syncEnabled": boolean;
                                "authSecretsPath": string;
                                "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                                "documentSystemAuthSecretsPath": string | null;
                                "emailSystemAuthSecretsPath": string | null;
                                "config": {
                                    "autoSubmitEnabled"?: boolean;
                                    "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                    "extractedPositionFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "extractedHeaderFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "productSelectionFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "priceFetchTriggers"?: {
                                        "customerSelect"?: boolean;
                                        "multiMatchSelect"?: boolean;
                                        "quantityChange"?: boolean;
                                        "productCardEdit"?: boolean;
                                        "positionDelete"?: boolean;
                                        "positionDeleteRevert"?: boolean;
                                        "uomChange"?: boolean;
                                    };
                                    "orderHeaderFields"?: {
                                        "comment"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "shippingTerms"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "paymentTerms"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "creditLimit"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                    };
                                    "customOrderHeaderFields"?: ({
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "text";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "textarea";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "date";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "number";
                                        "allowDecimals": boolean;
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "select";
                                        "innerType": "text" | "number";
                                        "source": {
                                            "type": "DICTIONARY";
                                            "name": string;
                                        } | {
                                            "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                            "namesBySellingEntityCode": Record<string, string>;
                                        } | {
                                            "type": "FIXED";
                                            "options": string[];
                                            "labels": Record<string, string>;
                                        };
                                    })[];
                                    "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                    "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                    "positionFieldAlwaysEditable"?: string[];
                                    "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                    "addressFieldOrder"?: (("address") | string)[];
                                    "displayQuantityInProductSalesUnit"?: boolean;
                                    "handleDeliveryInstructions"?: boolean;
                                    "deliveryAddressDisabled"?: boolean;
                                    "allowNewBillingAddressMatch"?: boolean;
                                    "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                    "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                    "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                    "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                    "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                    "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                    "paymentTermsWhenAboveCreditLimit"?: string | null;
                                    "replyTemplate"?: string | null;
                                    "positionFields"?: {
                                        "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                        "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "visibleWhenEmpty": boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "editSettings"?: {
                                            "required"?: boolean;
                                            "type": "date" | "text" | "number" | "select";
                                            "relatedFieldKey"?: string;
                                            "selectSettings"?: {
                                                "source": "PRODUCT_CUSTOM_FIELDS";
                                                "key": string;
                                            } | {
                                                "source": "DICTIONARY";
                                                "name": string;
                                            };
                                            "offerApplyToAllPositions"?: boolean;
                                        };
                                    }[];
                                    "customerFields"?: {
                                        "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                        "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "visibleWhenEmpty": boolean;
                                    }[];
                                    "billingAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "standardBillingAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "standardDeliveryAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "inboxFolders"?: ({
                                        "type": "rules";
                                        "inboxId": string;
                                        "basePath"?: string[];
                                        "rules": {
                                            "condition"?: Condition;
                                            "path": string;
                                            "folderMoveDelayMinutes"?: number;
                                        }[];
                                        "noTransactionFoundFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                    } | {
                                        "type"?: ("static") | null;
                                        "inboxId": string;
                                        "pipelineStartedFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "readyForReviewFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "submittedFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "cancelledFolder"?: {
                                            "folderId": string;
                                        };
                                        "noTransactionFoundFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "folderMoveDelayMinutes"?: number;
                                    })[];
                                    "inboxLabelsGerman"?: {
                                        "pipelineStarted"?: string;
                                        "pipelineFinished"?: string;
                                        "pipelineError"?: string;
                                        "submittedSuccessfully"?: string;
                                        "submittedWithError"?: string;
                                        "ORDER"?: string;
                                        "REQUEST_FOR_QUOTATION"?: string;
                                    };
                                    "inboxLabelsEnglish"?: {
                                        "pipelineStarted"?: string;
                                        "pipelineFinished"?: string;
                                        "pipelineError"?: string;
                                        "submittedSuccessfully"?: string;
                                        "submittedWithError"?: string;
                                        "ORDER"?: string;
                                        "REQUEST_FOR_QUOTATION"?: string;
                                    };
                                    "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                    "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                    "emailPushFileName"?: string;
                                    "salesOrderBasic"?: {
                                        "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                        "stripProductTexts"?: boolean;
                                        "allowNoMatch"?: boolean;
                                        "allowMultiMatch"?: boolean;
                                    };
                                    "salesOrderPOC"?: {
                                        "showProductCustomFieldsToLLM"?: boolean;
                                        "showUnitPricesToLLM"?: string[];
                                        "defaultCustomerAccountIdForPriceLookup"?: string;
                                        "validateMultiMatchesWithThoughts"?: boolean;
                                        "minimumLengthForLevenshteinDiff"?: number;
                                        "allowedLevenshteinDiffForArticleNumber"?: number;
                                        "allowedArticleNumberPrefixLength"?: number;
                                        "allowedArticleNumberSuffixLength"?: number;
                                    };
                                    "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                    "contactMatchingEnabled"?: boolean;
                                    "replyGenerationEnabled"?: boolean;
                                    "attachedImageByteSizeThreshold"?: number;
                                    "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                    "sellingEntityHeader"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "sellingEntityFields"?: ("name" | "address")[];
                                    "sellingEntityEditable"?: "NO" | "INLINE";
                                    "mockSubmission"?: boolean;
                                    "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                    "productIdWhenNoMatch"?: string;
                                    "productIdWhenNoMatchBadgeText"?: string;
                                    "productInvalidLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    } | null;
                                    "standardHeaderFieldLabels"?: {
                                        "orderReference"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "quoteNumber"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "totalValue"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                    };
                                    "standardPositionFieldLabels"?: {
                                        "price"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "totalValue"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                    };
                                    "orderSplitStrategy"?: {
                                        "strategy": "DELIVERY_DATE";
                                        "applyByDefault": boolean;
                                        "offerReprocess": boolean;
                                    };
                                    "ignoreGlobalPriceLists"?: boolean;
                                    "nonExactArticleNumberMatch"?: boolean;
                                    "partialCustomerArticleNumberMatch"?: boolean;
                                    "matchCustomerArticleNumbersGlobally"?: boolean;
                                    "exposedDictionariesToUI"?: string[];
                                    "judgeTransactionDetection"?: boolean;
                                    "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                    "searchProductsByCustomerAccountNumbers"?: boolean;
                                    "searchProductsByTextSearch"?: boolean;
                                    "restrictSearchToCustomerProducts"?: boolean;
                                    "additionalCostsEnabled"?: boolean;
                                    "priceFetchEnabled"?: boolean;
                                    "additionalOrderNumberSeparator"?: string;
                                    "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                    "warnings"?: ("QUOTE_NUMBER")[];
                                    "eliminateQuantityZeroPositions"?: boolean;
                                    "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                    "classifications"?: {
                                        "name": string;
                                        "description"?: string;
                                        "pipeline"?: string;
                                    }[];
                                };
                                "legalEntityNames": string[];
                                "defaultCurrencyCode": string;
                                "defaultLocale": string;
                                "createdAt": string;
                                "updatedAt": string;
                            };
                            "dataSetId": string;
                            "messageIds": `msg_${string}`[];
                            "attachmentIds": `att_${string}`[];
                            "firstMessageReceivedAt": string | null;
                            "wfId": `wkf_${string}`;
                            "parentFolderIdsByMessageId": Record<`msg_${string}`, string>;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "scheduleOfServicesReflex";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "scheduleOfServicesWika";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "organizationId": string;
                        "shouldDeletePreviousWorkflows"?: boolean;
                        "shouldApplyOrderSplit"?: boolean;
                        "folderChangedBefore"?: boolean;
                        "init": {
                            "config": {
                                "orgId": string;
                                "name": string;
                                "slug": string;
                                "goLiveDate": string | null;
                                "domain": string;
                                "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                                "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                                "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                                "syncEnabled": boolean;
                                "authSecretsPath": string;
                                "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                                "documentSystemAuthSecretsPath": string | null;
                                "emailSystemAuthSecretsPath": string | null;
                                "config": {
                                    "autoSubmitEnabled"?: boolean;
                                    "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                    "extractedPositionFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "extractedHeaderFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "productSelectionFields"?: {
                                        "technicalName": string;
                                        "readableNameEnglish": string;
                                        "readableNameGerman": string;
                                        "type": "string" | "number" | "money" | "date";
                                        "array"?: boolean;
                                        "nullable"?: boolean;
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "enum"?: {
                                            "type": "FIXED";
                                            "enum": string[];
                                            "labels"?: Record<string, string>;
                                        } | {
                                            "type": "DISTINCT_PRODUCT_VALUES";
                                            "source": {
                                                "type": "PRODUCT_ATTRIBUTE";
                                                "attributeName": string;
                                            } | {
                                                "type": "CUSTOM_PRODUCT_FIELD";
                                                "jsonPath": string;
                                            };
                                        };
                                        "description": string;
                                        "sortOrder": number;
                                        "editable": boolean;
                                    }[];
                                    "priceFetchTriggers"?: {
                                        "customerSelect"?: boolean;
                                        "multiMatchSelect"?: boolean;
                                        "quantityChange"?: boolean;
                                        "productCardEdit"?: boolean;
                                        "positionDelete"?: boolean;
                                        "positionDeleteRevert"?: boolean;
                                        "uomChange"?: boolean;
                                    };
                                    "orderHeaderFields"?: {
                                        "comment"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "shippingTerms"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "paymentTerms"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                        "creditLimit"?: {
                                            "visible"?: boolean;
                                            "visibleWhenEmpty"?: boolean;
                                            "editable"?: boolean;
                                            "labelGerman"?: string;
                                            "labelEnglish"?: string;
                                            "placeholderGerman"?: string;
                                            "placeholderEnglish"?: string;
                                        };
                                    };
                                    "customOrderHeaderFields"?: ({
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "text";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "textarea";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "date";
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "number";
                                        "allowDecimals": boolean;
                                    } | {
                                        "key": string;
                                        "editable": boolean;
                                        "visibleWhenEmpty": boolean;
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                        "defaultValue"?: ({
                                            "type": "CONSTANT";
                                            "value": string;
                                        } | {
                                            "type": "PARTNER_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                            "key": string;
                                        } | {
                                            "type": "TRANSACTION_CUSTOM_FIELD";
                                            "key": string;
                                        })[];
                                        "type": "select";
                                        "innerType": "text" | "number";
                                        "source": {
                                            "type": "DICTIONARY";
                                            "name": string;
                                        } | {
                                            "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                            "namesBySellingEntityCode": Record<string, string>;
                                        } | {
                                            "type": "FIXED";
                                            "options": string[];
                                            "labels": Record<string, string>;
                                        };
                                    })[];
                                    "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                    "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                    "positionFieldAlwaysEditable"?: string[];
                                    "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                    "addressFieldOrder"?: (("address") | string)[];
                                    "displayQuantityInProductSalesUnit"?: boolean;
                                    "handleDeliveryInstructions"?: boolean;
                                    "deliveryAddressDisabled"?: boolean;
                                    "allowNewBillingAddressMatch"?: boolean;
                                    "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                    "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                    "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                    "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                    "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                    "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                    "paymentTermsWhenAboveCreditLimit"?: string | null;
                                    "replyTemplate"?: string | null;
                                    "positionFields"?: {
                                        "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                        "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "visibleWhenEmpty": boolean;
                                        "visibleConditionally"?: {
                                            "dependentKey": string;
                                            "condition": {
                                                "type": "EQUALS";
                                                "value": string;
                                            } | {
                                                "type": "EXISTS";
                                            } | {
                                                "type": "NOT_EXISTS";
                                            };
                                        };
                                        "editSettings"?: {
                                            "required"?: boolean;
                                            "type": "date" | "text" | "number" | "select";
                                            "relatedFieldKey"?: string;
                                            "selectSettings"?: {
                                                "source": "PRODUCT_CUSTOM_FIELDS";
                                                "key": string;
                                            } | {
                                                "source": "DICTIONARY";
                                                "name": string;
                                            };
                                            "offerApplyToAllPositions"?: boolean;
                                        };
                                    }[];
                                    "customerFields"?: {
                                        "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                        "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                        "labelGerman": string;
                                        "labelEnglish": string;
                                        "visibleWhenEmpty": boolean;
                                    }[];
                                    "billingAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "standardBillingAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "standardDeliveryAddressLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "inboxFolders"?: ({
                                        "type": "rules";
                                        "inboxId": string;
                                        "basePath"?: string[];
                                        "rules": {
                                            "condition"?: Condition;
                                            "path": string;
                                            "folderMoveDelayMinutes"?: number;
                                        }[];
                                        "noTransactionFoundFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                    } | {
                                        "type"?: ("static") | null;
                                        "inboxId": string;
                                        "pipelineStartedFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "readyForReviewFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "submittedFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "cancelledFolder"?: {
                                            "folderId": string;
                                        };
                                        "noTransactionFoundFolder"?: {
                                            "parentId": string;
                                            "folderName": string;
                                        };
                                        "folderMoveDelayMinutes"?: number;
                                    })[];
                                    "inboxLabelsGerman"?: {
                                        "pipelineStarted"?: string;
                                        "pipelineFinished"?: string;
                                        "pipelineError"?: string;
                                        "submittedSuccessfully"?: string;
                                        "submittedWithError"?: string;
                                        "ORDER"?: string;
                                        "REQUEST_FOR_QUOTATION"?: string;
                                    };
                                    "inboxLabelsEnglish"?: {
                                        "pipelineStarted"?: string;
                                        "pipelineFinished"?: string;
                                        "pipelineError"?: string;
                                        "submittedSuccessfully"?: string;
                                        "submittedWithError"?: string;
                                        "ORDER"?: string;
                                        "REQUEST_FOR_QUOTATION"?: string;
                                    };
                                    "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                    "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                    "emailPushFileName"?: string;
                                    "salesOrderBasic"?: {
                                        "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                        "stripProductTexts"?: boolean;
                                        "allowNoMatch"?: boolean;
                                        "allowMultiMatch"?: boolean;
                                    };
                                    "salesOrderPOC"?: {
                                        "showProductCustomFieldsToLLM"?: boolean;
                                        "showUnitPricesToLLM"?: string[];
                                        "defaultCustomerAccountIdForPriceLookup"?: string;
                                        "validateMultiMatchesWithThoughts"?: boolean;
                                        "minimumLengthForLevenshteinDiff"?: number;
                                        "allowedLevenshteinDiffForArticleNumber"?: number;
                                        "allowedArticleNumberPrefixLength"?: number;
                                        "allowedArticleNumberSuffixLength"?: number;
                                    };
                                    "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                    "contactMatchingEnabled"?: boolean;
                                    "replyGenerationEnabled"?: boolean;
                                    "attachedImageByteSizeThreshold"?: number;
                                    "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                    "sellingEntityHeader"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    };
                                    "sellingEntityFields"?: ("name" | "address")[];
                                    "sellingEntityEditable"?: "NO" | "INLINE";
                                    "mockSubmission"?: boolean;
                                    "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                    "productIdWhenNoMatch"?: string;
                                    "productIdWhenNoMatchBadgeText"?: string;
                                    "productInvalidLabel"?: {
                                        "DE"?: string;
                                        "EN"?: string;
                                    } | null;
                                    "standardHeaderFieldLabels"?: {
                                        "orderReference"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "quoteNumber"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "totalValue"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                    };
                                    "standardPositionFieldLabels"?: {
                                        "price"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                        "totalValue"?: {
                                            "de"?: string;
                                            "en"?: string;
                                        };
                                    };
                                    "orderSplitStrategy"?: {
                                        "strategy": "DELIVERY_DATE";
                                        "applyByDefault": boolean;
                                        "offerReprocess": boolean;
                                    };
                                    "ignoreGlobalPriceLists"?: boolean;
                                    "nonExactArticleNumberMatch"?: boolean;
                                    "partialCustomerArticleNumberMatch"?: boolean;
                                    "matchCustomerArticleNumbersGlobally"?: boolean;
                                    "exposedDictionariesToUI"?: string[];
                                    "judgeTransactionDetection"?: boolean;
                                    "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                    "searchProductsByCustomerAccountNumbers"?: boolean;
                                    "searchProductsByTextSearch"?: boolean;
                                    "restrictSearchToCustomerProducts"?: boolean;
                                    "additionalCostsEnabled"?: boolean;
                                    "priceFetchEnabled"?: boolean;
                                    "additionalOrderNumberSeparator"?: string;
                                    "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                    "warnings"?: ("QUOTE_NUMBER")[];
                                    "eliminateQuantityZeroPositions"?: boolean;
                                    "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                    "classifications"?: {
                                        "name": string;
                                        "description"?: string;
                                        "pipeline"?: string;
                                    }[];
                                };
                                "legalEntityNames": string[];
                                "defaultCurrencyCode": string;
                                "defaultLocale": string;
                                "createdAt": string;
                                "updatedAt": string;
                            };
                            "dataSetId": string;
                            "messageIds": `msg_${string}`[];
                            "attachmentIds": `att_${string}`[];
                            "firstMessageReceivedAt": string | null;
                            "wfId": `wkf_${string}`;
                            "parentFolderIdsByMessageId": Record<`msg_${string}`, string>;
                        };
                        "classification": string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "selectScheduleOfServicesReflexRequestPositionProducts";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "detectedPosition": {
                            "positionIndex": number;
                            "positionNumber": string | null;
                            "productAttributes": {
                                "articleNumbers": string[];
                                "descriptionLines": string[];
                                "detectedProductName": string | null;
                                "matchingProductCategories": ("Abscheider" | "Druckhaltung" | "Entgasung" | "Frischwasserstation" | "Nachspeisung" | "Pufferspeicher" | "Trinkwasserspeicher" | "Verteiler" | "Weichen")[];
                                "matchingProductTypes": ("Longtherm" | "Longtherm Iso" | "MultiFlow Center" | "MultiFlow Expert" | "Sicherheitsventil" | "Kompaktverteiler" | "Lufttopf" | "Luftabscheider" | "Pumpe" | "Membran-Set" | "Kaskaden Unit" | "Entschlammungsgef\u00E4ss" | "D\u00E4mmung" | "Greenbox" | "LegioNixx" | "Steuerung" | "EEHR" | "EFHR" | "Anode" | "Exferro" | "Exiso" | "Inbetriebnahme" | "Wartung")[];
                                "technicalSpecs": {
                                    "maxPressureBar": number | null;
                                    "nominalVolumeLiters": number | null;
                                    "diameterMM": number | null;
                                    "maxTemperatureCelsius": number | null;
                                    "maxFlowRateM3H": number | null;
                                    "connectionSize": ("1\"" | "1 1/2\"" | "1 1/4\"" | "114,3" | "139,7" | "168,3" | "2\"" | "2 1/2\"" | "219,1" | "22 mm" | "273,0" | "28 mm" | "2xDN100 1xDN80" | "2xDN100/PN6" | "2xDN65 1xDN50" | "2xDN80 1xDN65" | "3\"" | "323,9" | "33,7" | "42,4" | "48,3" | "60,3" | "76,1" | "88,9" | "AG 1\"" | "AG 1 1/4\"" | "AG 2\"" | "AG 2 1/2\"" | "AG 3/4\"" | "AG 3/8\"" | "DN100" | "DN100/PN10" | "DN100/PN16" | "DN100/PN40" | "DN100/PN6" | "DN125" | "DN125/150/150" | "DN125/PN16" | "DN125/PN6" | "DN150" | "DN150/200/200" | "DN150/PN16" | "DN150/PN6" | "DN200" | "DN200/250/250" | "DN200/PN16" | "DN20/PN6" | "DN25" | "DN250" | "DN250/ANSI 150lbs" | "DN250/PN16" | "DN25/PN16" | "DN25/PN6" | "DN300" | "DN300/ANSI 150lbs" | "DN300/PN16" | "DN32" | "DN32/PN16" | "DN32/PN6" | "DN350/PN16" | "DN40" | "DN400/PN16" | "DN40/PN16" | "DN40/PN6" | "DN450/PN16" | "DN50" | "DN500/PN16" | "DN50/65/65" | "DN50/PN16" | "DN50/PN40" | "DN50/PN6" | "DN600/PN16" | "DN65" | "DN65/80/80" | "DN65/PN16" | "DN65/PN40" | "DN65/PN6" | "DN80" | "DN80/100/100" | "DN80/PN16" | "DN80/PN40" | "DN80/PN6" | "G 1\"" | "G 1 1/2\"" | "G 1 1/4\"" | "G 1/2\"" | "G 2\"" | "G 3/4\"" | "G 3/8\"" | "IG 1\"" | "IG 1 1/2\"" | "IG 1 1/4\"" | "IG 1/2\"" | "IG 2\"" | "IG 3/4\"" | "M8" | "R 1\"" | "R 1 1/2\"" | "R 1 1/4\"" | "R 1/4\"" | "R 2\"" | "R 3/4\"" | "Rp 1\"" | "Rp 1 1/2\"" | "Rp 1 1/4\"" | "Rp 1/2\"" | "Rp 2\"" | "Rp 2 1/2\"" | "Rp 3\"" | "Rp 3/4\"") | null;
                                    "connectionType": ("Flansch" | "Gewinde" | "Rohr" | "Rohr mit Gewinde" | "Schwei\u00DFstutzen" | "Klemmring" | "Pressflansch" | "Anschwei\u00DFenden") | null;
                                    "chamberSize": ("120/120" | "120/80" | "160/160" | "160/80" | "160/81" | "180/110" | "180/111" | "180/180" | "200/120" | "200/121" | "200/200" | "250/150" | "250/151" | "280/180" | "280/181" | "280/320" | "300/200" | "300/201" | "300/350" | "400/200" | "400/201" | "400/400" | "450/250" | "450/251" | "450/450" | "500/300" | "500/301" | "500/550" | "600/400" | "600/401" | "600/650" | "60/50" | "60/60" | "700/500" | "700/501" | "700/750" | "800/600" | "800/601" | "80/60" | "80/80" | "DN100" | "DN1000" | "DN1200" | "DN125" | "DN150" | "DN200" | "DN250" | "DN300" | "DN350" | "DN40" | "DN400" | "DN50" | "DN500" | "DN600" | "DN65" | "DN700" | "DN80" | "DN800" | "DN 800" | "DN900") | null;
                                    "thermalOutputKW": number | null;
                                    "material": string | null;
                                };
                            };
                            "expectedDeliveryDate": ({
                                "dateType": "NO_DATE_MENTIONED";
                            } | {
                                "dateType": "AS_SOON_AS_POSSIBLE";
                            } | {
                                "dateType": "DATE";
                                "isoDate": string;
                            } | {
                                "dateType": "CALENDAR_WEEK";
                                "year": number;
                                "week": number;
                            } | {
                                "dateType": "CALENDAR_MONTH";
                                "year": number;
                                "month": number;
                            }) | null;
                            "originalDeliveryDateExpression": string | null;
                            "productDescription": string;
                            "quantity": number | null;
                            "quantityUnitId": string | null;
                            "price": number | null;
                            "priceUnitId": string | null;
                            "priceQuantity": number | null;
                            "totalValue": number | null;
                        };
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                        "pIndex": number;
                        "tIndex": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "selectScheduleOfServicesRequestPositionProducts";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "orgId": string;
                        "dataSetId": string;
                        "threadId": `thr_${string}`;
                        "workflowId": `wkf_${string}`;
                        "messageIds": `msg_${string}`[];
                        "attachmentIds": `att_${string}`[];
                        "firstMessageReceivedAt": string | null;
                        "config": {
                            "orgId": string;
                            "name": string;
                            "slug": string;
                            "goLiveDate": string | null;
                            "domain": string;
                            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
                            "documentSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "emailSystem": ("ODOO" | "FILE_EXPORT" | "SAP_B1" | "DOCUWARE" | "FORM_DATA" | "UTZ" | "NONE") | null;
                            "syncEnabled": boolean;
                            "authSecretsPath": string;
                            "authSecretsPathByInboxId": Record<`inb_${string}`, string> | null;
                            "documentSystemAuthSecretsPath": string | null;
                            "emailSystemAuthSecretsPath": string | null;
                            "config": {
                                "autoSubmitEnabled"?: boolean;
                                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                                "extractedPositionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "extractedHeaderFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "productSelectionFields"?: {
                                    "technicalName": string;
                                    "readableNameEnglish": string;
                                    "readableNameGerman": string;
                                    "type": "string" | "number" | "money" | "date";
                                    "array"?: boolean;
                                    "nullable"?: boolean;
                                    "visible"?: boolean;
                                    "visibleWhenEmpty"?: boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "enum"?: {
                                        "type": "FIXED";
                                        "enum": string[];
                                        "labels"?: Record<string, string>;
                                    } | {
                                        "type": "DISTINCT_PRODUCT_VALUES";
                                        "source": {
                                            "type": "PRODUCT_ATTRIBUTE";
                                            "attributeName": string;
                                        } | {
                                            "type": "CUSTOM_PRODUCT_FIELD";
                                            "jsonPath": string;
                                        };
                                    };
                                    "description": string;
                                    "sortOrder": number;
                                    "editable": boolean;
                                }[];
                                "priceFetchTriggers"?: {
                                    "customerSelect"?: boolean;
                                    "multiMatchSelect"?: boolean;
                                    "quantityChange"?: boolean;
                                    "productCardEdit"?: boolean;
                                    "positionDelete"?: boolean;
                                    "positionDeleteRevert"?: boolean;
                                    "uomChange"?: boolean;
                                };
                                "orderHeaderFields"?: {
                                    "comment"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "shippingTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "paymentTerms"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                    "creditLimit"?: {
                                        "visible"?: boolean;
                                        "visibleWhenEmpty"?: boolean;
                                        "editable"?: boolean;
                                        "labelGerman"?: string;
                                        "labelEnglish"?: string;
                                        "placeholderGerman"?: string;
                                        "placeholderEnglish"?: string;
                                    };
                                };
                                "customOrderHeaderFields"?: ({
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "text";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "textarea";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "date";
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "number";
                                    "allowDecimals": boolean;
                                } | {
                                    "key": string;
                                    "editable": boolean;
                                    "visibleWhenEmpty": boolean;
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "placeholderGerman"?: string;
                                    "placeholderEnglish"?: string;
                                    "defaultValue"?: ({
                                        "type": "CONSTANT";
                                        "value": string;
                                    } | {
                                        "type": "PARTNER_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                                        "key": string;
                                    } | {
                                        "type": "TRANSACTION_CUSTOM_FIELD";
                                        "key": string;
                                    })[];
                                    "type": "select";
                                    "innerType": "text" | "number";
                                    "source": {
                                        "type": "DICTIONARY";
                                        "name": string;
                                    } | {
                                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                                        "namesBySellingEntityCode": Record<string, string>;
                                    } | {
                                        "type": "FIXED";
                                        "options": string[];
                                        "labels": Record<string, string>;
                                    };
                                })[];
                                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                                "positionFieldAlwaysEditable"?: string[];
                                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                                "addressFieldOrder"?: (("address") | string)[];
                                "displayQuantityInProductSalesUnit"?: boolean;
                                "handleDeliveryInstructions"?: boolean;
                                "deliveryAddressDisabled"?: boolean;
                                "allowNewBillingAddressMatch"?: boolean;
                                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                                "paymentTermsWhenAboveCreditLimit"?: string | null;
                                "replyTemplate"?: string | null;
                                "positionFields"?: {
                                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                    "visibleConditionally"?: {
                                        "dependentKey": string;
                                        "condition": {
                                            "type": "EQUALS";
                                            "value": string;
                                        } | {
                                            "type": "EXISTS";
                                        } | {
                                            "type": "NOT_EXISTS";
                                        };
                                    };
                                    "editSettings"?: {
                                        "required"?: boolean;
                                        "type": "date" | "text" | "number" | "select";
                                        "relatedFieldKey"?: string;
                                        "selectSettings"?: {
                                            "source": "PRODUCT_CUSTOM_FIELDS";
                                            "key": string;
                                        } | {
                                            "source": "DICTIONARY";
                                            "name": string;
                                        };
                                        "offerApplyToAllPositions"?: boolean;
                                    };
                                }[];
                                "customerFields"?: {
                                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                                    "labelGerman": string;
                                    "labelEnglish": string;
                                    "visibleWhenEmpty": boolean;
                                }[];
                                "billingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardBillingAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "standardDeliveryAddressLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "inboxFolders"?: ({
                                    "type": "rules";
                                    "inboxId": string;
                                    "basePath"?: string[];
                                    "rules": {
                                        "condition"?: Condition;
                                        "path": string;
                                        "folderMoveDelayMinutes"?: number;
                                    }[];
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                } | {
                                    "type"?: ("static") | null;
                                    "inboxId": string;
                                    "pipelineStartedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "readyForReviewFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "submittedFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "cancelledFolder"?: {
                                        "folderId": string;
                                    };
                                    "noTransactionFoundFolder"?: {
                                        "parentId": string;
                                        "folderName": string;
                                    };
                                    "folderMoveDelayMinutes"?: number;
                                })[];
                                "inboxLabelsGerman"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "inboxLabelsEnglish"?: {
                                    "pipelineStarted"?: string;
                                    "pipelineFinished"?: string;
                                    "pipelineError"?: string;
                                    "submittedSuccessfully"?: string;
                                    "submittedWithError"?: string;
                                    "ORDER"?: string;
                                    "REQUEST_FOR_QUOTATION"?: string;
                                };
                                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                                "emailPushFileName"?: string;
                                "salesOrderBasic"?: {
                                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                                    "stripProductTexts"?: boolean;
                                    "allowNoMatch"?: boolean;
                                    "allowMultiMatch"?: boolean;
                                };
                                "salesOrderPOC"?: {
                                    "showProductCustomFieldsToLLM"?: boolean;
                                    "showUnitPricesToLLM"?: string[];
                                    "defaultCustomerAccountIdForPriceLookup"?: string;
                                    "validateMultiMatchesWithThoughts"?: boolean;
                                    "minimumLengthForLevenshteinDiff"?: number;
                                    "allowedLevenshteinDiffForArticleNumber"?: number;
                                    "allowedArticleNumberPrefixLength"?: number;
                                    "allowedArticleNumberSuffixLength"?: number;
                                };
                                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                                "contactMatchingEnabled"?: boolean;
                                "replyGenerationEnabled"?: boolean;
                                "attachedImageByteSizeThreshold"?: number;
                                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                                "sellingEntityHeader"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                };
                                "sellingEntityFields"?: ("name" | "address")[];
                                "sellingEntityEditable"?: "NO" | "INLINE";
                                "mockSubmission"?: boolean;
                                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                                "productIdWhenNoMatch"?: string;
                                "productIdWhenNoMatchBadgeText"?: string;
                                "productInvalidLabel"?: {
                                    "DE"?: string;
                                    "EN"?: string;
                                } | null;
                                "standardHeaderFieldLabels"?: {
                                    "orderReference"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "quoteNumber"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "standardPositionFieldLabels"?: {
                                    "price"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                    "totalValue"?: {
                                        "de"?: string;
                                        "en"?: string;
                                    };
                                };
                                "orderSplitStrategy"?: {
                                    "strategy": "DELIVERY_DATE";
                                    "applyByDefault": boolean;
                                    "offerReprocess": boolean;
                                };
                                "ignoreGlobalPriceLists"?: boolean;
                                "nonExactArticleNumberMatch"?: boolean;
                                "partialCustomerArticleNumberMatch"?: boolean;
                                "matchCustomerArticleNumbersGlobally"?: boolean;
                                "exposedDictionariesToUI"?: string[];
                                "judgeTransactionDetection"?: boolean;
                                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                                "searchProductsByCustomerAccountNumbers"?: boolean;
                                "searchProductsByTextSearch"?: boolean;
                                "restrictSearchToCustomerProducts"?: boolean;
                                "additionalCostsEnabled"?: boolean;
                                "priceFetchEnabled"?: boolean;
                                "additionalOrderNumberSeparator"?: string;
                                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                                "warnings"?: ("QUOTE_NUMBER")[];
                                "eliminateQuantityZeroPositions"?: boolean;
                                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                                "classifications"?: {
                                    "name": string;
                                    "description"?: string;
                                    "pipeline"?: string;
                                }[];
                            };
                            "legalEntityNames": string[];
                            "defaultCurrencyCode": string;
                            "defaultLocale": string;
                            "createdAt": string;
                            "updatedAt": string;
                        };
                        "result": {
                            "transactions": {
                                "id": `tra_${string}`;
                                "relevantMessageIds"?: string[];
                                "relevantAttachmentIds"?: string[];
                                "customerAccountIds"?: string[];
                                "billToPartyIds"?: string[];
                                "shipToPartyIds"?: string[];
                                "newBilledPartyAddress"?: Serializable | null;
                                "newPayingPartyAddress"?: Serializable | null;
                                "newShipToAddress"?: Serializable | null;
                                "sellingEntityId"?: string | null;
                                "sellingEntityAddressId"?: string | null;
                                "receivedDate": string;
                                "emailReceivedDate"?: string | null;
                                "orderDate": string;
                                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                                "contactId"?: string | null;
                                "contactName": string;
                                "contactEmail": string;
                                "contactPhone"?: string | null;
                                "originalOrderReference"?: string | null;
                                "orderReference"?: string | null;
                                "quoteNumber"?: string | null;
                                "transactionType": string;
                                "eligibleOrderSplitStrategy"?: string | null;
                                "appliedOrderSplitStrategy"?: string | null;
                                "currencyId": string;
                                "currencyCode": string;
                                "customFields"?: Record<string, Serializable> | null;
                                "positions": {
                                    "id": `pos_${string}`;
                                    "invalidated": boolean;
                                    "positionIndex": number;
                                    "extractedPositionIndex"?: string | null;
                                    "deliveryDate"?: string | null;
                                    "extractedDeliveryDate"?: string | null;
                                    "extractedArticleNumbers"?: string[] | null;
                                    "extractedDescription": string;
                                    "extractedQuantity1": number;
                                    "extractedQuantityUnit1": string;
                                    "extractedQuantity2"?: number | null;
                                    "extractedQuantityUnit2"?: string | null;
                                    "extractedQuantity3"?: number | null;
                                    "extractedQuantityUnit3"?: string | null;
                                    "extractedPrice1"?: number | null;
                                    "extractedPriceUnit1"?: string | null;
                                    "extractedPriceQuantity1"?: number | null;
                                    "extractedPrice2"?: number | null;
                                    "extractedPriceUnit2"?: string | null;
                                    "extractedPriceQuantity2"?: number | null;
                                    "extractedPrice3"?: number | null;
                                    "extractedPriceUnit3"?: string | null;
                                    "extractedPriceQuantity3"?: number | null;
                                    "extractedTotalValue"?: number | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                    "matches": {
                                        "id": `pma_${string}`;
                                        "invalidated": boolean;
                                        "productId": string;
                                        "product": Serializable;
                                        "quantity": number;
                                        "validQuantity": number;
                                        "quantityUnitId": string;
                                        "quantityUnitCode": string;
                                        "price"?: number | null;
                                        "priceUnitId"?: string | null;
                                        "priceUnitCode"?: string | null;
                                        "priceQuantity"?: number | null;
                                        "matchingWarning"?: boolean;
                                        "matchingExplanation"?: string | null;
                                        "customFields"?: Record<string, Serializable> | null;
                                    }[];
                                }[];
                                "invalidated": boolean;
                                "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                                "autoSubmitError"?: string | null;
                                "autoSubmittedAt"?: string | null;
                            }[];
                        };
                        "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[] | null;
                        "judge"?: boolean;
                        "relevantAttachmentIdsByTransaction": Record<`tra_${string}`, `att_${string}`[]>;
                        "relevantMessageIdsByTransaction": Record<`tra_${string}`, `msg_${string}`[]>;
                        "detectedPosition": {
                            "positionIndex": number;
                            "positionNumber": string | null;
                            "productAttributes": {
                                "articleNumbers": string[];
                                "categories": string[];
                                "displayRanges": string[] | null;
                                "installationLengths": string[] | null;
                                "nominalSizes": string[] | null;
                                "accuracyClasses": string[] | null;
                                "nominalPressures": string[] | null;
                                "enclosureMaterials": string[] | null;
                                "measuringSystemMaterials": string[] | null;
                                "connectorSizes": string[] | null;
                            };
                            "expectedDeliveryDate": ({
                                "dateType": "NO_DATE_MENTIONED";
                            } | {
                                "dateType": "AS_SOON_AS_POSSIBLE";
                            } | {
                                "dateType": "DATE";
                                "isoDate": string;
                            } | {
                                "dateType": "CALENDAR_WEEK";
                                "year": number;
                                "week": number;
                            } | {
                                "dateType": "CALENDAR_MONTH";
                                "year": number;
                                "month": number;
                            }) | null;
                            "originalDeliveryDateExpression": string | null;
                            "productDescription": string;
                            "quantity": number | null;
                            "quantityUnitId": string | null;
                            "price": number | null;
                            "priceUnitId": string | null;
                            "priceQuantity": number | null;
                            "totalValue": number | null;
                            "isAccessory"?: boolean | null;
                        };
                        "transaction": {
                            "id": `tra_${string}`;
                            "relevantMessageIds"?: string[];
                            "relevantAttachmentIds"?: string[];
                            "customerAccountIds"?: string[];
                            "billToPartyIds"?: string[];
                            "shipToPartyIds"?: string[];
                            "newBilledPartyAddress"?: Serializable | null;
                            "newPayingPartyAddress"?: Serializable | null;
                            "newShipToAddress"?: Serializable | null;
                            "sellingEntityId"?: string | null;
                            "sellingEntityAddressId"?: string | null;
                            "receivedDate": string;
                            "emailReceivedDate"?: string | null;
                            "orderDate": string;
                            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                            "contactId"?: string | null;
                            "contactName": string;
                            "contactEmail": string;
                            "contactPhone"?: string | null;
                            "originalOrderReference"?: string | null;
                            "orderReference"?: string | null;
                            "quoteNumber"?: string | null;
                            "transactionType": string;
                            "eligibleOrderSplitStrategy"?: string | null;
                            "appliedOrderSplitStrategy"?: string | null;
                            "currencyId": string;
                            "currencyCode": string;
                            "customFields"?: Record<string, Serializable> | null;
                            "positions": {
                                "id": `pos_${string}`;
                                "invalidated": boolean;
                                "positionIndex": number;
                                "extractedPositionIndex"?: string | null;
                                "deliveryDate"?: string | null;
                                "extractedDeliveryDate"?: string | null;
                                "extractedArticleNumbers"?: string[] | null;
                                "extractedDescription": string;
                                "extractedQuantity1": number;
                                "extractedQuantityUnit1": string;
                                "extractedQuantity2"?: number | null;
                                "extractedQuantityUnit2"?: string | null;
                                "extractedQuantity3"?: number | null;
                                "extractedQuantityUnit3"?: string | null;
                                "extractedPrice1"?: number | null;
                                "extractedPriceUnit1"?: string | null;
                                "extractedPriceQuantity1"?: number | null;
                                "extractedPrice2"?: number | null;
                                "extractedPriceUnit2"?: string | null;
                                "extractedPriceQuantity2"?: number | null;
                                "extractedPrice3"?: number | null;
                                "extractedPriceUnit3"?: string | null;
                                "extractedPriceQuantity3"?: number | null;
                                "extractedTotalValue"?: number | null;
                                "customFields"?: Record<string, Serializable> | null;
                                "matches": {
                                    "id": `pma_${string}`;
                                    "invalidated": boolean;
                                    "productId": string;
                                    "product": Serializable;
                                    "quantity": number;
                                    "validQuantity": number;
                                    "quantityUnitId": string;
                                    "quantityUnitCode": string;
                                    "price"?: number | null;
                                    "priceUnitId"?: string | null;
                                    "priceUnitCode"?: string | null;
                                    "priceQuantity"?: number | null;
                                    "matchingWarning"?: boolean;
                                    "matchingExplanation"?: string | null;
                                    "customFields"?: Record<string, Serializable> | null;
                                }[];
                            }[];
                            "invalidated": boolean;
                            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
                            "autoSubmitError"?: string | null;
                            "autoSubmittedAt"?: string | null;
                        };
                        "pIndex": number;
                        "tIndex": number;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "sendApprovalRequest";
                "options": {
                    "args": {
                        "organizationId": string;
                        "taskData": {
                            "taskId": `apt_${string}`;
                            "reminder"?: boolean;
                        } | {
                            "email": string;
                            "keys": string[];
                            "processingId": number;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncAbstractEntitySource";
                "options": {
                    "args": {
                        "organizationId": string;
                        "id": `abs_${string}`;
                        "strategy": "ONE_SHOT" | "UNTIL_EMPTY";
                        "forceSyncState"?: Serializable;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncEntitySource";
                "options": {
                    "args": {
                        "entitySourceId": `src_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncInbox";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncSOCustomerData";
                "options": {
                    "args": {
                        "organizationId": string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncSqlEntitySource";
                "options": {
                    "args": {
                        "id": `sql_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "syncThread";
                "options": {
                    "args": {
                        "inboxId": `inb_${string}`;
                        "threadId": `thr_${string}`;
                        "externalMessageIds"?: string[];
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "testActivity";
                "options": {
                    "args": {
                        "idSuffix"?: string;
                        "activity": string;
                        "input": SerializableObject;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "triggerApprovalFlow";
                "options": {
                    "args": {
                        "organizationId": string;
                        "keyEmailPairs": {
                            "key": string;
                            "email": string;
                            "role": string | {
                                "email": ("self") | string;
                            };
                        }[];
                        "processingId": number;
                        "manageInbox"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "universalAgent";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                        "settingsKey"?: string;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "updateAswoPurchaseOrder";
                "options": {
                    "args": {
                        "organizationId": string;
                        "update": {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "updateMsDynamicsPurchaseOrder";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                        "update": {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "updatePurchaseOrderByProcessingId";
                "options": {
                    "args": {
                        "organizationId": string;
                        "processingId": number;
                        "nextStatus": "confirmed" | "declined";
                        "manageInbox"?: boolean;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "updatePurchaseOrderByUpdate";
                "options": {
                    "args": {
                        "organizationId": string;
                        "update": {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                        "threadId": `thr_${string}`;
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            } | {
                "workflowType": "updateSapPurchaseOrder";
                "options": {
                    "args": {
                        "organizationId": string;
                        "threadId": `thr_${string}`;
                        "update": {
                            "number": string;
                            "head": {
                                "incoterms1": string | null;
                                "incoterms2": string | null;
                                "paymentTerms": string | null;
                            };
                            "items": {
                                "id": string;
                                "serialNumber"?: number;
                                "supplierSku": string | null;
                                "unitPrice": BigNumberValue | null;
                                "quantity": BigNumberValue | null;
                                "extra": {
                                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                    "calculation": {
                                        "class": "percentage";
                                        "rate": BigNumberValue | null;
                                        "appliedTo": "gross" | "net";
                                    } | {
                                        "class": "fixedAmount";
                                        "amount": BigNumberValue | null;
                                        "distributed": boolean;
                                    } | {
                                        "class": "quantityBased";
                                        "quantity": BigNumberValue;
                                        "quantityUom": string;
                                        "unitPrice": BigNumberValue;
                                        "priceUom": string;
                                        "perAmount": BigNumberValue;
                                        "uomConversionCoefficient": BigNumberValue;
                                    };
                                }[] | null;
                                "confirmationNumber"?: string | null;
                                "confirmations": {
                                    "quantity": BigNumberValue;
                                    "deliveryDate": string;
                                    "reference": string | null;
                                }[] | null;
                            }[];
                            "headerExtra": {
                                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                                "calculation": {
                                    "class": "percentage";
                                    "rate": BigNumberValue | null;
                                    "appliedTo": "gross" | "net";
                                } | {
                                    "class": "fixedAmount";
                                    "amount": BigNumberValue | null;
                                    "distributed": boolean;
                                } | {
                                    "class": "quantityBased";
                                    "quantity": BigNumberValue;
                                    "quantityUom": string;
                                    "unitPrice": BigNumberValue;
                                    "priceUom": string;
                                    "perAmount": BigNumberValue;
                                    "uomConversionCoefficient": BigNumberValue;
                                };
                            }[] | null;
                            "masterPrice"?: {
                                "positions": {
                                    "id": string;
                                    "value": BigNumberValue | null;
                                    "masterPriceId"?: string;
                                }[];
                            };
                            "arbitrary"?: Record<string, Serializable>;
                        };
                    }[];
                    "workflowId"?: string;
                    "taskQueue"?: string;
                    "workflowIdConflictPolicy"?: "FAIL" | "USE_EXISTING" | "TERMINATE_EXISTING";
                    "organizationId"?: string;
                };
            };
        }, Res["Start workflow"]>("POST", "/api/admin/workflow"),
        /** POST /api/approvals/{approvalTaskId} */
        "Submit approval review": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            jsonBody: {
                "itemIdToIndex": Record<string, number>;
                "indexToSerialNumber": Record<string, number>;
                "decisions": Record<string, AssistantDecisionSchema>;
                "masterPriceDecisions": Record<string, {
                    "type": "accepted";
                    "value": BigNumberValue | null;
                    "id"?: string;
                } | {
                    "type": "rejected";
                }>;
                "corrections": Record<string, (string | null) | (boolean | null) | (string | null) | {
                    "amount": {
                        "amount": BigNumberValue | null;
                        "currency": string | null;
                    } | null;
                    "quantity": {
                        "amount": BigNumberValue | null;
                        "unit": string | null;
                    } | null;
                    "extra"?: {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                } | {
                    "amount": BigNumberValue | null;
                    "currency": string | null;
                } | {
                    "amount": BigNumberValue | null;
                    "unit": string | null;
                } | (("open" | "partiallyConfirmed" | "confirmed") | null) | {
                    "id": string | null;
                    "source": "paymentTerms";
                } | {
                    "incoterms1": string | null;
                    "incoterms2": string | null;
                } | {
                    "schedules": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                        "status"?: string;
                    }[];
                    "combineMode"?: "partial" | "reduction";
                    "confirmations": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                    }[];
                } | {
                    "lines": {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                }>;
                "arbitrary": Record<string, Serializable>;
            };
        }, Res["Submit approval review"]>("POST", "/api/approvals/{approvalTaskId}"),
        /** POST /api/approvals/{approvalTaskId}/resend */
        "Resend approval task": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            query: {
                "token": string;
            };
        }, Res["Resend approval task"]>("POST", "/api/approvals/{approvalTaskId}/resend"),
        /** POST /api/approvals/{approvalTaskId}/token */
        "Submit approval review with token": handler<{
            params: {
                "approvalTaskId": `apt_${string}`;
            };
            query: {
                "token": string;
            };
            jsonBody: {
                "itemIdToIndex": Record<string, number>;
                "indexToSerialNumber": Record<string, number>;
                "decisions": Record<string, AssistantDecisionSchema>;
                "masterPriceDecisions": Record<string, {
                    "type": "accepted";
                    "value": BigNumberValue | null;
                    "id"?: string;
                } | {
                    "type": "rejected";
                }>;
                "corrections": Record<string, (string | null) | (boolean | null) | (string | null) | {
                    "amount": {
                        "amount": BigNumberValue | null;
                        "currency": string | null;
                    } | null;
                    "quantity": {
                        "amount": BigNumberValue | null;
                        "unit": string | null;
                    } | null;
                    "extra"?: {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                } | {
                    "amount": BigNumberValue | null;
                    "currency": string | null;
                } | {
                    "amount": BigNumberValue | null;
                    "unit": string | null;
                } | (("open" | "partiallyConfirmed" | "confirmed") | null) | {
                    "id": string | null;
                    "source": "paymentTerms";
                } | {
                    "incoterms1": string | null;
                    "incoterms2": string | null;
                } | {
                    "schedules": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                        "status"?: string;
                    }[];
                    "combineMode"?: "partial" | "reduction";
                    "confirmations": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                    }[];
                } | {
                    "lines": {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                }>;
                "arbitrary": Record<string, Serializable>;
            };
        }, Res["Submit approval review with token"]>("POST", "/api/approvals/{approvalTaskId}/token"),
        /** POST /api/assistant */
        "Submit assistant review": handler<{
            jsonBody: {
                "poProcessingId": number;
                "poNumber": string;
                "itemIdToIndex": Record<string, number>;
                "indexToSerialNumber": Record<string, number>;
                "decisions": Record<string, AssistantDecisionSchema>;
                "masterPriceDecisions": Record<string, {
                    "type": "accepted";
                    "value": BigNumberValue | null;
                    "id"?: string;
                } | {
                    "type": "rejected";
                }>;
                "corrections": Record<string, (string | null) | (boolean | null) | (string | null) | {
                    "amount": {
                        "amount": BigNumberValue | null;
                        "currency": string | null;
                    } | null;
                    "quantity": {
                        "amount": BigNumberValue | null;
                        "unit": string | null;
                    } | null;
                    "extra"?: {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                } | {
                    "amount": BigNumberValue | null;
                    "currency": string | null;
                } | {
                    "amount": BigNumberValue | null;
                    "unit": string | null;
                } | (("open" | "partiallyConfirmed" | "confirmed") | null) | {
                    "id": string | null;
                    "source": "paymentTerms";
                } | {
                    "incoterms1": string | null;
                    "incoterms2": string | null;
                } | {
                    "schedules": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                        "status"?: string;
                    }[];
                    "combineMode"?: "partial" | "reduction";
                    "confirmations": {
                        "quantity": {
                            "amount": BigNumberValue | null;
                            "unit": string | null;
                        };
                        "deliveryDate": string | null;
                        "reference": string | null;
                    }[];
                } | {
                    "lines": {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                        "name"?: string;
                    }[];
                }>;
                "arbitrary": Record<string, Serializable>;
                "approvalTriggers": {
                    "threshold": ({
                        "role": string | {
                            "email": ("self") | string;
                        };
                    } & ({
                        "type": "alwaysWithin";
                        "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                        "active": boolean;
                    } | {
                        "type": "anyChange";
                        "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                        "active": boolean;
                    } | {
                        "type": "absoluteDifference";
                        "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                        "direction": "positive" | "negative" | "both";
                        "value": number;
                        "active": boolean;
                    } | {
                        "type": "absoluteValue";
                        "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                        "direction": "positive" | "negative" | "both";
                        "value": number;
                        "active": boolean;
                    } | {
                        "type": "percentage";
                        "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                        "direction": "positive" | "negative" | "both";
                        "value": number;
                        "active": boolean;
                    })) & {
                        "key": string;
                        "before": Serializable;
                        "after": Serializable;
                    };
                    "email": string;
                }[];
            };
        }, Res["Submit assistant review"]>("POST", "/api/assistant"),
        /** POST /api/assistant/cancel */
        "Cancel po processing": handler<{
            jsonBody: {
                "poProcessingId": number;
            };
        }, Res["Cancel po processing"]>("POST", "/api/assistant/cancel"),
        /** POST /api/assistant/cancel/token */
        "Cancel po processing with token": handler<{
            query: {
                "token": string;
            };
            jsonBody: {
                "poProcessingId": number;
            };
        }, Res["Cancel po processing with token"]>("POST", "/api/assistant/cancel/token"),
        /** POST /api/auth-custom/members/{memberId} */
        "Update organization member": handler<{
            params: {
                "memberId": string;
            };
            jsonBody: {
                "firstName": string;
                "lastName": string;
                "memberRole": "manager" | "member";
                "teamIds": string[];
            };
        }, Res["Update organization member"]>("POST", "/api/auth-custom/members/{memberId}"),
        /** POST /api/auth-custom/members/invite */
        "Invite organization member": handler<{
            jsonBody: {
                "email": string;
                "firstName": string;
                "lastName": string;
                "role"?: "user" | "service";
                "memberRole": "manager" | "member";
                "teamIds"?: string[];
            };
        }, Res["Invite organization member"]>("POST", "/api/auth-custom/members/invite"),
        /** POST /api/browser-extension */
        "Browser Extension": handler<{
            jsonBody: {
                "tabURL": string;
            };
        }, Res["Browser Extension"]>("POST", "/api/browser-extension"),
        /** POST /api/cases/acknowledgements/{caseId}/acknowledge */
        "Acknowledge case": handler<{
            params: {
                "caseId": `acs_${string}`;
            };
        }, Res["Acknowledge case"]>("POST", "/api/cases/acknowledgements/{caseId}/acknowledge"),
        /** POST /api/dashboard/transactions */
        "Get dashboard transactions": handler<{
            jsonBody: {
                "from": string;
                "to": string;
                "page": number;
                "pageSize": number;
                "searchQuery"?: string;
                "statusFilter"?: "all" | "submitted" | "auto-submitted" | "pending";
                "adjustments"?: ("CUSTOMER_CHANGED" | "QUANTITY_CHANGED" | "PRODUCT_CHANGED" | "POSITION_ADDED" | "POSITION_REMOVED" | "PRODUCT_NOT_DETECTED")[];
                "sortColumn"?: "emailSubject" | "isSubmitted" | "customerName" | "suggestedPositionCount" | "submittedPositionCount" | "orderReference" | "erpTransactionNumber" | "processedAt" | "submittedAt" | "submittedBy";
                "sortDirection"?: "asc" | "desc";
            };
        }, Res["Get dashboard transactions"]>("POST", "/api/dashboard/transactions"),
        /** POST /api/organizations/{organizationId}/products-by-ids */
        "Get products by ids": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "ids": string[];
                "inboxId": `inb_${string}`;
            };
        }, Res["Get products by ids"]>("POST", "/api/organizations/{organizationId}/products-by-ids"),
        /** POST /api/organizations/erp/po/{number}/approvers/authorization */
        "Check approver authorization": handler<{
            params: {
                "number": string;
            };
            jsonBody: {
                "key": string;
                "role": string | {
                    "email": ("self") | string;
                };
                "currency": string | null;
                "comparison": PurchaseOrderComparisonSchema;
                "approverEntity": Serializable;
            };
        }, Res["Check approver authorization"]>("POST", "/api/organizations/erp/po/{number}/approvers/authorization"),
        /** POST /api/preferences */
        "Update preferences": handler<{
            jsonBody: {
                "domains": string[];
            };
        }, Res["Update preferences"]>("POST", "/api/preferences"),
        /** POST /api/salesorder/additional-costs */
        "Get Sales Order Additional Costs": handler<{
            jsonBody: {
                "threadId": `thr_${string}`;
                "partnerId": string;
                "total": number;
            };
        }, Res["Get Sales Order Additional Costs"]>("POST", "/api/salesorder/additional-costs"),
        /** POST /api/salesorder/cancel */
        "Cancel sales order transaction": handler<{
            jsonBody: {
                "transactionId": `tra_${string}`;
                "reason": string | null;
            };
        }, Res["Cancel sales order transaction"]>("POST", "/api/salesorder/cancel"),
        /** POST /api/salesorder/customer-contacts */
        "Get customer contacts": handler<{
            jsonBody: {
                "customerAccountIds": string[];
                "inboxId": `inb_${string}`;
            };
        }, Res["Get customer contacts"]>("POST", "/api/salesorder/customer-contacts"),
        /** POST /api/salesorder/fetch-prices */
        "fetch prices": handler<{
            jsonBody: {
                "threadId": `thr_${string}`;
                "customerAccountId"?: string | null;
                "partnerId"?: string | null;
                "currencyId": string;
                "currencyCode": string;
                "items": {
                    "productId": string;
                    "quantity": number;
                    "quantityUnitId"?: string | null;
                    "quantityUnitCode": string;
                }[];
            };
        }, Res["fetch prices"]>("POST", "/api/salesorder/fetch-prices"),
        /** POST /api/salesorder/generate-reply */
        "Generate reply": handler<{
            jsonBody: {
                "transactions": {
                    "transactionId": `tra_${string}`;
                    "workflowId": `wkf_${string}`;
                }[];
            };
        }, Res["Generate reply"]>("POST", "/api/salesorder/generate-reply"),
        /** POST /api/salesorder/product-search-suggestions */
        "Get product search default suggestions": handler<{
            jsonBody: {
                "customerAccountIds": string[];
                "inboxId": `inb_${string}`;
            };
        }, Res["Get product search default suggestions"]>("POST", "/api/salesorder/product-search-suggestions"),
        /** POST /api/salesorder/products/search */
        "Search products by code, title, description, or article number": handler<{
            jsonBody: {
                "q": string;
                "customerAccountId": string | null;
                "inboxId": `inb_${string}`;
                "limit"?: number;
            };
        }, Res["Search products by code, title, description, or article number"]>("POST", "/api/salesorder/products/search"),
        /** POST /api/salesorder/reprocess/{threadId} */
        "Reprocess thread by user": handler<{
            params: {
                "threadId": `thr_${string}`;
            };
            jsonBody: {
                "shouldApplyOrderSplit": boolean;
            };
        }, Res["Reprocess thread by user"]>("POST", "/api/salesorder/reprocess/{threadId}"),
        /** POST /api/salesorder/submit */
        "Submit sales order to ERP": handler<{
            jsonBody: {
                "transaction": {
                    "shippingTerms": string | null;
                    "paymentTerms": string | null;
                    "suggestedTransactionId": string;
                    "threadId": `thr_${string}`;
                    "customerAccountId": string;
                    "billToPartyId": string;
                    "billToAddressId": string;
                    "shipToPartyId": string;
                    "shipToAddressId": string;
                    "sellingEntityId"?: string | null;
                    "sellingEntityAddressId"?: string | null;
                    "newBilledPartyAddress"?: {
                        "name1": string;
                        "name2": string | null;
                        "name3": string | null;
                        "street1": string;
                        "houseNumber1": string;
                        "streetWithNumber1": string;
                        "street2": string | null;
                        "houseNumber2": string | null;
                        "streetWithNumber2": string | null;
                        "street3": string | null;
                        "houseNumber3": string | null;
                        "streetWithNumber3": string | null;
                        "deliveryInstructions"?: string[] | null;
                        "postalCode": string;
                        "postBox": string | null;
                        "city": string;
                        "county": string | null;
                        "countryCode": string;
                    } | null;
                    "newPayingPartyAddress"?: {
                        "name1": string;
                        "name2": string | null;
                        "name3": string | null;
                        "street1": string;
                        "houseNumber1": string;
                        "streetWithNumber1": string;
                        "street2": string | null;
                        "houseNumber2": string | null;
                        "streetWithNumber2": string | null;
                        "street3": string | null;
                        "houseNumber3": string | null;
                        "streetWithNumber3": string | null;
                        "deliveryInstructions"?: string[] | null;
                        "postalCode": string;
                        "postBox": string | null;
                        "city": string;
                        "county": string | null;
                        "countryCode": string;
                    } | null;
                    "newShipToAddress"?: {
                        "name1": string;
                        "name2": string | null;
                        "name3": string | null;
                        "street1": string;
                        "houseNumber1": string;
                        "streetWithNumber1": string;
                        "street2": string | null;
                        "houseNumber2": string | null;
                        "streetWithNumber2": string | null;
                        "street3": string | null;
                        "houseNumber3": string | null;
                        "streetWithNumber3": string | null;
                        "deliveryInstructions"?: string[] | null;
                        "postalCode": string;
                        "postBox": string | null;
                        "city": string;
                        "county": string | null;
                        "countryCode": string;
                    } | null;
                    "receivedDate": string;
                    "emailReceivedDate"?: string | null;
                    "entryDate"?: string | null;
                    "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                    "orderDate": string;
                    "contactId"?: string | null;
                    "contactName": string;
                    "contactEmail": string;
                    "contactPhone": string;
                    "orderReference": string;
                    "quoteNumber": string | null;
                    "comment": string;
                    "transactionType": string;
                    "currencyId": string;
                    "currencyCode": string;
                    "deletedPositionIds"?: string[];
                    "customFields"?: Record<string, Serializable> | null;
                };
                "calculation": {
                    "total": number;
                };
                "positions": {
                    "suggestedPositionId": string;
                    "selectedMatchId": string;
                    "added": boolean;
                    "positionIndex": number;
                    "productId": string;
                    "quantity": number;
                    "quantityUnitId": string;
                    "quantityUnitCode": string;
                    "price": number;
                    "priceUnitId": string;
                    "priceUnitCode": string;
                    "priceQuantity": number;
                    "deliveryDate"?: string | null;
                    "externalPositionNumber"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "mock"?: boolean;
                "splitOrdersPerPosition"?: boolean;
            };
        }, Res["Submit sales order to ERP"]>("POST", "/api/salesorder/submit"),
        /** POST /api/salesorder/trigger-folder-move */
        "Trigger email folder move": handler<{
            jsonBody: {
                "suggestedTransactionIds": `tra_${string}`[];
            };
        }, Res["Trigger email folder move"]>("POST", "/api/salesorder/trigger-folder-move"),
        /** POST /api/threads */
        "Search threads": handler<{
            jsonBody: {
                "inboxIds"?: `inb_${string}`[];
                "threadIds"?: `thr_${string}`[];
                "externalThreadIds"?: string[];
                "query"?: string;
                "offset"?: number;
                "limit"?: number;
            };
        }, Res["Search threads"]>("POST", "/api/threads"),
        /** POST /api/threads/by-any-id */
        "Get thread by any id": handler<{
            jsonBody: {
                "inboxId"?: `inb_${string}`;
                "id": string;
            };
        }, Res["Get thread by any id"]>("POST", "/api/threads/by-any-id"),
        /** POST /api/uify/prompts/{promptName} */
        "Uify - Resolve prompt by name": handler<{
            params: {
                "promptName": string;
            };
            headers: {
                "x-uify-secret": string;
            };
            jsonBody: {
                "input"?: SerializableObject;
            };
        }, Res["Uify - Resolve prompt by name"]>("POST", "/api/uify/prompts/{promptName}"),
        /** POST /api/universal-agent/assistant-cases */
        "Get Assistant Cases": handler<{
            jsonBody: {
                "referenceId": string;
            };
        }, Res["Get Assistant Cases"]>("POST", "/api/universal-agent/assistant-cases"),
        /** POST /api/universal-agent/assistant-cases/{caseId}/review */
        "Submit Universal Agent Case Review": handler<{
            params: {
                "caseId": `acs_${string}`;
            };
            jsonBody: {
                "decisions": ({
                    "type": "changed";
                    "path": (string | number)[];
                    "value": Serializable;
                } | {
                    "type": "addElement";
                    "path": (string | number)[];
                    "value": Serializable;
                } | {
                    "type": "removeElement";
                    "path": (string | number)[];
                })[];
            };
        }, Res["Submit Universal Agent Case Review"]>("POST", "/api/universal-agent/assistant-cases/{caseId}/review"),
        /** POST /api/workbench */
        "Get Workbench view": handler<{
            jsonBody: {
                "page": number;
                "pageSize": number;
                "filter"?: Condition;
                "searchQuery"?: string;
            };
        }, Res["Get Workbench view"]>("POST", "/api/workbench"),
        /** POST /api/workbench/case-comment */
        "Upsert case comment": handler<{
            jsonBody: {
                "caseId": string;
                "comment": string;
            };
        }, Res["Upsert case comment"]>("POST", "/api/workbench/case-comment"),
        /** POST /api/workbench/case-metadata */
        "Get case metadata": handler<{
            jsonBody: {
                "caseId"?: string;
                "processingId"?: number;
            };
        }, Res["Get case metadata"]>("POST", "/api/workbench/case-metadata"),
        /** POST /api/workbench/case-owners */
        "Set case owners": handler<{
            jsonBody: {
                "caseId": string;
                "userIds": string[];
            };
        }, Res["Set case owners"]>("POST", "/api/workbench/case-owners"),
        /** POST /api/workbench/case-tags */
        "Set case tags": handler<{
            jsonBody: {
                "caseId": string;
                "tagIds": `ctg_${string}`[];
            };
        }, Res["Set case tags"]>("POST", "/api/workbench/case-tags"),
        /** PUT /api/abstract-sources/{sourceId}/entities/search */
        "Search abstract entities": handler<{
            params: {
                "sourceId": `abs_${string}`;
            };
            jsonBody: {
                "query": string;
            };
        }, Res["Search abstract entities"]>("PUT", "/api/abstract-sources/{sourceId}/entities/search"),
        /** PUT /api/admin/inboxes/{inboxId} */
        "Enable inbox": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
            jsonBody: {
                "syncFrom": string;
                "exchangeFolderIds"?: string[];
                "gmailLabelIds"?: string[];
                "subjectPrefix"?: string;
                "senders"?: string[];
                "receivers"?: string[];
            };
        }, Res["Enable inbox"]>("PUT", "/api/admin/inboxes/{inboxId}"),
        /** PUT /api/admin/inboxes/{inboxId}/disable */
        "Disable inbox": handler<{
            params: {
                "inboxId": `inb_${string}`;
            };
        }, Res["Disable inbox"]>("PUT", "/api/admin/inboxes/{inboxId}/disable"),
        /** PUT /api/admin/organizations/{organizationId}/abstract-sources/{id} */
        "Update abstract entity source": handler<{
            params: {
                "organizationId": string;
                "id": `abs_${string}`;
            };
            jsonBody: {
                "entity": string;
                "definition": {
                    "source": {
                        "type": "S3_EXPORT";
                        "bucket": string;
                        "key": string;
                        "format": {
                            "type": "CSV";
                            "removeDuplicateLines"?: boolean;
                            "delimiter"?: string;
                        };
                    } | {
                        "type": "ODATA";
                        "name": string;
                        "path"?: string;
                        "query"?: Record<string, string>;
                        "map": string | string[];
                        "syncState"?: string | string[];
                    } | {
                        "type": "MANUAL";
                    };
                    "fields": Record<string, {
                        "type": "string";
                        "behavior": "identity" | "reference" | "required" | "optional";
                        "indexed"?: boolean;
                    }>;
                };
            };
        }, Res["Update abstract entity source"]>("PUT", "/api/admin/organizations/{organizationId}/abstract-sources/{id}"),
        /** PUT /api/admin/organizations/{organizationId}/master-data/query */
        "Query master data": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "query": MasterDataQuery;
                "ctx"?: SerializableObject;
            };
        }, Res["Query master data"]>("PUT", "/api/admin/organizations/{organizationId}/master-data/query"),
        /** PUT /api/admin/organizations/{organizationId}/sources/{entitySourceId}/fields/{name} */
        "Update entity field": handler<{
            params: {
                "organizationId": string;
                "entitySourceId": `src_${string}`;
                "name": string;
            };
            jsonBody: {
                "config": {
                    "description"?: string;
                    "llmSearch"?: boolean;
                    "comparisonInstructions"?: string;
                };
            };
        }, Res["Update entity field"]>("PUT", "/api/admin/organizations/{organizationId}/sources/{entitySourceId}/fields/{name}"),
        /** PUT /api/inboxes/attachments/base64 */
        "Select attachments base64 content": handler<{
            jsonBody: {
                "inboxId": `inb_${string}`;
                "attachmentId": `att_${string}`;
            }[];
        }, Res["Select attachments base64 content"]>("PUT", "/api/inboxes/attachments/base64"),
        /** PUT /api/organizations/{organizationId}/attachments */
        "Search attachments": handler<{
            params: {
                "organizationId": string;
            };
            jsonBody: {
                "contentTypes"?: string[];
                "offset"?: number;
                "limit"?: number;
            };
        }, Res["Search attachments"]>("PUT", "/api/organizations/{organizationId}/attachments"),
        /** PUT /api/results/brose */
        "Special - Get Brose extractions": handler<{
            jsonBody: {
                "correlationIds"?: string[];
                "runId"?: string;
                "type"?: string;
            };
        }, Res["Special - Get Brose extractions"]>("PUT", "/api/results/brose"),
        /** PUT /api/universal-agent/meta */
        "Get universal agent meta": handler<{
            jsonBody: {
                "references": {
                    "type": string;
                    "value": string;
                }[];
            };
        }, Res["Get universal agent meta"]>("PUT", "/api/universal-agent/meta"),
        /** PUT /api/universal-agent/results */
        "Search universal agent results": handler<{
            jsonBody: {
                "limit"?: number;
                "references": {
                    "type": string;
                    "value": string;
                }[];
            };
        }, Res["Search universal agent results"]>("PUT", "/api/universal-agent/results")
    };
}
export type Res = {
    "Delete abstract entity source": void;
    "Get abstract entity": {
        "organizationId": string;
        "id": `abe_${string}`;
        "sourceId": `abs_${string}`;
        "identityKey": string;
        "data": Serializable;
        "createdAt": string;
        "updatedAt": string;
    };
    "Download assistant case file": {
        "url": string;
    };
    "Search inboxes": {
        "id": `inb_${string}`;
        "provider": "GMAIL" | "EXCHANGE" | "EWS";
        "email": string;
        "authInfo": {
            "type"?: "DataSource";
            "dataSourceId": string;
            "dataSourceEnv": "staging" | "production";
            "organizationId": number;
        } | {
            "type": "ENTRA_APP";
            "tenantId": string;
            "clientId": string;
            "clientSecret": string;
        } | {
            "type": "EWS";
            "host": string;
            "port": number;
            "servername"?: string;
            "username": string;
            "password": string;
            "domain"?: string;
            "workstation"?: string;
        };
        "enabled": boolean;
        "syncFrom": string | null;
        "syncInfo": ({
            "mode": "full" | "partial";
            "nextPageToken": string | null;
            "lastHistoryId": (string | number) | null;
            "labelIds": string[] | null;
        } | {
            "nextLinks": Record<string, string | null> | null;
            "lastFoldersSyncAt": string | null;
            "folderIds": string[] | null;
            "subjectPrefix"?: string;
            "from"?: string[];
            "to"?: string[];
        } | {
            "syncState": Record<string, string> | null;
            "folderIds": string[];
        }) | null;
        "idempotencyKey": string;
        "useImpersonation": boolean;
        "organizationId"?: string;
    }[];
    "Get exchange thread": Serializable;
    "Get external attachment": Serializable;
    "Get external thread": Serializable;
    "Get inbox folders": Serializable;
    "Mailbox view": {
        "inboxes": {
            "id": `inb_${string}`;
            "email": string;
            "idempotencyKey": string;
        }[];
        "threads": {
            "id": `thr_${string}`;
            "inboxId": `inb_${string}`;
            "externalId": string;
            "versionKey": string | number;
            "messages": {
                "id": `msg_${string}`;
                "subject": string | null;
                "content": string;
                "snippet": string | null;
                "senderName": string | null;
                "senderEmail": string | null;
                "receivedAt": string;
                "sent": boolean;
                "attachments": {
                    "id": `att_${string}`;
                    "filename": string;
                    "contentType": string;
                }[];
            }[];
        }[];
    };
    "List abstract entity sources": {
        "organizationId": string;
        "id": `abs_${string}`;
        "entity": string;
        "definition": {
            "source": {
                "type": "S3_EXPORT";
                "bucket": string;
                "key": string;
                "format": {
                    "type": "CSV";
                    "removeDuplicateLines"?: boolean;
                    "delimiter"?: string;
                };
            } | {
                "type": "ODATA";
                "name": string;
                "path"?: string;
                "query"?: Record<string, string>;
                "map": string | string[];
                "syncState"?: string | string[];
            } | {
                "type": "MANUAL";
            };
            "fields": Record<string, {
                "type": "string";
                "behavior": "identity" | "reference" | "required" | "optional";
                "indexed"?: boolean;
            }>;
        };
        "createdAt": string;
        "updatedAt": string;
    }[];
    "Get abstract entity source": {
        "organizationId": string;
        "id": `abs_${string}`;
        "entity": string;
        "definition": {
            "source": {
                "type": "S3_EXPORT";
                "bucket": string;
                "key": string;
                "format": {
                    "type": "CSV";
                    "removeDuplicateLines"?: boolean;
                    "delimiter"?: string;
                };
            } | {
                "type": "ODATA";
                "name": string;
                "path"?: string;
                "query"?: Record<string, string>;
                "map": string | string[];
                "syncState"?: string | string[];
            } | {
                "type": "MANUAL";
            };
            "fields": Record<string, {
                "type": "string";
                "behavior": "identity" | "reference" | "required" | "optional";
                "indexed"?: boolean;
            }>;
        };
        "createdAt": string;
        "updatedAt": string;
    };
    "Fetch Purchase Order from ERP": Serializable;
    "Fetch Purchase Order from ERP (RAW)": Serializable;
    "Get Supplier email": Serializable;
    "Get Raw OC PO cases": {
        "id": number;
        "organizationId": string;
        "number": string;
        "status": "pending" | "review" | "error" | "confirming" | "confirmed" | "approving" | "declined" | "cancelled" | "erp-error";
        "result": ({
            "result": OrderConfirmationProcessingResultSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "decisions"?: Record<string, AssistantDecisionSchema>;
            "masterPriceDecisions"?: Record<string, {
                "type": "accepted";
                "value": BigNumberValue | null;
                "id"?: string;
            } | {
                "type": "rejected";
            }>;
            "update"?: {
                "number": string;
                "head": {
                    "incoterms1": string | null;
                    "incoterms2": string | null;
                    "paymentTerms": string | null;
                };
                "items": {
                    "id": string;
                    "serialNumber"?: number;
                    "supplierSku": string | null;
                    "unitPrice": BigNumberValue | null;
                    "quantity": BigNumberValue | null;
                    "extra": {
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                    }[] | null;
                    "confirmationNumber"?: string | null;
                    "confirmations": {
                        "quantity": BigNumberValue;
                        "deliveryDate": string;
                        "reference": string | null;
                    }[] | null;
                }[];
                "headerExtra": {
                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                    "calculation": {
                        "class": "percentage";
                        "rate": BigNumberValue | null;
                        "appliedTo": "gross" | "net";
                    } | {
                        "class": "fixedAmount";
                        "amount": BigNumberValue | null;
                        "distributed": boolean;
                    } | {
                        "class": "quantityBased";
                        "quantity": BigNumberValue;
                        "quantityUom": string;
                        "unitPrice": BigNumberValue;
                        "priceUom": string;
                        "perAmount": BigNumberValue;
                        "uomConversionCoefficient": BigNumberValue;
                    };
                }[] | null;
                "masterPrice"?: {
                    "positions": {
                        "id": string;
                        "value": BigNumberValue | null;
                        "masterPriceId"?: string;
                    }[];
                };
                "arbitrary"?: Record<string, Serializable>;
            };
            "corrections"?: Record<string, (string | null) | (boolean | null) | (string | null) | {
                "amount": {
                    "amount": BigNumberValue | null;
                    "currency": string | null;
                } | null;
                "quantity": {
                    "amount": BigNumberValue | null;
                    "unit": string | null;
                } | null;
                "extra"?: {
                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                    "calculation": {
                        "class": "percentage";
                        "rate": BigNumberValue | null;
                        "appliedTo": "gross" | "net";
                    } | {
                        "class": "fixedAmount";
                        "amount": BigNumberValue | null;
                        "distributed": boolean;
                    } | {
                        "class": "quantityBased";
                        "quantity": BigNumberValue;
                        "quantityUom": string;
                        "unitPrice": BigNumberValue;
                        "priceUom": string;
                        "perAmount": BigNumberValue;
                        "uomConversionCoefficient": BigNumberValue;
                    };
                    "name"?: string;
                }[];
            } | {
                "amount": BigNumberValue | null;
                "currency": string | null;
            } | {
                "amount": BigNumberValue | null;
                "unit": string | null;
            } | (("open" | "partiallyConfirmed" | "confirmed") | null) | {
                "id": string | null;
                "source": "paymentTerms";
            } | {
                "incoterms1": string | null;
                "incoterms2": string | null;
            } | {
                "schedules": {
                    "quantity": {
                        "amount": BigNumberValue | null;
                        "unit": string | null;
                    };
                    "deliveryDate": string | null;
                    "reference": string | null;
                    "status"?: string;
                }[];
                "combineMode"?: "partial" | "reduction";
                "confirmations": {
                    "quantity": {
                        "amount": BigNumberValue | null;
                        "unit": string | null;
                    };
                    "deliveryDate": string | null;
                    "reference": string | null;
                }[];
            } | {
                "lines": {
                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                    "calculation": {
                        "class": "percentage";
                        "rate": BigNumberValue | null;
                        "appliedTo": "gross" | "net";
                    } | {
                        "class": "fixedAmount";
                        "amount": BigNumberValue | null;
                        "distributed": boolean;
                    } | {
                        "class": "quantityBased";
                        "quantity": BigNumberValue;
                        "quantityUom": string;
                        "unitPrice": BigNumberValue;
                        "priceUom": string;
                        "perAmount": BigNumberValue;
                        "uomConversionCoefficient": BigNumberValue;
                    };
                    "name"?: string;
                }[];
            }>;
            "triggeredApprovalThresholds"?: (({
                "role": string | {
                    "email": ("self") | string;
                };
            } & ({
                "type": "alwaysWithin";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "anyChange";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "absoluteDifference";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "absoluteValue";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "percentage";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            })) & {
                "key": string;
                "before": Serializable;
                "after": Serializable;
            })[];
            "triggeredAsOrderedThresholds"?: {
                "threshold": {
                    "type": "alwaysWithin";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                    "active": boolean;
                } | {
                    "type": "anyChange";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                    "active": boolean;
                } | {
                    "type": "absoluteDifference";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                } | {
                    "type": "absoluteValue";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                } | {
                    "type": "percentage";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                };
                "key": string;
                "before": Serializable;
                "after": Serializable;
                "beyond": boolean;
            }[];
            "triggeredAutoAcceptThresholds"?: {
                "threshold": {
                    "type": "alwaysWithin";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                    "active": boolean;
                } | {
                    "type": "anyChange";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                    "active": boolean;
                } | {
                    "type": "absoluteDifference";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                } | {
                    "type": "absoluteValue";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                } | {
                    "type": "percentage";
                    "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                    "direction": "positive" | "negative" | "both";
                    "value": number;
                    "active": boolean;
                };
                "key": string;
                "before": Serializable;
                "after": Serializable;
                "beyond": boolean;
            }[];
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "failure"?: {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
            "postProcessingState": "review" | "approval" | "submission" | "failure";
        } | {
            "error": {
                "type": "noPurchaseOrderFound";
                "number": string;
            } | {
                "type": "supplierProcessingDisabled";
            } | {
                "type": "workflowFailed";
                "failure": {
                    "type": "NotImplemented";
                    "details": [
                    ];
                } | {
                    "type": "ERPRequestError";
                    "details": [
                        {
                            "first"?: boolean;
                            "status": number;
                            "message": string;
                        }
                    ];
                } | {
                    "type": "ERPReadBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                        }
                    ];
                } | {
                    "type": "ERPModifyBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                            "first": boolean;
                        }
                    ];
                } | {
                    "type": "Unknown";
                    "details": [
                        {
                            "type": string | null;
                            "message": string | null;
                        }
                    ];
                };
            };
        }) | null;
        "assignee": string[] | null;
    }[];
    "Get Raw OC thread cases": {
        "id": number;
        "organizationId": string;
        "threadId": `thr_${string}`;
        "status": "pending" | "finished" | "error" | "erp-error";
        "result": ({
            "meta": {
                "traceId": string;
            };
            "extractionResult": {
                "confirmations": {
                    "header": {
                        "number": string | null;
                        "date": string;
                        "currency": string | null;
                        "billingAddress": string | null;
                        "paymentTerms": string | null;
                        "deliveryTerms": {
                            "incoterms": string | null;
                            "location": string | null;
                            "additions"?: string | null;
                        } | null;
                    };
                    "items": {
                        "serialNumber": number;
                        "positionNumber": string | null;
                        "poNumber": string;
                        "description": string | null;
                        "articleNumbers": string[];
                        "deliveryAddress": string | null;
                        "deliverySchedules": {
                            "quantity": number | null;
                            "deliveryDate": string | null;
                            "shipmentDate": string | null;
                            "deliveryDateMentioned"?: boolean;
                            "shipmentDateMentioned"?: boolean;
                        }[];
                        "costStructure": {
                            "quantity": number | null;
                            "quantityUom": string | null;
                            "unitPrice": number | null;
                            "priceUom": string | null;
                            "priceUnit": number | null;
                            "totalPrice": number | null;
                        };
                        "itemExtra": {
                            "serialNumber": number;
                            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                            "description": string;
                            "amount": number;
                            "calculation": {
                                "class": "percentage";
                                "rate": BigNumberValue | null;
                                "appliedTo": "gross" | "net";
                            } | {
                                "class": "fixedAmount";
                                "amount": BigNumberValue | null;
                                "distributed": boolean;
                            } | {
                                "class": "quantityBased";
                                "quantity": BigNumberValue;
                                "quantityUom": string;
                                "unitPrice": BigNumberValue;
                                "priceUom": string;
                                "perAmount": BigNumberValue;
                                "uomConversionCoefficient": BigNumberValue;
                            };
                        }[];
                    }[];
                    "headerExtra": {
                        "serialNumber": number;
                        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                        "description": string;
                        "amount": number;
                        "calculation": {
                            "class": "percentage";
                            "rate": BigNumberValue | null;
                            "appliedTo": "gross" | "net";
                        } | {
                            "class": "fixedAmount";
                            "amount": BigNumberValue | null;
                            "distributed": boolean;
                        } | {
                            "class": "quantityBased";
                            "quantity": BigNumberValue;
                            "quantityUom": string;
                            "unitPrice": BigNumberValue;
                            "priceUom": string;
                            "perAmount": BigNumberValue;
                            "uomConversionCoefficient": BigNumberValue;
                        };
                    }[];
                }[];
            };
        } | {
            "meta": {
                "traceId": string;
            };
            "error": {
                "message": string;
            } | ({
                "type": "noConfirmationExtracted";
            } | {
                "type": "unsupportedFileType";
            } | {
                "type": "workflowFailed";
                "failure": {
                    "type": "NotImplemented";
                    "details": [
                    ];
                } | {
                    "type": "ERPRequestError";
                    "details": [
                        {
                            "first"?: boolean;
                            "status": number;
                            "message": string;
                        }
                    ];
                } | {
                    "type": "ERPReadBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                        }
                    ];
                } | {
                    "type": "ERPModifyBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                            "first": boolean;
                        }
                    ];
                } | {
                    "type": "Unknown";
                    "details": [
                        {
                            "type": string | null;
                            "message": string | null;
                        }
                    ];
                };
            });
        }) | null;
    }[];
    "Get Organization Settings": {
        "organizationId": string | null;
        "userId": string | null;
        "key": string;
        "value": Serializable;
        "inboxId": `inb_${string}` | null;
    }[];
    "Get approval task": {
        "id": `apt_${string}`;
        "email": string;
        "poProcessingId": number;
        "createdAt": string;
        "lastReminderSentAt": string | null;
        "active": boolean;
    };
    "Approval assistant view": {
        "threadId": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "traceId"?: string | null;
    } & ({
        "status": "pending";
    } | {
        "status": "error";
        "error": {
            "type": "noConfirmationExtracted";
        } | {
            "type": "unsupportedFileType";
        } | {
            "type": "workflowFailed";
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        };
    } | {
        "status": "erp-error";
        "error": {
            "type": "noConfirmationExtracted";
        } | {
            "type": "unsupportedFileType";
        } | {
            "type": "workflowFailed";
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        };
    } | {
        "status": "finished";
        "orders": ({
            "processingId": number;
            "number": string;
        } & ({
            "status": "pending";
        } | {
            "status": "processing-error";
            "error": {
                "type": "noPurchaseOrderFound";
                "number": string;
            } | {
                "type": "supplierProcessingDisabled";
            } | {
                "type": "workflowFailed";
                "failure": {
                    "type": "NotImplemented";
                    "details": [
                    ];
                } | {
                    "type": "ERPRequestError";
                    "details": [
                        {
                            "first"?: boolean;
                            "status": number;
                            "message": string;
                        }
                    ];
                } | {
                    "type": "ERPReadBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                        }
                    ];
                } | {
                    "type": "ERPModifyBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                            "first": boolean;
                        }
                    ];
                } | {
                    "type": "Unknown";
                    "details": [
                        {
                            "type": string | null;
                            "message": string | null;
                        }
                    ];
                };
            };
        } | {
            "status": "review";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "decisions"?: Record<string, AssistantDecisionSchema>;
        } | {
            "status": "cancelled";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "decisions"?: Record<string, AssistantDecisionSchema>;
        } | {
            "status": "post-processing-error";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "decisions": Record<string, AssistantDecisionSchema>;
            "assignedKeys"?: string[];
            "approvalTaskId"?: `apt_${string}`;
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "masterPriceDecisions"?: Record<string, {
                "type": "accepted";
                "value": BigNumberValue | null;
                "id"?: string;
            } | {
                "type": "rejected";
            }>;
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        } | {
            "status": "approving" | "confirming" | "confirmed" | "declined";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "decisions": Record<string, AssistantDecisionSchema>;
            "assignedKeys"?: string[];
            "approvalTaskId"?: `apt_${string}`;
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "masterPriceDecisions"?: Record<string, {
                "type": "accepted";
                "value": BigNumberValue | null;
                "id"?: string;
            } | {
                "type": "rejected";
            }>;
        }))[];
    });
    "Get thread for approval task": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "versionKey": string | number;
        "messages": {
            "id": `msg_${string}`;
            "externalId": string;
            "subject": string | null;
            "content": string;
            "snippet": string | null;
            "senderName": string | null;
            "senderEmail": string | null;
            "to": string[];
            "cc": string[];
            "bcc": string[];
            "receivedAt": string;
            "sent": boolean;
            "attachmentIds": `att_${string}`[];
            "labelIds": string[];
            "versionKey": (string | number) | null;
            "parentFolderId": string | null;
        }[];
        "attachments": Record<`att_${string}`, {
            "filename": string;
            "contentType": string;
            "size": number;
            "hashHex": string;
            "s3Bucket": string;
            "s3Key": string;
            "data"?: string;
        }>;
    };
    "Get approval meta": {
        "langSmithProjectId"?: string;
        "masterData": {
            "paymentTerms": {
                "id": string;
                "text": string;
            }[];
            "incoterms": {
                "value": string;
                "meaning": string;
            }[];
            "deliveryTerms": {
                "id": `dtm_${string}`;
                "organizationId": string;
                "externalId": string | null;
                "description1": string | null;
                "description2": string | null;
                "arbitrary": Record<string, Serializable> | null;
            }[];
            "extraTypes": Record<string, {
                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                "placement": "item" | "header" | "both";
                "calculation": {
                    "class": "percentage";
                    "appliedTo": "gross" | "net";
                } | {
                    "class": "fixedAmount";
                    "distributed": boolean;
                } | {
                    "class": "quantityBased";
                };
                "default"?: boolean;
            }> | null;
        };
        "email": string;
        "organizationId": string;
        "organizationName": string;
        "settings": {
            "approvalRoles"?: Record<string, {
                "resolve": MasterDataQuery | MasterDataQuery[];
                "toEmail"?: string;
                "authorization"?: ({
                    "type": "query";
                    "if"?: Condition;
                    "query": MasterDataQuery | MasterDataQuery[];
                    "condition": Condition;
                } | {
                    "type": "condition";
                    "if"?: Condition;
                    "condition"?: Condition;
                })[];
            }>;
            "approvalThresholds": ({
                "role": string | {
                    "email": ("self") | string;
                };
            } & ({
                "type": "alwaysWithin";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "anyChange";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "absoluteDifference";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "absoluteValue";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "percentage";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            }))[];
            "readOnly": boolean;
            "reclamationFeedback"?: {
                "type": "no";
            } | {
                "type": "supplierEmail";
                "fallbackEmail": string;
                "overrideSupplierEmail"?: string;
                "languages"?: ("DE" | "EN")[];
            } | {
                "type": "comment";
                "language": "DE" | "EN";
            };
            "minCurrencyPrecision": number;
            "minQuantityPrecision": number;
            "fields": {
                "poNumber": {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                };
                "reference": {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison": {
                        "defaultValue": string;
                    };
                };
                "paymentTerms"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                    };
                };
                "deliveryTerms"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                    };
                    "deliveryTerms2": "location" | "locationSensitive" | "meaning" | "description" | "ignore";
                    "useDefault": boolean;
                };
                "deliveryAddress"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                };
                "aswoDeliveryState"?: {};
                "extraItems"?: {
                    "extraction": ({
                        "type": "mergeToUnitPrice";
                        "mergingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "movingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "mergingHeaderTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "headerTypeWhitelist"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                    } | {
                        "type": "straight";
                    }) & {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "review": {
                        "required": boolean;
                    };
                };
            };
        };
    };
    "Assistant view": {
        "threadId": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "traceId"?: string | null;
    } & ({
        "status": "pending";
    } | {
        "status": "error";
        "error": {
            "type": "noConfirmationExtracted";
        } | {
            "type": "unsupportedFileType";
        } | {
            "type": "workflowFailed";
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        };
    } | {
        "status": "erp-error";
        "error": {
            "type": "noConfirmationExtracted";
        } | {
            "type": "unsupportedFileType";
        } | {
            "type": "workflowFailed";
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        };
    } | {
        "status": "finished";
        "orders": ({
            "processingId": number;
            "number": string;
        } & ({
            "status": "pending";
        } | {
            "status": "processing-error";
            "error": {
                "type": "noPurchaseOrderFound";
                "number": string;
            } | {
                "type": "supplierProcessingDisabled";
            } | {
                "type": "workflowFailed";
                "failure": {
                    "type": "NotImplemented";
                    "details": [
                    ];
                } | {
                    "type": "ERPRequestError";
                    "details": [
                        {
                            "first"?: boolean;
                            "status": number;
                            "message": string;
                        }
                    ];
                } | {
                    "type": "ERPReadBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                        }
                    ];
                } | {
                    "type": "ERPModifyBusinessError";
                    "details": [
                        {
                            "errors": {
                                "code": string;
                                "message": string;
                            }[];
                            "first": boolean;
                        }
                    ];
                } | {
                    "type": "Unknown";
                    "details": [
                        {
                            "type": string | null;
                            "message": string | null;
                        }
                    ];
                };
            };
        } | {
            "status": "review";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "decisions"?: Record<string, AssistantDecisionSchema>;
        } | {
            "status": "cancelled";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "decisions"?: Record<string, AssistantDecisionSchema>;
        } | {
            "status": "post-processing-error";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "decisions": Record<string, AssistantDecisionSchema>;
            "assignedKeys"?: string[];
            "approvalTaskId"?: `apt_${string}`;
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "masterPriceDecisions"?: Record<string, {
                "type": "accepted";
                "value": BigNumberValue | null;
                "id"?: string;
            } | {
                "type": "rejected";
            }>;
            "failure": {
                "type": "NotImplemented";
                "details": [
                ];
            } | {
                "type": "ERPRequestError";
                "details": [
                    {
                        "first"?: boolean;
                        "status": number;
                        "message": string;
                    }
                ];
            } | {
                "type": "ERPReadBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                    }
                ];
            } | {
                "type": "ERPModifyBusinessError";
                "details": [
                    {
                        "errors": {
                            "code": string;
                            "message": string;
                        }[];
                        "first": boolean;
                    }
                ];
            } | {
                "type": "Unknown";
                "details": [
                    {
                        "type": string | null;
                        "message": string | null;
                    }
                ];
            };
        } | {
            "status": "approving" | "confirming" | "confirmed" | "declined";
            "result": OrderConfirmationProcessingResultSchema;
            "resolvedApprovers"?: {
                "key": string;
                "entity": Serializable;
                "email": string | null;
                "role": string | {
                    "email": ("self") | string;
                };
            }[];
            "decisions": Record<string, AssistantDecisionSchema>;
            "assignedKeys"?: string[];
            "approvalTaskId"?: `apt_${string}`;
            "comparison": PurchaseOrderComparisonSchema;
            "masterPriceWhitelist"?: {
                "positions": Record<string, {
                    "updatable": boolean;
                    "id"?: string;
                }>;
            };
            "masterPriceDecisions"?: Record<string, {
                "type": "accepted";
                "value": BigNumberValue | null;
                "id"?: string;
            } | {
                "type": "rejected";
            }>;
        }))[];
    });
    "Get assistant meta": {
        "langSmithProjectId"?: string;
        "masterData": {
            "paymentTerms": {
                "id": string;
                "text": string;
            }[];
            "incoterms": {
                "value": string;
                "meaning": string;
            }[];
            "deliveryTerms": {
                "id": `dtm_${string}`;
                "organizationId": string;
                "externalId": string | null;
                "description1": string | null;
                "description2": string | null;
                "arbitrary": Record<string, Serializable> | null;
            }[];
            "extraTypes": Record<string, {
                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                "placement": "item" | "header" | "both";
                "calculation": {
                    "class": "percentage";
                    "appliedTo": "gross" | "net";
                } | {
                    "class": "fixedAmount";
                    "distributed": boolean;
                } | {
                    "class": "quantityBased";
                };
                "default"?: boolean;
            }> | null;
        };
        "email": string;
        "organizationId": string;
        "organizationName": string;
        "settings": {
            "approvalRoles"?: Record<string, {
                "resolve": MasterDataQuery | MasterDataQuery[];
                "toEmail"?: string;
                "authorization"?: ({
                    "type": "query";
                    "if"?: Condition;
                    "query": MasterDataQuery | MasterDataQuery[];
                    "condition": Condition;
                } | {
                    "type": "condition";
                    "if"?: Condition;
                    "condition"?: Condition;
                })[];
            }>;
            "approvalThresholds": ({
                "role": string | {
                    "email": ("self") | string;
                };
            } & ({
                "type": "alwaysWithin";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "anyChange";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate" | "deliveryTerms" | "paymentTerms" | "itemExtra" | "headerExtra";
                "active": boolean;
            } | {
                "type": "absoluteDifference";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice" | "deliveryDate";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "absoluteValue";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            } | {
                "type": "percentage";
                "field": "unitPrice" | "quantity" | "scheduleLinesQuantity" | "orderTotalPrice";
                "direction": "positive" | "negative" | "both";
                "value": number;
                "active": boolean;
            }))[];
            "readOnly": boolean;
            "reclamationFeedback"?: {
                "type": "no";
            } | {
                "type": "supplierEmail";
                "fallbackEmail": string;
                "overrideSupplierEmail"?: string;
                "languages"?: ("DE" | "EN")[];
            } | {
                "type": "comment";
                "language": "DE" | "EN";
            };
            "minCurrencyPrecision": number;
            "minQuantityPrecision": number;
            "fields": {
                "poNumber": {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                };
                "reference": {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison": {
                        "defaultValue": string;
                    };
                };
                "paymentTerms"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                    };
                };
                "deliveryTerms"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "comparison"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                    };
                    "deliveryTerms2": "location" | "locationSensitive" | "meaning" | "description" | "ignore";
                    "useDefault": boolean;
                };
                "deliveryAddress"?: {
                    "extraction"?: {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                };
                "aswoDeliveryState"?: {};
                "extraItems"?: {
                    "extraction": ({
                        "type": "mergeToUnitPrice";
                        "mergingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "movingItemTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "mergingHeaderTypes"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                        "headerTypeWhitelist"?: ("unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge")[];
                    } | {
                        "type": "straight";
                    }) & {
                        "instructions"?: string | {
                            "promptId": string;
                        };
                        "validators"?: ({
                            "type": "condition";
                            "condition": Condition;
                            "error": string;
                        })[];
                    };
                    "review": {
                        "required": boolean;
                    };
                };
            };
        };
    };
    "Get auth email policy": {
        "providerId": string | null;
        "signUpEnabled"?: boolean;
        "isSso": boolean;
    };
    "Get cases acknowledgements": {
        "cases": {
            "id": `acs_${string}`;
            "type": string;
        }[];
    };
    "Get case content": void;
    "Get dashboard data": ({
        "dateId"?: string;
        "weekStart"?: string;
        "monthStart"?: string;
    } & {
        [x: string]: bigint | number;
    })[];
    "Get dashboard meta": {
        "inboxes": {
            "inboxId": `inb_${string}`;
            "email": string;
        }[];
        "partnerNames": string[];
    };
    "Get SO transaction view": {
        "threadId": `thr_${string}`;
        "workflowId"?: `wkf_${string}`;
        "transactionId"?: `tra_${string}`;
    };
    "Get sales order diff data": {
        "decisions": {
            "id": string;
            "caseId": string;
            "organizationId": string;
            "createdAt": string;
            "dataPoint": "PRODUCT" | "QUANTITY" | "CUSTOMER";
            "suggestionType": "NONE" | "SINGLE" | "MULTIPLE";
            "status": "PENDING" | "ACCEPTED" | "CHANGED" | "REMOVED" | "ADDED" | "INDETERMINATE";
        }[];
        "suggestedTransaction": {
            "id": `tra_${string}`;
            "relevantMessageIds"?: string[];
            "relevantAttachmentIds"?: string[];
            "customerAccountIds"?: string[];
            "billToPartyIds"?: string[];
            "shipToPartyIds"?: string[];
            "newBilledPartyAddress"?: {
                "name1": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox"?: string | null;
                "city": string;
                "county"?: string | null;
                "countryCode": string;
            } | null;
            "newPayingPartyAddress"?: {
                "name1": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox"?: string | null;
                "city": string;
                "county"?: string | null;
                "countryCode": string;
            } | null;
            "newShipToAddress"?: {
                "name1": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox"?: string | null;
                "city": string;
                "county"?: string | null;
                "countryCode": string;
            } | null;
            "sellingEntityId"?: string | null;
            "sellingEntityAddressId"?: string | null;
            "receivedDate": string;
            "emailReceivedDate"?: string | null;
            "orderDate": string;
            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
            "contactId"?: string | null;
            "contactName": string;
            "contactEmail": string;
            "contactPhone"?: string | null;
            "originalOrderReference"?: string | null;
            "orderReference"?: string | null;
            "quoteNumber"?: string | null;
            "transactionType": string;
            "eligibleOrderSplitStrategy"?: string | null;
            "appliedOrderSplitStrategy"?: string | null;
            "currencyId": string;
            "currencyCode": string;
            "customFields"?: Record<string, Serializable> | null;
            "positions": {
                "id": `pos_${string}`;
                "invalidated": boolean;
                "positionIndex": number;
                "extractedPositionIndex"?: string | null;
                "deliveryDate"?: string | null;
                "extractedDeliveryDate"?: string | null;
                "extractedArticleNumbers"?: string[] | null;
                "extractedDescription": string;
                "extractedQuantity1": number;
                "extractedQuantityUnit1": string;
                "extractedQuantity2"?: number | null;
                "extractedQuantityUnit2"?: string | null;
                "extractedQuantity3"?: number | null;
                "extractedQuantityUnit3"?: string | null;
                "extractedPrice1"?: number | null;
                "extractedPriceUnit1"?: string | null;
                "extractedPriceQuantity1"?: number | null;
                "extractedPrice2"?: number | null;
                "extractedPriceUnit2"?: string | null;
                "extractedPriceQuantity2"?: number | null;
                "extractedPrice3"?: number | null;
                "extractedPriceUnit3"?: string | null;
                "extractedPriceQuantity3"?: number | null;
                "extractedTotalValue"?: number | null;
                "customFields"?: Record<string, Serializable> | null;
                "matches": {
                    "id": `pma_${string}`;
                    "invalidated": boolean;
                    "productId": string;
                    "product": {
                        "id": string;
                        "parentId"?: string | null;
                        "code": string;
                        "replacesProductId"?: string | null;
                        "replacesProduct"?: {
                            "id": string;
                            "code": string;
                            "title": string;
                        } | null;
                        "replacedByProductId"?: string | null;
                        "replacedByProduct"?: {
                            "id": string;
                            "code": string;
                            "title": string;
                        } | null;
                        "ean"?: string | null;
                        "weight"?: number | null;
                        "weightUnit"?: string | null;
                        "groupId1"?: string | null;
                        "groupId2"?: string | null;
                        "groupId3"?: string | null;
                        "groupId4"?: string | null;
                        "groupId5"?: string | null;
                        "groupName1"?: string | null;
                        "groupName2"?: string | null;
                        "groupName3"?: string | null;
                        "groupName4"?: string | null;
                        "groupName5"?: string | null;
                        "referenceProductId1"?: string | null;
                        "referenceProductId2"?: string | null;
                        "referenceProductId3"?: string | null;
                        "titleDe1": string;
                        "textDe1"?: string | null;
                        "textDe2"?: string | null;
                        "textDe3"?: string | null;
                        "textDe4"?: string | null;
                        "textDe5"?: string | null;
                        "titleEn1": string;
                        "textEn1"?: string | null;
                        "textEn2"?: string | null;
                        "textEn3"?: string | null;
                        "textEn4"?: string | null;
                        "textEn5"?: string | null;
                        "titleFr1": string;
                        "textFr1"?: string | null;
                        "textFr2"?: string | null;
                        "textFr3"?: string | null;
                        "textFr4"?: string | null;
                        "textFr5"?: string | null;
                        "defaultSalesUnitCode"?: string | null;
                        "defaultPriceUnitCode": string;
                        "articleNumber1"?: string | null;
                        "articleNumber2"?: string | null;
                        "articleNumber3"?: string | null;
                        "articleNumber4"?: string | null;
                        "colorId"?: string | null;
                        "color"?: string | null;
                        "length"?: number | null;
                        "lengthUnitId"?: string | null;
                        "lengthUnit"?: string | null;
                        "width"?: number | null;
                        "widthUnitId"?: string | null;
                        "widthUnit"?: string | null;
                        "height"?: number | null;
                        "heightUnitId"?: string | null;
                        "heightUnit"?: string | null;
                        "diameter"?: number | null;
                        "diameterUnitId"?: string | null;
                        "diameterUnit"?: string | null;
                        "sizeId"?: string | null;
                        "size"?: string | null;
                        "materialName1"?: string | null;
                        "materialName2"?: string | null;
                        "materialName3"?: string | null;
                        "unitsOfMeasure": {
                            "externalId"?: string | null;
                            "code": string;
                            "readableSingularDe": string;
                            "readablePluralDe": string;
                            "readableSingularEn": string;
                            "readablePluralEn": string;
                            "integerOnly": boolean;
                            "conversionFactors": Record<string, number>;
                        }[];
                        "customerArticleNumbers"?: Record<string, {
                            "articleNumber": string;
                            "customFields"?: SerializableObject | null;
                        }[]> | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "blocked"?: boolean | null;
                    } | null;
                    "quantity": number;
                    "validQuantity": number;
                    "quantityUnitId": string;
                    "quantityUnitCode": string;
                    "price"?: number | null;
                    "priceUnitId"?: string | null;
                    "priceUnitCode"?: string | null;
                    "priceQuantity"?: number | null;
                    "matchingWarning"?: boolean;
                    "matchingExplanation"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
            }[];
            "invalidated": boolean;
            "autoSubmitResult"?: ("SUCCESS" | "FAILURE") | null;
            "autoSubmitError"?: string | null;
            "autoSubmittedAt"?: string | null;
            "contact": {
                "orgId": string;
                "id": string;
                "contactNumber"?: string | null;
                "name": string;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "customFields"?: Record<string, Serializable> | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "deletedAt"?: string | null;
                "blocked"?: boolean | null;
                "blockedAt"?: string | null;
                "syncMarker"?: string | null;
            } | null;
            "sellingEntity": {
                "id": string;
                "code": string;
                "name": string;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "sellingEntityAddress": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "country"?: {
                    "id": string;
                    "iso2Code": string;
                    "iso3Code": string;
                    "nameEn": string;
                    "nameDe": string;
                    "nameFr": string;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                } | null;
            } | null;
            "customerAccounts": {
                "id": string;
                "partnerId": string;
                "unionMemberId"?: string | null;
                "defaultDeliveryTerms"?: string | null;
                "defaultShippingTerms"?: string | null;
                "defaultPaymentTerms"?: string | null;
                "defaultCustomTerms1"?: string | null;
                "defaultCustomTerms2"?: string | null;
                "defaultCustomTerms3"?: string | null;
                "defaultBillingPartnerId": string;
                "defaultShippingPartnerId": string;
                "defaultPayingPartnerId": string;
                "creditLimit"?: number | null;
                "popupText"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "partner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultBillingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultShippingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultPayingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
            }[];
        };
        "submittedTransaction": {
            "id": `stx_${string}`;
            "orgId": string;
            "suggestedTransactionId": string;
            "erpTransactionId": string;
            "erpTransactionNumber": string;
            "threadId": `thr_${string}`;
            "customerAccountId": string;
            "shippingTerms"?: string | null;
            "paymentTerms"?: string | null;
            "billToPartyId": string;
            "billToAddressId": string;
            "shipToPartyId": string;
            "shipToAddressId": string;
            "sellingEntityId"?: string | null;
            "sellingEntityAddressId"?: string | null;
            "newBilledPartyAddress"?: {
                "name1": string;
                "name2": string | null;
                "name3": string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2": string | null;
                "houseNumber2": string | null;
                "streetWithNumber2": string | null;
                "street3": string | null;
                "houseNumber3": string | null;
                "streetWithNumber3": string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox": string | null;
                "city": string;
                "county": string | null;
                "countryCode": string;
            } | null;
            "newPayingPartyAddress"?: {
                "name1": string;
                "name2": string | null;
                "name3": string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2": string | null;
                "houseNumber2": string | null;
                "streetWithNumber2": string | null;
                "street3": string | null;
                "houseNumber3": string | null;
                "streetWithNumber3": string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox": string | null;
                "city": string;
                "county": string | null;
                "countryCode": string;
            } | null;
            "newShipToAddress"?: {
                "name1": string;
                "name2": string | null;
                "name3": string | null;
                "street1": string;
                "houseNumber1": string;
                "streetWithNumber1": string;
                "street2": string | null;
                "houseNumber2": string | null;
                "streetWithNumber2": string | null;
                "street3": string | null;
                "houseNumber3": string | null;
                "streetWithNumber3": string | null;
                "deliveryInstructions"?: string[] | null;
                "postalCode": string;
                "postBox": string | null;
                "city": string;
                "county": string | null;
                "countryCode": string;
            } | null;
            "receivedDate": string;
            "emailReceivedDate"?: string | null;
            "entryDate"?: string | null;
            "orderDate": string;
            "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
            "contactId"?: string | null;
            "contactName": string;
            "contactEmail": string;
            "contactPhone": string;
            "orderReference": string;
            "quoteNumber": string | null;
            "comment": string | null;
            "transactionType": string;
            "currencyId": string;
            "currencyCode": string;
            "deletedPositionIds": string[];
            "erpResponse"?: string | null;
            "customFields"?: Record<string, Serializable> | null;
            "createdAt": string;
            "updatedAt": string;
            "createdBy": string;
            "positions": {
                "id": `spo_${string}`;
                "orgId": string;
                "threadId": `thr_${string}`;
                "transactionId": string;
                "suggestedPositionId": string;
                "selectedMatchId": string;
                "added": boolean;
                "positionIndex": number;
                "productId": string;
                "quantity": number;
                "quantityUnitId": string;
                "quantityUnitCode": string;
                "price": number;
                "priceUnitId": string;
                "priceUnitCode": string;
                "priceQuantity": number;
                "deliveryDate"?: string | null;
                "externalPositionNumber"?: string | null;
                "customFields"?: Record<string, Serializable> | null;
                "createdAt": string;
                "updatedAt": string;
                "product": {
                    "id": string;
                    "parentId"?: string | null;
                    "code": string;
                    "replacesProductId"?: string | null;
                    "replacesProduct"?: {
                        "id": string;
                        "code": string;
                        "title": string;
                    } | null;
                    "replacedByProductId"?: string | null;
                    "replacedByProduct"?: {
                        "id": string;
                        "code": string;
                        "title": string;
                    } | null;
                    "ean"?: string | null;
                    "weight"?: number | null;
                    "weightUnit"?: string | null;
                    "groupId1"?: string | null;
                    "groupId2"?: string | null;
                    "groupId3"?: string | null;
                    "groupId4"?: string | null;
                    "groupId5"?: string | null;
                    "groupName1"?: string | null;
                    "groupName2"?: string | null;
                    "groupName3"?: string | null;
                    "groupName4"?: string | null;
                    "groupName5"?: string | null;
                    "referenceProductId1"?: string | null;
                    "referenceProductId2"?: string | null;
                    "referenceProductId3"?: string | null;
                    "titleDe1": string;
                    "textDe1"?: string | null;
                    "textDe2"?: string | null;
                    "textDe3"?: string | null;
                    "textDe4"?: string | null;
                    "textDe5"?: string | null;
                    "titleEn1": string;
                    "textEn1"?: string | null;
                    "textEn2"?: string | null;
                    "textEn3"?: string | null;
                    "textEn4"?: string | null;
                    "textEn5"?: string | null;
                    "titleFr1": string;
                    "textFr1"?: string | null;
                    "textFr2"?: string | null;
                    "textFr3"?: string | null;
                    "textFr4"?: string | null;
                    "textFr5"?: string | null;
                    "defaultSalesUnitCode"?: string | null;
                    "defaultPriceUnitCode": string;
                    "articleNumber1"?: string | null;
                    "articleNumber2"?: string | null;
                    "articleNumber3"?: string | null;
                    "articleNumber4"?: string | null;
                    "colorId"?: string | null;
                    "color"?: string | null;
                    "length"?: number | null;
                    "lengthUnitId"?: string | null;
                    "lengthUnit"?: string | null;
                    "width"?: number | null;
                    "widthUnitId"?: string | null;
                    "widthUnit"?: string | null;
                    "height"?: number | null;
                    "heightUnitId"?: string | null;
                    "heightUnit"?: string | null;
                    "diameter"?: number | null;
                    "diameterUnitId"?: string | null;
                    "diameterUnit"?: string | null;
                    "sizeId"?: string | null;
                    "size"?: string | null;
                    "materialName1"?: string | null;
                    "materialName2"?: string | null;
                    "materialName3"?: string | null;
                    "unitsOfMeasure": {
                        "externalId"?: string | null;
                        "code": string;
                        "readableSingularDe": string;
                        "readablePluralDe": string;
                        "readableSingularEn": string;
                        "readablePluralEn": string;
                        "integerOnly": boolean;
                        "conversionFactors": Record<string, number>;
                    }[];
                    "customerArticleNumbers"?: Record<string, {
                        "articleNumber": string;
                        "customFields"?: SerializableObject | null;
                    }[]> | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "blocked"?: boolean | null;
                } | null;
            }[];
            "contact": {
                "orgId": string;
                "id": string;
                "contactNumber"?: string | null;
                "name": string;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "customFields"?: Record<string, Serializable> | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "deletedAt"?: string | null;
                "blocked"?: boolean | null;
                "blockedAt"?: string | null;
                "syncMarker"?: string | null;
            } | null;
            "sellingEntity": {
                "id": string;
                "code": string;
                "name": string;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "sellingEntityAddress": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "country"?: {
                    "id": string;
                    "iso2Code": string;
                    "iso3Code": string;
                    "nameEn": string;
                    "nameDe": string;
                    "nameFr": string;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                } | null;
            } | null;
            "customerAccount": {
                "id": string;
                "partnerId": string;
                "unionMemberId"?: string | null;
                "defaultDeliveryTerms"?: string | null;
                "defaultShippingTerms"?: string | null;
                "defaultPaymentTerms"?: string | null;
                "defaultCustomTerms1"?: string | null;
                "defaultCustomTerms2"?: string | null;
                "defaultCustomTerms3"?: string | null;
                "defaultBillingPartnerId": string;
                "defaultShippingPartnerId": string;
                "defaultPayingPartnerId": string;
                "creditLimit"?: number | null;
                "popupText"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "partner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultBillingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultShippingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
                "defaultPayingPartner"?: {
                    "id": string;
                    "isIntercompany"?: boolean | null;
                    "erpCompanyId"?: number | null;
                    "name1"?: string | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "partnerNumber"?: string | null;
                    "partnerSlug"?: string | null;
                    "taxNumber"?: string | null;
                    "domains"?: string[] | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "billingAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "deliveryAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                    "mainAddresses": {
                        "id": string;
                        "sequenceNumber"?: string | null;
                        "name1": string;
                        "postalCode": string;
                        "city": string;
                        "countryId": string;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "name4"?: string | null;
                        "name5"?: string | null;
                        "street1"?: string | null;
                        "houseNumber1"?: string | null;
                        "streetWithNumber1"?: string | null;
                        "street2"?: string | null;
                        "houseNumber2"?: string | null;
                        "streetWithNumber2"?: string | null;
                        "street3"?: string | null;
                        "houseNumber3"?: string | null;
                        "streetWithNumber3"?: string | null;
                        "street4"?: string | null;
                        "houseNumber4"?: string | null;
                        "streetWithNumber4"?: string | null;
                        "street5"?: string | null;
                        "houseNumber5"?: string | null;
                        "streetWithNumber5"?: string | null;
                        "deliveryInstructions"?: string[] | null;
                        "phone1"?: string | null;
                        "phone2"?: string | null;
                        "phone3"?: string | null;
                        "fax1"?: string | null;
                        "fax2"?: string | null;
                        "fax3"?: string | null;
                        "email1"?: string | null;
                        "email2"?: string | null;
                        "email3"?: string | null;
                        "postBox"?: string | null;
                        "county"?: string | null;
                        "createdAt": string;
                        "updatedAt": string;
                        "deleted"?: boolean | null;
                        "blocked"?: boolean | null;
                        "customFields"?: Record<string, Serializable> | null;
                        "country"?: {
                            "id": string;
                            "iso2Code": string;
                            "iso3Code": string;
                            "nameEn": string;
                            "nameDe": string;
                            "nameFr": string;
                            "createdAt": string;
                            "updatedAt": string;
                            "deleted"?: boolean | null;
                            "blocked"?: boolean | null;
                            "customFields"?: Record<string, Serializable> | null;
                        } | null;
                        "isDefault": boolean;
                    }[];
                } | null;
            } | null;
            "billToAddress": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "country"?: {
                    "id": string;
                    "iso2Code": string;
                    "iso3Code": string;
                    "nameEn": string;
                    "nameDe": string;
                    "nameFr": string;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                } | null;
            } | null;
            "shipToAddress": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "createdAt": string;
                "updatedAt": string;
                "deleted"?: boolean | null;
                "blocked"?: boolean | null;
                "customFields"?: Record<string, Serializable> | null;
                "country"?: {
                    "id": string;
                    "iso2Code": string;
                    "iso3Code": string;
                    "nameEn": string;
                    "nameDe": string;
                    "nameFr": string;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                } | null;
            } | null;
        } | null;
    };
    "Get dashboard data v2": ({
        "dateId"?: string;
        "weekStart"?: string;
        "monthStart"?: string;
    } & {
        [x: string]: bigint | number;
    })[];
    "Get dashboard OC transactions v2": {
        "processingId": string;
        "emailSubject": string | null;
        "status": "REVIEW" | "APPROVAL" | "ACCEPTED" | "REJECTED" | "CANCELLED";
        "supplierName": string | null;
        "poNumber": string | null;
        "itemCount": number;
        "deviations": ("DELIVERY_TERMS" | "PAYMENT_TERMS" | "DELIVERY_ADDRESS" | "PRICE" | "QUANTITY" | "OC_REFERENCE" | "TOTAL_QUANTITY")[];
        "receivedAt": string;
        "threadId": string;
    }[];
    "Get dashboard transactions v2": {
        "suggestedTransactionId": string;
        "emailSubject": string | null;
        "isSubmitted": boolean;
        "customerName": string | null;
        "suggestedPositionCount": number;
        "submittedPositionCount": number | null;
        "adjustments": ("CUSTOMER_CHANGED" | "QUANTITY_CHANGED" | "PRODUCT_CHANGED" | "POSITION_ADDED" | "POSITION_REMOVED" | "PRODUCT_NOT_DETECTED")[];
        "createdAt": string;
        "orderReference": string | null;
        "erpTransactionNumber": string | null;
        "processedAt": string | null;
        "submittedAt": string | null;
        "submittedBy": string | null;
        "threadId": string;
    }[];
    "Get document content": string | null;
    "Pre resolve approvers": {
        "key": string;
        "entity": Serializable;
        "email": string | null;
        "role": string | {
            "email": ("self") | string;
        };
    }[];
    "Get thread by id": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "versionKey": string | number;
        "messages": {
            "id": `msg_${string}`;
            "externalId": string;
            "subject": string | null;
            "content": string;
            "snippet": string | null;
            "senderName": string | null;
            "senderEmail": string | null;
            "to": string[];
            "cc": string[];
            "bcc": string[];
            "receivedAt": string;
            "sent": boolean;
            "attachmentIds": `att_${string}`[];
            "labelIds": string[];
            "versionKey": (string | number) | null;
            "parentFolderId": string | null;
        }[];
        "attachments": Record<`att_${string}`, {
            "filename": string;
            "contentType": string;
            "size": number;
            "hashHex": string;
            "s3Bucket": string;
            "s3Key": string;
            "data"?: string;
        }>;
    };
    "Get entity sources": {
        "id": `src_${string}`;
        "organizationId": string;
        "entity": string;
        "config": {
            "deduplicate"?: boolean | ("last-win") | ("merge");
            "filter"?: Condition;
        } & ({
            "type": "S3_EXPORT";
            "bucket": string;
            "key": string;
            "format": {
                "type": "CSV";
                "removeDuplicateLines"?: boolean;
                "delimiter"?: string;
            };
        } | {
            "type": "LEXINGTON_POC";
        } | {
            "type": "ATP_POC";
        } | {
            "type": "MANUAL";
        });
    }[];
    "Get entity fields": {
        "entitySourceId": `src_${string}`;
        "name": string;
        "config": {
            "optional"?: boolean;
            "description"?: string;
            "llmSearch"?: boolean;
            "comparisonInstructions"?: string;
            "priority"?: number;
        } & ({
            "type": "string";
            "originalName": string;
            "pk": boolean;
            "fk"?: string;
            "regex"?: string;
        } | {
            "type": "integer";
            "originalName": string;
            "pk": boolean;
            "fk"?: string;
        } | {
            "type": "decimal";
            "originalName": string;
        } | {
            "type": "constant";
            "value": string;
            "pk": boolean;
        } | {
            "type": "active";
            "originalName": string;
            "true": string;
            "false": string;
        } | {
            "type": "domain";
            "originalName": string;
        } | {
            "type": "country";
            "originalName": string;
        } | {
            "type": "boolean";
            "originalName": string;
        });
    }[];
    "Get threads by organizationId": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "subject": string | null;
    }[];
    "Search customers by name, partner number, or billing address": {
        "partner": {
            "id": string;
            "name1"?: string | null;
            "name2"?: string | null;
            "name3"?: string | null;
            "partnerNumber"?: string | null;
            "partnerSlug"?: string | null;
            "taxNumber"?: string | null;
            "domains"?: string[] | null;
            "billingAddresses": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "isDefault"?: boolean | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "customFields"?: Record<string, Serializable> | null;
            }[];
            "deliveryAddresses": {
                "id": string;
                "sequenceNumber"?: string | null;
                "name1": string;
                "postalCode": string;
                "city": string;
                "countryId": string;
                "isDefault"?: boolean | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "name4"?: string | null;
                "name5"?: string | null;
                "street1"?: string | null;
                "houseNumber1"?: string | null;
                "streetWithNumber1"?: string | null;
                "street2"?: string | null;
                "houseNumber2"?: string | null;
                "streetWithNumber2"?: string | null;
                "street3"?: string | null;
                "houseNumber3"?: string | null;
                "streetWithNumber3"?: string | null;
                "street4"?: string | null;
                "houseNumber4"?: string | null;
                "streetWithNumber4"?: string | null;
                "street5"?: string | null;
                "houseNumber5"?: string | null;
                "streetWithNumber5"?: string | null;
                "deliveryInstructions"?: string[] | null;
                "phone1"?: string | null;
                "phone2"?: string | null;
                "phone3"?: string | null;
                "fax1"?: string | null;
                "fax2"?: string | null;
                "fax3"?: string | null;
                "email1"?: string | null;
                "email2"?: string | null;
                "email3"?: string | null;
                "postBox"?: string | null;
                "county"?: string | null;
                "customFields"?: Record<string, Serializable> | null;
            }[];
            "customFields"?: Record<string, Serializable> | null;
        };
        "customerAccount": {
            "id": string;
            "partnerId": string;
            "partner"?: {
                "id": string;
                "name1"?: string | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "partnerNumber"?: string | null;
                "partnerSlug"?: string | null;
                "taxNumber"?: string | null;
                "domains"?: string[] | null;
                "billingAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "deliveryAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "unionMemberId"?: string | null;
            "defaultDeliveryTerms"?: string | null;
            "defaultShippingTerms"?: string | null;
            "defaultPaymentTerms"?: string | null;
            "defaultCustomTerms1"?: string | null;
            "defaultCustomTerms2"?: string | null;
            "defaultCustomTerms3"?: string | null;
            "defaultBillingPartnerId"?: string | null;
            "defaultBillingPartner"?: {
                "id": string;
                "name1"?: string | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "partnerNumber"?: string | null;
                "partnerSlug"?: string | null;
                "taxNumber"?: string | null;
                "domains"?: string[] | null;
                "billingAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "deliveryAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "defaultShippingPartnerId"?: string | null;
            "defaultShippingPartner"?: {
                "id": string;
                "name1"?: string | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "partnerNumber"?: string | null;
                "partnerSlug"?: string | null;
                "taxNumber"?: string | null;
                "domains"?: string[] | null;
                "billingAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "deliveryAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "defaultPayingPartnerId"?: string | null;
            "defaultPayingPartner"?: {
                "id": string;
                "name1"?: string | null;
                "name2"?: string | null;
                "name3"?: string | null;
                "partnerNumber"?: string | null;
                "partnerSlug"?: string | null;
                "taxNumber"?: string | null;
                "domains"?: string[] | null;
                "billingAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "deliveryAddresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "customFields"?: Record<string, Serializable> | null;
            } | null;
            "creditLimit"?: number | null;
            "popupText"?: string | null;
            "customFields"?: Record<string, Serializable> | null;
        };
    }[];
    "Get sales order duplicate": {
        "submittedTransactionId": string;
        "erpTransactionNumber": string;
        "createdAt": string;
        "createdBy": string;
    } | null;
    "Get relevant transaction email parts": {
        "attachmentIds": `att_${string}`[];
        "messageIds": `msg_${string}`[];
    };
    "Get sales order results for thread": {
        "type": "RESULT";
        "threadId": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "customerConfig": {
            "orgId": string;
            "config": {
                "autoSubmitEnabled"?: boolean;
                "productTextAttributes"?: ("title1" | "text1" | "text2" | "text3" | "text4" | "text5")[];
                "extractedPositionFields"?: {
                    "technicalName": string;
                    "readableNameEnglish": string;
                    "readableNameGerman": string;
                    "type": "string" | "number" | "money" | "date";
                    "array"?: boolean;
                    "nullable"?: boolean;
                    "visible"?: boolean;
                    "visibleWhenEmpty"?: boolean;
                    "visibleConditionally"?: {
                        "dependentKey": string;
                        "condition": {
                            "type": "EQUALS";
                            "value": string;
                        } | {
                            "type": "EXISTS";
                        } | {
                            "type": "NOT_EXISTS";
                        };
                    };
                    "enum"?: {
                        "type": "FIXED";
                        "enum": string[];
                        "labels"?: Record<string, string>;
                    } | {
                        "type": "DISTINCT_PRODUCT_VALUES";
                        "source": {
                            "type": "PRODUCT_ATTRIBUTE";
                            "attributeName": string;
                        } | {
                            "type": "CUSTOM_PRODUCT_FIELD";
                            "jsonPath": string;
                        };
                    };
                    "description": string;
                    "sortOrder": number;
                    "editable": boolean;
                }[];
                "extractedHeaderFields"?: {
                    "technicalName": string;
                    "readableNameEnglish": string;
                    "readableNameGerman": string;
                    "type": "string" | "number" | "money" | "date";
                    "array"?: boolean;
                    "nullable"?: boolean;
                    "visible"?: boolean;
                    "visibleWhenEmpty"?: boolean;
                    "visibleConditionally"?: {
                        "dependentKey": string;
                        "condition": {
                            "type": "EQUALS";
                            "value": string;
                        } | {
                            "type": "EXISTS";
                        } | {
                            "type": "NOT_EXISTS";
                        };
                    };
                    "enum"?: {
                        "type": "FIXED";
                        "enum": string[];
                        "labels"?: Record<string, string>;
                    } | {
                        "type": "DISTINCT_PRODUCT_VALUES";
                        "source": {
                            "type": "PRODUCT_ATTRIBUTE";
                            "attributeName": string;
                        } | {
                            "type": "CUSTOM_PRODUCT_FIELD";
                            "jsonPath": string;
                        };
                    };
                    "description": string;
                    "sortOrder": number;
                    "editable": boolean;
                }[];
                "productSelectionFields"?: {
                    "technicalName": string;
                    "readableNameEnglish": string;
                    "readableNameGerman": string;
                    "type": "string" | "number" | "money" | "date";
                    "array"?: boolean;
                    "nullable"?: boolean;
                    "visible"?: boolean;
                    "visibleWhenEmpty"?: boolean;
                    "visibleConditionally"?: {
                        "dependentKey": string;
                        "condition": {
                            "type": "EQUALS";
                            "value": string;
                        } | {
                            "type": "EXISTS";
                        } | {
                            "type": "NOT_EXISTS";
                        };
                    };
                    "enum"?: {
                        "type": "FIXED";
                        "enum": string[];
                        "labels"?: Record<string, string>;
                    } | {
                        "type": "DISTINCT_PRODUCT_VALUES";
                        "source": {
                            "type": "PRODUCT_ATTRIBUTE";
                            "attributeName": string;
                        } | {
                            "type": "CUSTOM_PRODUCT_FIELD";
                            "jsonPath": string;
                        };
                    };
                    "description": string;
                    "sortOrder": number;
                    "editable": boolean;
                }[];
                "priceFetchTriggers"?: {
                    "customerSelect"?: boolean;
                    "multiMatchSelect"?: boolean;
                    "quantityChange"?: boolean;
                    "productCardEdit"?: boolean;
                    "positionDelete"?: boolean;
                    "positionDeleteRevert"?: boolean;
                    "uomChange"?: boolean;
                };
                "orderHeaderFields"?: {
                    "comment"?: {
                        "visible"?: boolean;
                        "visibleWhenEmpty"?: boolean;
                        "editable"?: boolean;
                        "labelGerman"?: string;
                        "labelEnglish"?: string;
                        "placeholderGerman"?: string;
                        "placeholderEnglish"?: string;
                    };
                    "shippingTerms"?: {
                        "visible"?: boolean;
                        "visibleWhenEmpty"?: boolean;
                        "editable"?: boolean;
                        "labelGerman"?: string;
                        "labelEnglish"?: string;
                        "placeholderGerman"?: string;
                        "placeholderEnglish"?: string;
                    };
                    "paymentTerms"?: {
                        "visible"?: boolean;
                        "visibleWhenEmpty"?: boolean;
                        "editable"?: boolean;
                        "labelGerman"?: string;
                        "labelEnglish"?: string;
                        "placeholderGerman"?: string;
                        "placeholderEnglish"?: string;
                    };
                    "creditLimit"?: {
                        "visible"?: boolean;
                        "visibleWhenEmpty"?: boolean;
                        "editable"?: boolean;
                        "labelGerman"?: string;
                        "labelEnglish"?: string;
                        "placeholderGerman"?: string;
                        "placeholderEnglish"?: string;
                    };
                };
                "customOrderHeaderFields"?: ({
                    "key": string;
                    "editable": boolean;
                    "visibleWhenEmpty": boolean;
                    "labelGerman": string;
                    "labelEnglish": string;
                    "placeholderGerman"?: string;
                    "placeholderEnglish"?: string;
                    "defaultValue"?: ({
                        "type": "CONSTANT";
                        "value": string;
                    } | {
                        "type": "PARTNER_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "TRANSACTION_CUSTOM_FIELD";
                        "key": string;
                    })[];
                    "type": "text";
                } | {
                    "key": string;
                    "editable": boolean;
                    "visibleWhenEmpty": boolean;
                    "labelGerman": string;
                    "labelEnglish": string;
                    "placeholderGerman"?: string;
                    "placeholderEnglish"?: string;
                    "defaultValue"?: ({
                        "type": "CONSTANT";
                        "value": string;
                    } | {
                        "type": "PARTNER_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "TRANSACTION_CUSTOM_FIELD";
                        "key": string;
                    })[];
                    "type": "textarea";
                } | {
                    "key": string;
                    "editable": boolean;
                    "visibleWhenEmpty": boolean;
                    "labelGerman": string;
                    "labelEnglish": string;
                    "placeholderGerman"?: string;
                    "placeholderEnglish"?: string;
                    "defaultValue"?: ({
                        "type": "CONSTANT";
                        "value": string;
                    } | {
                        "type": "PARTNER_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "TRANSACTION_CUSTOM_FIELD";
                        "key": string;
                    })[];
                    "type": "date";
                } | {
                    "key": string;
                    "editable": boolean;
                    "visibleWhenEmpty": boolean;
                    "labelGerman": string;
                    "labelEnglish": string;
                    "placeholderGerman"?: string;
                    "placeholderEnglish"?: string;
                    "defaultValue"?: ({
                        "type": "CONSTANT";
                        "value": string;
                    } | {
                        "type": "PARTNER_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "TRANSACTION_CUSTOM_FIELD";
                        "key": string;
                    })[];
                    "type": "number";
                    "allowDecimals": boolean;
                } | {
                    "key": string;
                    "editable": boolean;
                    "visibleWhenEmpty": boolean;
                    "labelGerman": string;
                    "labelEnglish": string;
                    "placeholderGerman"?: string;
                    "placeholderEnglish"?: string;
                    "defaultValue"?: ({
                        "type": "CONSTANT";
                        "value": string;
                    } | {
                        "type": "PARTNER_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "CUSTOMER_ACCOUNT_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "DELIVERY_ADDRESS_CUSTOM_FIELD";
                        "key": string;
                    } | {
                        "type": "TRANSACTION_CUSTOM_FIELD";
                        "key": string;
                    })[];
                    "type": "select";
                    "innerType": "text" | "number";
                    "source": {
                        "type": "DICTIONARY";
                        "name": string;
                    } | {
                        "type": "SELLING_ENTITY_CODE_DICTIONARY";
                        "namesBySellingEntityCode": Record<string, string>;
                    } | {
                        "type": "FIXED";
                        "options": string[];
                        "labels": Record<string, string>;
                    };
                })[];
                "orderHeaderFieldOrder"?: (("orderReference" | "contactName" | "contactEmail" | "contactPhone" | "orderDate" | "emailDate" | "entryDate" | "totalValue" | "comment" | "shippingTerms" | "paymentTerms" | "creditLimit") | string)[];
                "positionFieldOrder"?: (("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string)[];
                "positionFieldAlwaysEditable"?: string[];
                "customerFieldOrder"?: (("name" | "address" | "partnerNumber" | "taxNumber") | string)[];
                "addressFieldOrder"?: (("address") | string)[];
                "displayQuantityInProductSalesUnit"?: boolean;
                "handleDeliveryInstructions"?: boolean;
                "deliveryAddressDisabled"?: boolean;
                "allowNewBillingAddressMatch"?: boolean;
                "billingAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH";
                "deliveryAddressMatchingStrategy"?: "ALWAYS_MATCH" | "TAKE_SINGLE_OR_MATCH" | "TAKE_DEFAULT_OR_MATCH" | "TAKE_MAIN_AS_NEW_OR_MATCH";
                "deliveryDateDetectionStrategy"?: "DETECT" | "NONE";
                "pricePriorities"?: ("CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE" | "EXTRACTED_PRICE")[];
                "quantityUnitForErpSubmit"?: "SUBMITTED" | "DEFAULT_SALES";
                "productSalesUnitRoundingStrategy"?: "COMMERCIALLY" | "ROUND_UP" | "ROUND_DOWN";
                "paymentTermsWhenAboveCreditLimit"?: string | null;
                "replyTemplate"?: string | null;
                "positionFields"?: {
                    "key": ("articleNumber1" | "articleNumber2" | "articleNumber3" | "articleNumber4" | "color" | "material" | "length" | "width" | "height" | "dimensions" | "size") | string;
                    "source": "PRODUCT" | "PRODUCT_CUSTOM_FIELDS" | "MATCH_CUSTOM_FIELDS" | "NONE";
                    "labelGerman": string;
                    "labelEnglish": string;
                    "visibleWhenEmpty": boolean;
                    "visibleConditionally"?: {
                        "dependentKey": string;
                        "condition": {
                            "type": "EQUALS";
                            "value": string;
                        } | {
                            "type": "EXISTS";
                        } | {
                            "type": "NOT_EXISTS";
                        };
                    };
                    "editSettings"?: {
                        "required"?: boolean;
                        "type": "date" | "text" | "number" | "select";
                        "relatedFieldKey"?: string;
                        "selectSettings"?: {
                            "source": "PRODUCT_CUSTOM_FIELDS";
                            "key": string;
                        } | {
                            "source": "DICTIONARY";
                            "name": string;
                        };
                        "offerApplyToAllPositions"?: boolean;
                    };
                }[];
                "customerFields"?: {
                    "key": ("name" | "address" | "partnerNumber" | "taxNumber") | string;
                    "source": "BILLING_PARTNER" | "BILLING_PARTNER_CUSTOM_FIELDS";
                    "labelGerman": string;
                    "labelEnglish": string;
                    "visibleWhenEmpty": boolean;
                }[];
                "billingAddressLabel"?: {
                    "DE"?: string;
                    "EN"?: string;
                };
                "standardBillingAddressLabel"?: {
                    "DE"?: string;
                    "EN"?: string;
                };
                "standardDeliveryAddressLabel"?: {
                    "DE"?: string;
                    "EN"?: string;
                };
                "inboxFolders"?: ({
                    "type": "rules";
                    "inboxId": string;
                    "basePath"?: string[];
                    "rules": {
                        "condition"?: Condition;
                        "path": string;
                        "folderMoveDelayMinutes"?: number;
                    }[];
                    "noTransactionFoundFolder"?: {
                        "parentId": string;
                        "folderName": string;
                    };
                } | {
                    "type"?: ("static") | null;
                    "inboxId": string;
                    "pipelineStartedFolder"?: {
                        "parentId": string;
                        "folderName": string;
                    };
                    "readyForReviewFolder"?: {
                        "parentId": string;
                        "folderName": string;
                    };
                    "submittedFolder"?: {
                        "parentId": string;
                        "folderName": string;
                    };
                    "cancelledFolder"?: {
                        "folderId": string;
                    };
                    "noTransactionFoundFolder"?: {
                        "parentId": string;
                        "folderName": string;
                    };
                    "folderMoveDelayMinutes"?: number;
                })[];
                "inboxLabelsGerman"?: {
                    "pipelineStarted"?: string;
                    "pipelineFinished"?: string;
                    "pipelineError"?: string;
                    "submittedSuccessfully"?: string;
                    "submittedWithError"?: string;
                    "ORDER"?: string;
                    "REQUEST_FOR_QUOTATION"?: string;
                };
                "inboxLabelsEnglish"?: {
                    "pipelineStarted"?: string;
                    "pipelineFinished"?: string;
                    "pipelineError"?: string;
                    "submittedSuccessfully"?: string;
                    "submittedWithError"?: string;
                    "ORDER"?: string;
                    "REQUEST_FOR_QUOTATION"?: string;
                };
                "addedBusinessDaysWhenImmediateDeliveryRequested"?: number;
                "emailPushCondition"?: "ALWAYS" | "NEVER" | "ONLY_WHEN_NO_ATTACHMENTS";
                "emailPushFileName"?: string;
                "salesOrderBasic"?: {
                    "productChoiceBase"?: "ALL_PRODUCTS" | "CUSTOMER_PRODUCTS";
                    "stripProductTexts"?: boolean;
                    "allowNoMatch"?: boolean;
                    "allowMultiMatch"?: boolean;
                };
                "salesOrderPOC"?: {
                    "showProductCustomFieldsToLLM"?: boolean;
                    "showUnitPricesToLLM"?: string[];
                    "defaultCustomerAccountIdForPriceLookup"?: string;
                    "validateMultiMatchesWithThoughts"?: boolean;
                    "minimumLengthForLevenshteinDiff"?: number;
                    "allowedLevenshteinDiffForArticleNumber"?: number;
                    "allowedArticleNumberPrefixLength"?: number;
                    "allowedArticleNumberSuffixLength"?: number;
                };
                "productSearchDefaultSuggestions"?: "CUSTOMER_PRODUCTS";
                "contactMatchingEnabled"?: boolean;
                "replyGenerationEnabled"?: boolean;
                "attachedImageByteSizeThreshold"?: number;
                "orderDateStrategy"?: "DOCUMENT_DATE" | "EMAIL_DATE" | "TODAY";
                "sellingEntityHeader"?: {
                    "DE"?: string;
                    "EN"?: string;
                };
                "sellingEntityFields"?: ("name" | "address")[];
                "sellingEntityEditable"?: "NO" | "INLINE";
                "mockSubmission"?: boolean;
                "requiredFieldsForSubmission"?: ("deliveryDate")[];
                "productIdWhenNoMatch"?: string;
                "productIdWhenNoMatchBadgeText"?: string;
                "productInvalidLabel"?: {
                    "DE"?: string;
                    "EN"?: string;
                } | null;
                "standardHeaderFieldLabels"?: {
                    "orderReference"?: {
                        "de"?: string;
                        "en"?: string;
                    };
                    "quoteNumber"?: {
                        "de"?: string;
                        "en"?: string;
                    };
                    "totalValue"?: {
                        "de"?: string;
                        "en"?: string;
                    };
                };
                "standardPositionFieldLabels"?: {
                    "price"?: {
                        "de"?: string;
                        "en"?: string;
                    };
                    "totalValue"?: {
                        "de"?: string;
                        "en"?: string;
                    };
                };
                "orderSplitStrategy"?: {
                    "strategy": "DELIVERY_DATE";
                    "applyByDefault": boolean;
                    "offerReprocess": boolean;
                };
                "ignoreGlobalPriceLists"?: boolean;
                "nonExactArticleNumberMatch"?: boolean;
                "partialCustomerArticleNumberMatch"?: boolean;
                "matchCustomerArticleNumbersGlobally"?: boolean;
                "exposedDictionariesToUI"?: string[];
                "judgeTransactionDetection"?: boolean;
                "allowOnlyConvertableToUoMsOnPositionEditing"?: boolean;
                "searchProductsByCustomerAccountNumbers"?: boolean;
                "searchProductsByTextSearch"?: boolean;
                "restrictSearchToCustomerProducts"?: boolean;
                "additionalCostsEnabled"?: boolean;
                "priceFetchEnabled"?: boolean;
                "additionalOrderNumberSeparator"?: string;
                "additionalOrderNumberOnlyForPartnerNumbers"?: string[];
                "warnings"?: ("QUOTE_NUMBER")[];
                "eliminateQuantityZeroPositions"?: boolean;
                "allowedClassifications"?: ("ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER")[];
                "classifications"?: {
                    "name": string;
                    "description"?: string;
                    "pipeline"?: string;
                }[];
            };
            "name": string;
            "slug": string;
            "domain": string;
            "erpSystem": "ODOO" | "DRESSELHAUS_SAP" | "FILE_EXPORT" | "SAP_B1" | "NETSUITE" | "EPICOR" | "ORDERWISE" | "REFLEX_SAP" | "HAUFE_X360" | "SCHELLING" | "WASHTEC_SAP" | "SUCO" | "GEHR" | "UTZ" | "ASATEX" | "NONE";
            "syncEnabled": boolean;
            "legalEntityNames": string[];
            "defaultCurrencyCode": string;
            "defaultLocale": string;
        };
        "masterData": {
            "countries": {
                "id": string;
                "iso2Code": string;
                "iso3Code": string;
                "nameDe": string;
                "nameEn": string;
                "nameFr": string;
            }[];
            "currencies": {
                "id": string;
                "isoCode": string;
                "customFields"?: Record<string, Serializable> | null;
            }[];
            "unitsOfMeasure": {
                "externalId"?: string | null;
                "code": string;
                "readableSingularDe": string;
                "readablePluralDe": string;
                "readableSingularEn": string;
                "readablePluralEn": string;
                "integerOnly": boolean;
                "conversionFactors": Record<string, number>;
            }[];
            "shippingTerms": {
                "id": string;
                "code": string;
                "name": string;
                "customFields"?: Record<string, Serializable> | null;
            }[];
            "paymentTerms": {
                "id": string;
                "code": string;
                "name": string;
                "customFields"?: Record<string, Serializable> | null;
            }[];
            "sellingEntities": {
                "sellingEntity": {
                    "id": string;
                    "code": string;
                    "name": string;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "blocked"?: boolean | null;
                    "customFields"?: Record<string, Serializable> | null;
                };
                "addresses": {
                    "id": string;
                    "sequenceNumber"?: string | null;
                    "name1": string;
                    "postalCode": string;
                    "city": string;
                    "countryId": string;
                    "isDefault"?: boolean | null;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "name4"?: string | null;
                    "name5"?: string | null;
                    "street1"?: string | null;
                    "houseNumber1"?: string | null;
                    "streetWithNumber1"?: string | null;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "street4"?: string | null;
                    "houseNumber4"?: string | null;
                    "streetWithNumber4"?: string | null;
                    "street5"?: string | null;
                    "houseNumber5"?: string | null;
                    "streetWithNumber5"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "fax1"?: string | null;
                    "fax2"?: string | null;
                    "fax3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "postBox"?: string | null;
                    "county"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "defaultAddressId": string | null;
            }[];
            "dictionaries": {
                "orgId": string;
                "name": string;
                "entries": Record<string, {
                    "id": string;
                    "value1": string | null;
                    "value2": string | null;
                    "value3": string | null;
                    "value4": string | null;
                    "value5": string | null;
                    "valueJson1": Serializable;
                    "valueJson2": Serializable;
                    "valueJson3": Serializable;
                    "valueJson4": Serializable;
                    "valueJson5": Serializable;
                }>;
            }[];
        };
        "workflowResults": {
            "workflowId": `wkf_${string}`;
            "status": "STARTED" | "IN_PROGRESS" | "PROCESSED" | "FAILED" | "REPLACED";
            "startedAt": string;
            "completedAt"?: string | null;
            "inboxId": `inb_${string}`;
            "transactions": {
                "id": `tra_${string}`;
                "relevantMessageIds": string[];
                "relevantAttachmentIds": string[];
                "customerAccounts": {
                    "id": string;
                    "partnerId": string;
                    "partner"?: {
                        "id": string;
                        "name1"?: string | null;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "partnerNumber"?: string | null;
                        "partnerSlug"?: string | null;
                        "taxNumber"?: string | null;
                        "domains"?: string[] | null;
                        "billingAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "deliveryAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "customFields"?: Record<string, Serializable> | null;
                    } | null;
                    "unionMemberId"?: string | null;
                    "defaultDeliveryTerms"?: string | null;
                    "defaultShippingTerms"?: string | null;
                    "defaultPaymentTerms"?: string | null;
                    "defaultCustomTerms1"?: string | null;
                    "defaultCustomTerms2"?: string | null;
                    "defaultCustomTerms3"?: string | null;
                    "defaultBillingPartnerId"?: string | null;
                    "defaultBillingPartner"?: {
                        "id": string;
                        "name1"?: string | null;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "partnerNumber"?: string | null;
                        "partnerSlug"?: string | null;
                        "taxNumber"?: string | null;
                        "domains"?: string[] | null;
                        "billingAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "deliveryAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "customFields"?: Record<string, Serializable> | null;
                    } | null;
                    "defaultShippingPartnerId"?: string | null;
                    "defaultShippingPartner"?: {
                        "id": string;
                        "name1"?: string | null;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "partnerNumber"?: string | null;
                        "partnerSlug"?: string | null;
                        "taxNumber"?: string | null;
                        "domains"?: string[] | null;
                        "billingAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "deliveryAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "customFields"?: Record<string, Serializable> | null;
                    } | null;
                    "defaultPayingPartnerId"?: string | null;
                    "defaultPayingPartner"?: {
                        "id": string;
                        "name1"?: string | null;
                        "name2"?: string | null;
                        "name3"?: string | null;
                        "partnerNumber"?: string | null;
                        "partnerSlug"?: string | null;
                        "taxNumber"?: string | null;
                        "domains"?: string[] | null;
                        "billingAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "deliveryAddresses": {
                            "id": string;
                            "sequenceNumber"?: string | null;
                            "name1": string;
                            "postalCode": string;
                            "city": string;
                            "countryId": string;
                            "isDefault"?: boolean | null;
                            "name2"?: string | null;
                            "name3"?: string | null;
                            "name4"?: string | null;
                            "name5"?: string | null;
                            "street1"?: string | null;
                            "houseNumber1"?: string | null;
                            "streetWithNumber1"?: string | null;
                            "street2"?: string | null;
                            "houseNumber2"?: string | null;
                            "streetWithNumber2"?: string | null;
                            "street3"?: string | null;
                            "houseNumber3"?: string | null;
                            "streetWithNumber3"?: string | null;
                            "street4"?: string | null;
                            "houseNumber4"?: string | null;
                            "streetWithNumber4"?: string | null;
                            "street5"?: string | null;
                            "houseNumber5"?: string | null;
                            "streetWithNumber5"?: string | null;
                            "deliveryInstructions"?: string[] | null;
                            "phone1"?: string | null;
                            "phone2"?: string | null;
                            "phone3"?: string | null;
                            "fax1"?: string | null;
                            "fax2"?: string | null;
                            "fax3"?: string | null;
                            "email1"?: string | null;
                            "email2"?: string | null;
                            "email3"?: string | null;
                            "postBox"?: string | null;
                            "county"?: string | null;
                            "customFields"?: Record<string, Serializable> | null;
                        }[];
                        "customFields"?: Record<string, Serializable> | null;
                    } | null;
                    "creditLimit"?: number | null;
                    "popupText"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                }[];
                "billingAddressId": string | null;
                "deliveryAddressId": string | null;
                "sellingEntityId"?: string | null;
                "sellingEntityAddressId"?: string | null;
                "newBilledPartyAddress"?: {
                    "name1": string;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "street1": string;
                    "houseNumber1": string;
                    "streetWithNumber1": string;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "postalCode": string;
                    "postBox"?: string | null;
                    "city": string;
                    "county"?: string | null;
                    "countryCode": string;
                } | null;
                "newPayingPartyAddress"?: {
                    "name1": string;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "street1": string;
                    "houseNumber1": string;
                    "streetWithNumber1": string;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "postalCode": string;
                    "postBox"?: string | null;
                    "city": string;
                    "county"?: string | null;
                    "countryCode": string;
                } | null;
                "newShipToAddress"?: {
                    "name1": string;
                    "name2"?: string | null;
                    "name3"?: string | null;
                    "street1": string;
                    "houseNumber1": string;
                    "streetWithNumber1": string;
                    "street2"?: string | null;
                    "houseNumber2"?: string | null;
                    "streetWithNumber2"?: string | null;
                    "street3"?: string | null;
                    "houseNumber3"?: string | null;
                    "streetWithNumber3"?: string | null;
                    "deliveryInstructions"?: string[] | null;
                    "postalCode": string;
                    "postBox"?: string | null;
                    "city": string;
                    "county"?: string | null;
                    "countryCode": string;
                } | null;
                "deliveryInstructions"?: string[] | null;
                "receivedDate": string;
                "emailReceivedDate"?: string | null;
                "entryDate"?: string | null;
                "orderDate": string;
                "deliveryType"?: ("DELIVERY" | "PICKUP") | null;
                "contactId"?: string | null;
                "contact"?: {
                    "orgId": string;
                    "id": string;
                    "contactNumber"?: string | null;
                    "name": string;
                    "phone1"?: string | null;
                    "phone2"?: string | null;
                    "phone3"?: string | null;
                    "email1"?: string | null;
                    "email2"?: string | null;
                    "email3"?: string | null;
                    "customFields"?: Record<string, Serializable> | null;
                    "createdAt": string;
                    "updatedAt": string;
                    "deleted"?: boolean | null;
                    "deletedAt"?: string | null;
                    "blocked"?: boolean | null;
                    "blockedAt"?: string | null;
                    "syncMarker"?: string | null;
                } | null;
                "contactName": string;
                "contactEmail": string;
                "contactPhone"?: string | null;
                "orderReference"?: string | null;
                "quoteNumber"?: string | null;
                "comment"?: string | null;
                "transactionType": string;
                "eligibleOrderSplitStrategy"?: string | null;
                "appliedOrderSplitStrategy"?: string | null;
                "currency": {
                    "id": string;
                    "isoCode": string;
                    "customFields"?: Record<string, Serializable> | null;
                } | null;
                "shippingTerms": string | null;
                "paymentTerms": string | null;
                "positions": {
                    "id": string;
                    "positionIndex": number;
                    "extractedPositionIndex"?: string | null;
                    "deliveryDate"?: string | null;
                    "extractedDeliveryDate"?: string | null;
                    "extractedDescription": string;
                    "extractedQuantity1": number;
                    "extractedQuantityUnit1": string;
                    "extractedQuantity2"?: number | null;
                    "extractedQuantityUnit2"?: string | null;
                    "extractedQuantity3"?: number | null;
                    "extractedQuantityUnit3"?: string | null;
                    "extractedPrice1"?: number | null;
                    "extractedPriceUnit1"?: string | null;
                    "extractedPriceQuantity1"?: number | null;
                    "extractedPrice2"?: number | null;
                    "extractedPriceUnit2"?: string | null;
                    "extractedPriceQuantity2"?: number | null;
                    "extractedPrice3"?: number | null;
                    "extractedPriceUnit3"?: string | null;
                    "extractedPriceQuantity3"?: number | null;
                    "extractedTotalValue"?: number | null;
                    "matches": {
                        "id": string;
                        "product": {
                            "id": string;
                            "parentId"?: string | null;
                            "code": string;
                            "replacesProductId"?: string | null;
                            "replacesProduct"?: {
                                "id": string;
                                "code": string;
                                "title": string;
                            } | null;
                            "replacedByProductId"?: string | null;
                            "replacedByProduct"?: {
                                "id": string;
                                "code": string;
                                "title": string;
                            } | null;
                            "ean"?: string | null;
                            "weight"?: number | null;
                            "weightUnit"?: string | null;
                            "groupId1"?: string | null;
                            "groupId2"?: string | null;
                            "groupId3"?: string | null;
                            "groupId4"?: string | null;
                            "groupId5"?: string | null;
                            "groupName1"?: string | null;
                            "groupName2"?: string | null;
                            "groupName3"?: string | null;
                            "groupName4"?: string | null;
                            "groupName5"?: string | null;
                            "referenceProductId1"?: string | null;
                            "referenceProductId2"?: string | null;
                            "referenceProductId3"?: string | null;
                            "titleDe1": string;
                            "textDe1"?: string | null;
                            "textDe2"?: string | null;
                            "textDe3"?: string | null;
                            "textDe4"?: string | null;
                            "textDe5"?: string | null;
                            "titleEn1": string;
                            "textEn1"?: string | null;
                            "textEn2"?: string | null;
                            "textEn3"?: string | null;
                            "textEn4"?: string | null;
                            "textEn5"?: string | null;
                            "titleFr1": string;
                            "textFr1"?: string | null;
                            "textFr2"?: string | null;
                            "textFr3"?: string | null;
                            "textFr4"?: string | null;
                            "textFr5"?: string | null;
                            "defaultSalesUnitCode"?: string | null;
                            "defaultPriceUnitCode": string;
                            "articleNumber1"?: string | null;
                            "articleNumber2"?: string | null;
                            "articleNumber3"?: string | null;
                            "articleNumber4"?: string | null;
                            "colorId"?: string | null;
                            "color"?: string | null;
                            "length"?: number | null;
                            "lengthUnitId"?: string | null;
                            "lengthUnit"?: string | null;
                            "width"?: number | null;
                            "widthUnitId"?: string | null;
                            "widthUnit"?: string | null;
                            "height"?: number | null;
                            "heightUnitId"?: string | null;
                            "heightUnit"?: string | null;
                            "diameter"?: number | null;
                            "diameterUnitId"?: string | null;
                            "diameterUnit"?: string | null;
                            "sizeId"?: string | null;
                            "size"?: string | null;
                            "materialName1"?: string | null;
                            "materialName2"?: string | null;
                            "materialName3"?: string | null;
                            "unitsOfMeasure": {
                                "externalId"?: string | null;
                                "code": string;
                                "readableSingularDe": string;
                                "readablePluralDe": string;
                                "readableSingularEn": string;
                                "readablePluralEn": string;
                                "integerOnly": boolean;
                                "conversionFactors": Record<string, number>;
                            }[];
                            "customerArticleNumbers"?: Record<string, {
                                "articleNumber": string;
                                "customFields"?: SerializableObject | null;
                            }[]> | null;
                            "customFields"?: Record<string, Serializable> | null;
                            "blocked"?: boolean | null;
                        } | null;
                        "quantity": number;
                        "validQuantity": number;
                        "quantityUnitId": string;
                        "quantityUnitCode": string;
                        "price"?: number | null;
                        "priceUnitId"?: string | null;
                        "priceUnitCode"?: string | null;
                        "priceQuantity"?: number | null;
                        "matchingWarning": boolean;
                        "matchingExplanation"?: string | null;
                        "customFields"?: Record<string, Serializable> | null;
                    }[];
                    "customFields": Record<string, Serializable>;
                }[];
                "customFields"?: Record<string, Serializable> | null;
                "submissionInfo"?: {
                    "submittedTransactionId": string;
                    "erpTransactionId": string;
                    "erpTransactionNumber": string;
                    "createdAt": string;
                    "createdBy": string;
                    "autoSubmitted": boolean;
                } | null;
                "cancellationInfo"?: {
                    "cancelledBy": string;
                    "cancelledAt": string;
                    "cancelReason": string | null;
                } | null;
                "autoSubmitFailure"?: {
                    "failedAt": string;
                } | null;
            }[];
            "attachments": {
                "processedIds": string[];
                "unsupportedIds": string[];
                "unsupportedNames": string[];
                "errorIds": string[];
                "errorNames": string[];
            };
        }[];
        "pendingFolderMove"?: {
            "triggerAt": number;
        } | null;
    } | {
        "type": "NO_THREAD";
    } | {
        "type": "NOT_STARTED";
    } | {
        "type": "RUNNING";
    } | {
        "type": "ERROR";
    };
    "Get pipeline type for thread": {
        "pipeline": "SO" | "PO" | "NONE" | "THREAD_NOT_FOUND" | "UNIVERSAL";
        "pipelineName": string | null;
    };
    "Uify - Get attachment by id": {
        "id": `att_${string}`;
        "inboxId": `inb_${string}`;
        "filename": string;
        "contentType": string;
        "mimeType": string;
        "data": string;
    };
    "Uify - Get external thread by id": Serializable;
    "Uify - Get thread by id": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "versionKey": string | number;
        "messages": {
            "id": `msg_${string}`;
            "externalId": string;
            "subject": string | null;
            "content": string;
            "snippet": string | null;
            "senderName": string | null;
            "senderEmail": string | null;
            "to": string[];
            "cc": string[];
            "bcc": string[];
            "receivedAt": string;
            "sent": boolean;
            "attachmentIds": `att_${string}`[];
            "labelIds": string[];
            "versionKey": (string | number) | null;
            "parentFolderId": string | null;
        }[];
        "attachments": Record<`att_${string}`, {
            "filename": string;
            "contentType": string;
            "size": number;
            "hashHex": string;
            "s3Bucket": string;
            "s3Key": string;
            "data"?: string;
        }>;
    };
    "Uify - Get document content": string | null;
    "Uify - Get sales order results by thread id": {
        "id": string;
        "type": "ORDER" | "RFQ";
        "threadId": string;
        "externalNumber": string | null;
        "customerId": string | null;
        "contactName": string | null;
        "contactEmail": string | null;
        "contactPhone": string | null;
        "deliveryAddressId": string | null;
        "extractedDeliveryAddress": SerializableObject | null;
        "positions": {
            "id": string;
            "orderId": string;
            "deliveryDate": string | null;
            "productDescription": string;
            "price": number;
            "priceUnit": string | null;
            "priceQuantity": number;
            "priceCurrency": string;
            "productMatches": {
                "id": string;
                "productId": string;
                "quantity": number;
                "quantityUnit": string;
                "price": number;
                "priceUnit": string | null;
                "priceQuantity": number;
                "priceCurrency": string;
            }[];
            "customFields": Record<string, Serializable> | null;
        }[];
    }[];
    "Get Case view": {
        "caseId": string;
        "threadId": `thr_${string}`;
        "processingId"?: number;
        "workflowId"?: `wkf_${string}`;
        "transactionId"?: `tra_${string}`;
    };
    "Get case tags": {
        "id": `ctg_${string}`;
        "slug": string;
        "labelEn": string;
        "labelDe": string;
        "color": string;
        "organizationId": string | null;
    }[];
    "Re-issue access token": void;
    "Save PostgresErp purchaser order": void;
    "Register inbox": `inb_${string}`;
    "Ensure inbox folder": string;
    "Reprocess thread": void;
    "Reset purchase order": {
        "resetting": string[];
        "resetNumbers": string[];
        "error"?: string;
    };
    "Save purchase order reset": void;
    "Create abstract entity source": `abs_${string}`;
    "Upsert abstract entities": void;
    "Upsert thread manually": `thr_${string}`;
    "Insert Raw OC workflow results": {
        "threadProcessingId": number;
        "poProcessingIds": number[];
    };
    "Upsert Organization Settings": void;
    "Upsert Specific Organization Settings": void;
    "Upsert source entities": void;
    "Create entity source": `src_${string}`;
    "Create entity field": void;
    "Validate Universal agent data": {
        "path": string;
        "error": string;
    }[];
    "LLM For Uify": {
        "role": "assistant";
        "content": {
            "text": string;
            "parsed"?: SerializableObject;
        };
    } | {
        "role": "toolRequest";
        "content": {
            "id"?: string;
            "name": string;
            "args": SerializableObject;
        };
    }[];
    "Start workflow": string;
    "Submit approval review": void;
    "Resend approval task": void;
    "Submit approval review with token": void;
    "Submit assistant review": void;
    "Cancel po processing": void;
    "Cancel po processing with token": void;
    "Update organization member": void;
    "Invite organization member": void;
    "Browser Extension": {
        "_": "unknown_url";
    } | {
        "_": "thread_not_found";
    } | {
        "_": "success";
        "threadId": string;
        "processingId"?: number;
        "workflowId"?: `wkf_${string}`;
        "transactionId"?: `tra_${string}`;
    };
    "Acknowledge case": void;
    "Get dashboard transactions": {
        "rows": {
            "suggestedTransactionId": string;
            "emailSubject": string | null;
            "isSubmitted": boolean;
            "customerName": string | null;
            "suggestedPositionCount": number;
            "submittedPositionCount": number | null;
            "adjustments": ("CUSTOMER_CHANGED" | "QUANTITY_CHANGED" | "PRODUCT_CHANGED" | "POSITION_ADDED" | "POSITION_REMOVED" | "PRODUCT_NOT_DETECTED")[];
            "createdAt": string;
            "orderReference": string | null;
            "erpTransactionNumber": string | null;
            "processedAt": string | null;
            "submittedAt": string | null;
            "submittedBy": string | null;
            "isAutoSubmitted": boolean;
            "threadId": string;
        }[];
        "totalCount": number;
    };
    "Get products by ids": {
        "id": string;
        "parentId"?: string | null;
        "code": string;
        "replacesProductId"?: string | null;
        "replacesProduct"?: {
            "id": string;
            "code": string;
            "title": string;
        } | null;
        "replacedByProductId"?: string | null;
        "replacedByProduct"?: {
            "id": string;
            "code": string;
            "title": string;
        } | null;
        "ean"?: string | null;
        "weight"?: number | null;
        "weightUnit"?: string | null;
        "groupId1"?: string | null;
        "groupId2"?: string | null;
        "groupId3"?: string | null;
        "groupId4"?: string | null;
        "groupId5"?: string | null;
        "groupName1"?: string | null;
        "groupName2"?: string | null;
        "groupName3"?: string | null;
        "groupName4"?: string | null;
        "groupName5"?: string | null;
        "referenceProductId1"?: string | null;
        "referenceProductId2"?: string | null;
        "referenceProductId3"?: string | null;
        "titleDe1": string;
        "textDe1"?: string | null;
        "textDe2"?: string | null;
        "textDe3"?: string | null;
        "textDe4"?: string | null;
        "textDe5"?: string | null;
        "titleEn1": string;
        "textEn1"?: string | null;
        "textEn2"?: string | null;
        "textEn3"?: string | null;
        "textEn4"?: string | null;
        "textEn5"?: string | null;
        "titleFr1": string;
        "textFr1"?: string | null;
        "textFr2"?: string | null;
        "textFr3"?: string | null;
        "textFr4"?: string | null;
        "textFr5"?: string | null;
        "defaultSalesUnitCode"?: string | null;
        "defaultPriceUnitCode": string;
        "articleNumber1"?: string | null;
        "articleNumber2"?: string | null;
        "articleNumber3"?: string | null;
        "articleNumber4"?: string | null;
        "colorId"?: string | null;
        "color"?: string | null;
        "length"?: number | null;
        "lengthUnitId"?: string | null;
        "lengthUnit"?: string | null;
        "width"?: number | null;
        "widthUnitId"?: string | null;
        "widthUnit"?: string | null;
        "height"?: number | null;
        "heightUnitId"?: string | null;
        "heightUnit"?: string | null;
        "diameter"?: number | null;
        "diameterUnitId"?: string | null;
        "diameterUnit"?: string | null;
        "sizeId"?: string | null;
        "size"?: string | null;
        "materialName1"?: string | null;
        "materialName2"?: string | null;
        "materialName3"?: string | null;
        "unitsOfMeasure": {
            "externalId"?: string | null;
            "code": string;
            "readableSingularDe": string;
            "readablePluralDe": string;
            "readableSingularEn": string;
            "readablePluralEn": string;
            "integerOnly": boolean;
            "conversionFactors": Record<string, number>;
        }[];
        "customerArticleNumbers"?: Record<string, {
            "articleNumber": string;
            "customFields"?: SerializableObject | null;
        }[]> | null;
        "customFields"?: Record<string, Serializable> | null;
        "blocked"?: boolean | null;
    }[];
    "Check approver authorization": boolean;
    "Update preferences": void;
    "Get Sales Order Additional Costs": {
        "type": "SHIPPING_COST";
        "total": number;
    }[];
    "Cancel sales order transaction": {
        "success": true;
        "pendingFolderMove"?: {
            "triggerAt": number;
        } | null;
    } | {
        "success": false;
        "error": string;
    };
    "Get customer contacts": {
        "contacts": {
            "orgId": string;
            "id": string;
            "contactNumber"?: string | null;
            "name": string;
            "phone1"?: string | null;
            "phone2"?: string | null;
            "phone3"?: string | null;
            "email1"?: string | null;
            "email2"?: string | null;
            "email3"?: string | null;
            "customFields"?: Record<string, Serializable> | null;
            "createdAt": string;
            "updatedAt": string;
            "deleted"?: boolean | null;
            "deletedAt"?: string | null;
            "blocked"?: boolean | null;
            "blockedAt"?: string | null;
            "syncMarker"?: string | null;
        }[];
    };
    "fetch prices": {
        "productId": string;
        "quantity": number | null;
        "quantityUnitId": string | null;
        "prices": {
            "type": "CUSTOMER_PRICE_LIST" | "GLOBAL_PRICE_LIST" | "PRODUCT_PRICE";
            "currencyId": string;
            "currencyCode": string;
            "price": number;
            "priceUnitId": string;
            "priceUnitCode": string;
            "priceQuantity": number;
            "priceListTitleDE"?: string;
        }[];
        "warnings"?: ("BELOW_MOQ")[];
    }[];
    "Generate reply": {
        "html": string;
    };
    "Get product search default suggestions": {
        "suggestions": {
            "id": string;
            "parentId"?: string | null;
            "code": string;
            "replacesProductId"?: string | null;
            "replacesProduct"?: {
                "id": string;
                "code": string;
                "title": string;
            } | null;
            "replacedByProductId"?: string | null;
            "replacedByProduct"?: {
                "id": string;
                "code": string;
                "title": string;
            } | null;
            "ean"?: string | null;
            "weight"?: number | null;
            "weightUnit"?: string | null;
            "groupId1"?: string | null;
            "groupId2"?: string | null;
            "groupId3"?: string | null;
            "groupId4"?: string | null;
            "groupId5"?: string | null;
            "groupName1"?: string | null;
            "groupName2"?: string | null;
            "groupName3"?: string | null;
            "groupName4"?: string | null;
            "groupName5"?: string | null;
            "referenceProductId1"?: string | null;
            "referenceProductId2"?: string | null;
            "referenceProductId3"?: string | null;
            "titleDe1": string;
            "textDe1"?: string | null;
            "textDe2"?: string | null;
            "textDe3"?: string | null;
            "textDe4"?: string | null;
            "textDe5"?: string | null;
            "titleEn1": string;
            "textEn1"?: string | null;
            "textEn2"?: string | null;
            "textEn3"?: string | null;
            "textEn4"?: string | null;
            "textEn5"?: string | null;
            "titleFr1": string;
            "textFr1"?: string | null;
            "textFr2"?: string | null;
            "textFr3"?: string | null;
            "textFr4"?: string | null;
            "textFr5"?: string | null;
            "defaultSalesUnitCode"?: string | null;
            "defaultPriceUnitCode": string;
            "articleNumber1"?: string | null;
            "articleNumber2"?: string | null;
            "articleNumber3"?: string | null;
            "articleNumber4"?: string | null;
            "colorId"?: string | null;
            "color"?: string | null;
            "length"?: number | null;
            "lengthUnitId"?: string | null;
            "lengthUnit"?: string | null;
            "width"?: number | null;
            "widthUnitId"?: string | null;
            "widthUnit"?: string | null;
            "height"?: number | null;
            "heightUnitId"?: string | null;
            "heightUnit"?: string | null;
            "diameter"?: number | null;
            "diameterUnitId"?: string | null;
            "diameterUnit"?: string | null;
            "sizeId"?: string | null;
            "size"?: string | null;
            "materialName1"?: string | null;
            "materialName2"?: string | null;
            "materialName3"?: string | null;
            "unitsOfMeasure": {
                "externalId"?: string | null;
                "code": string;
                "readableSingularDe": string;
                "readablePluralDe": string;
                "readableSingularEn": string;
                "readablePluralEn": string;
                "integerOnly": boolean;
                "conversionFactors": Record<string, number>;
            }[];
            "customerArticleNumbers"?: Record<string, {
                "articleNumber": string;
                "customFields"?: SerializableObject | null;
            }[]> | null;
            "customFields"?: Record<string, Serializable> | null;
            "blocked"?: boolean | null;
        }[];
    };
    "Search products by code, title, description, or article number": {
        "id": string;
        "parentId"?: string | null;
        "code": string;
        "replacesProductId"?: string | null;
        "replacesProduct"?: {
            "id": string;
            "code": string;
            "title": string;
        } | null;
        "replacedByProductId"?: string | null;
        "replacedByProduct"?: {
            "id": string;
            "code": string;
            "title": string;
        } | null;
        "ean"?: string | null;
        "weight"?: number | null;
        "weightUnit"?: string | null;
        "groupId1"?: string | null;
        "groupId2"?: string | null;
        "groupId3"?: string | null;
        "groupId4"?: string | null;
        "groupId5"?: string | null;
        "groupName1"?: string | null;
        "groupName2"?: string | null;
        "groupName3"?: string | null;
        "groupName4"?: string | null;
        "groupName5"?: string | null;
        "referenceProductId1"?: string | null;
        "referenceProductId2"?: string | null;
        "referenceProductId3"?: string | null;
        "titleDe1": string;
        "textDe1"?: string | null;
        "textDe2"?: string | null;
        "textDe3"?: string | null;
        "textDe4"?: string | null;
        "textDe5"?: string | null;
        "titleEn1": string;
        "textEn1"?: string | null;
        "textEn2"?: string | null;
        "textEn3"?: string | null;
        "textEn4"?: string | null;
        "textEn5"?: string | null;
        "titleFr1": string;
        "textFr1"?: string | null;
        "textFr2"?: string | null;
        "textFr3"?: string | null;
        "textFr4"?: string | null;
        "textFr5"?: string | null;
        "defaultSalesUnitCode"?: string | null;
        "defaultPriceUnitCode": string;
        "articleNumber1"?: string | null;
        "articleNumber2"?: string | null;
        "articleNumber3"?: string | null;
        "articleNumber4"?: string | null;
        "colorId"?: string | null;
        "color"?: string | null;
        "length"?: number | null;
        "lengthUnitId"?: string | null;
        "lengthUnit"?: string | null;
        "width"?: number | null;
        "widthUnitId"?: string | null;
        "widthUnit"?: string | null;
        "height"?: number | null;
        "heightUnitId"?: string | null;
        "heightUnit"?: string | null;
        "diameter"?: number | null;
        "diameterUnitId"?: string | null;
        "diameterUnit"?: string | null;
        "sizeId"?: string | null;
        "size"?: string | null;
        "materialName1"?: string | null;
        "materialName2"?: string | null;
        "materialName3"?: string | null;
        "unitsOfMeasure": {
            "externalId"?: string | null;
            "code": string;
            "readableSingularDe": string;
            "readablePluralDe": string;
            "readableSingularEn": string;
            "readablePluralEn": string;
            "integerOnly": boolean;
            "conversionFactors": Record<string, number>;
        }[];
        "customerArticleNumbers"?: Record<string, {
            "articleNumber": string;
            "customFields"?: SerializableObject | null;
        }[]> | null;
        "customFields"?: Record<string, Serializable> | null;
        "blocked"?: boolean | null;
    }[];
    "Reprocess thread by user": void;
    "Submit sales order to ERP": {
        "success": true;
        "result": {
            "id": string;
            "erpTransactionId": string;
            "erpTransactionNumber": string;
        };
        "pendingFolderMove"?: {
            "triggerAt": number;
        } | null;
    } | {
        "success": false;
        "error": string;
        "createdEntities": {
            "type": "ORDER" | "ADDRESS";
            "id": string;
            "readableNumber"?: string | null;
        }[];
    };
    "Trigger email folder move": {
        "triggered": number;
    };
    "Search threads": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "versionKey": string | number;
        "messages": {
            "id": `msg_${string}`;
            "externalId": string;
            "subject": string | null;
            "content": string;
            "snippet": string | null;
            "senderName": string | null;
            "senderEmail": string | null;
            "to": string[];
            "cc": string[];
            "bcc": string[];
            "receivedAt": string;
            "sent": boolean;
            "attachmentIds": `att_${string}`[];
            "labelIds": string[];
            "versionKey": (string | number) | null;
            "parentFolderId": string | null;
        }[];
        "attachments": Record<`att_${string}`, {
            "filename": string;
            "contentType": string;
            "size": number;
            "hashHex": string;
            "s3Bucket": string;
            "s3Key": string;
            "data"?: string;
        }>;
    }[];
    "Get thread by any id": {
        "id": `thr_${string}`;
        "inboxId": `inb_${string}`;
        "externalId": string;
        "versionKey": string | number;
        "messages": {
            "id": `msg_${string}`;
            "externalId": string;
            "subject": string | null;
            "content": string;
            "snippet": string | null;
            "senderName": string | null;
            "senderEmail": string | null;
            "to": string[];
            "cc": string[];
            "bcc": string[];
            "receivedAt": string;
            "sent": boolean;
            "attachmentIds": `att_${string}`[];
            "labelIds": string[];
            "versionKey": (string | number) | null;
            "parentFolderId": string | null;
        }[];
        "attachments": Record<`att_${string}`, {
            "filename": string;
            "contentType": string;
            "size": number;
            "hashHex": string;
            "s3Bucket": string;
            "s3Key": string;
            "data"?: string;
        }>;
    };
    "Uify - Resolve prompt by name": Serializable;
    "Get Assistant Cases": {
        "case": {
            "id": `acs_${string}`;
            "organizationId": string;
            "referenceId": string | null;
            "type": string;
            "data": Serializable;
        };
        "review": {
            "id": `csr_${string}`;
            "status": string;
            "decisions": ({
                "type": "changed";
                "path": (string | number)[];
                "value": Serializable;
            } | {
                "type": "addElement";
                "path": (string | number)[];
                "value": Serializable;
            } | {
                "type": "removeElement";
                "path": (string | number)[];
            })[] | null;
            "updatedData": Serializable | null;
            "reviewedBy": string | null;
            "reviewedAt": string | null;
        } | null;
        "acknowledgement": {
            "id": `csa_${string}`;
            "acknowledged": boolean;
        } | null;
        "file": {
            "id": `csf_${string}`;
            "s3Bucket": string | null;
            "s3Key": string | null;
        } | null;
    }[];
    "Submit Universal Agent Case Review": void;
    "Get Workbench view": {
        "inboxes": {
            "id": `inb_${string}`;
            "email": string;
        }[];
        "allTags": {
            "id": `ctg_${string}`;
            "slug": string;
            "labelEn": string;
            "labelDe": string;
            "color": string;
            "organizationId": string | null;
        }[];
        "totalCases": number;
        "cases": {
            "caseId": string;
            "emailReceivedAt": string;
            "caseStatus": "NONE" | "PROCESSING" | "REVIEW" | "APPROVAL" | "REJECTED" | "ACCEPTED" | "ACCEPTED_EDITED" | "ACCEPTED_AUTO" | "ACCEPTED_TOLERANCE" | "SUBMITTED" | "SUBMITTED_AUTO" | "ERROR" | "CANCELLED" | "ERP_ERROR";
            "emailSubject": string | null;
            "emailFrom": string;
            "inboxId": `inb_${string}`;
            "threadId": `thr_${string}`;
            "caseType": "SO" | "OC";
            "caseClassification": "OC" | "ORDER" | "REQUEST_FOR_PROPOSAL" | "REQUEST_FOR_QUOTATION" | "QUESTION_ABOUT_ONGOING" | "CALL_OFF_ORDER" | "ADJUST_ALREADY_PLACED_ORDER" | "OTHER";
            "caseReference": string | null;
            "partnerName": string | null;
            "organizationId": string;
            "assignee": string[] | null;
            "erpTransactionId": string | null;
            "erpTransactionNumber": string | null;
            "tagIds": `ctg_${string}`[];
            "ownerUserIds": string[];
            "comment": string | null;
            "automation": "NONE" | "FULL" | "FULL_TOLERANCES";
        }[];
    };
    "Upsert case comment": void;
    "Get case metadata": {
        "caseId": string | null;
        "tags": {
            "id": `ctg_${string}`;
            "slug": string;
            "labelEn": string;
            "labelDe": string;
            "color": string;
            "organizationId": string | null;
        }[];
        "tagIds": `ctg_${string}`[];
        "ownerUserIds": string[];
        "comment": string | null;
    };
    "Set case owners": void;
    "Set case tags": void;
    "Search abstract entities": {
        "organizationId": string;
        "id": `abe_${string}`;
        "sourceId": `abs_${string}`;
        "identityKey": string;
        "data": Serializable;
        "createdAt": string;
        "updatedAt": string;
    }[];
    "Enable inbox": void;
    "Disable inbox": void;
    "Update abstract entity source": void;
    "Query master data": Serializable;
    "Update entity field": void;
    "Select attachments base64 content": {
        "id": `att_${string}`;
        "inboxId": `inb_${string}`;
        "filename": string;
        "contentType": string;
        "data": string;
    }[];
    "Search attachments": {
        "id": `att_${string}`;
        "filename": string;
        "inboxId": `inb_${string}`;
        "threadId": `thr_${string}`;
        "externalId": string | null;
        "size": number;
        "contentType": string;
    }[];
    "Special - Get Brose extractions": {
        "id": string;
        "correlationId": string;
        "status": string;
        "result": SerializableObject | null;
    }[];
    "Get universal agent meta": {
        "workflowResultId": `wfr_${string}`;
        "langSmithProjectId"?: string;
        "traceId"?: string;
        "config": {
            "props": Record<string, UniversalAgentNode>;
            "messages": ({
                "promptId": string;
                "args"?: SerializableObject;
            } | ({
                "role": "system";
                "content": string | {
                    "text": string;
                };
            } | {
                "role": "user";
                "content": ((string | {
                    "text": string;
                }) | {
                    "externalId": string;
                    "required"?: boolean;
                } | {
                    "inboxId": `inb_${string}`;
                    "attachmentId": `att_${string}`;
                    "required"?: boolean;
                } | {
                    "bucket": string;
                    "key": string;
                    "required"?: boolean;
                } | {
                    "data": string;
                    "contentType": string;
                    "required"?: boolean;
                } | {
                    "toolId"?: string;
                    "toolName": string;
                    "toolResult": SerializableObject;
                }) | {
                    "parts": ((string | {
                        "text": string;
                    }) | {
                        "externalId": string;
                        "required"?: boolean;
                    } | {
                        "inboxId": `inb_${string}`;
                        "attachmentId": `att_${string}`;
                        "required"?: boolean;
                    } | {
                        "bucket": string;
                        "key": string;
                        "required"?: boolean;
                    } | {
                        "data": string;
                        "contentType": string;
                        "required"?: boolean;
                    } | {
                        "toolId"?: string;
                        "toolName": string;
                        "toolResult": SerializableObject;
                    })[];
                };
            } | {
                "role": "assistant";
                "content": {
                    "parts": ({
                        "text": string;
                        "parsed"?: SerializableObject;
                        "thought"?: boolean;
                        "arbitrary"?: Record<string, Serializable>;
                    } | {
                        "toolName": string;
                        "toolArgs"?: SerializableObject;
                        "toolId"?: string;
                        "arbitrary"?: Record<string, Serializable>;
                    })[];
                };
            }))[];
            "sources"?: Record<string, {
                "title": string;
                "sourceName": string;
                "valueField": string;
                "context": ("no") | ("all") | number;
            }>;
            "masterData"?: Record<string, {
                "type": "ABSTRACT_ENTITY";
                "id": `abs_${string}`;
                "context": ("no") | ("all") | number;
            } | {
                "type": "ENTITY_SOURCE";
                "entity": string;
                "context": ("no") | ("all") | number;
            }>;
            "tools"?: (("resolveISOWeekDateTool") | {
                "search": string;
            })[];
            "inbox"?: {
                "folders"?: {
                    "if"?: Condition;
                    "path": string;
                }[];
            };
            "processor"?: {
                "type": "assistantCase";
                "pathToData": (string | number)[];
                "caseType": string;
                "withAcknowledgement": boolean;
                "file"?: (({
                    "filename": string;
                    "attrs"?: Record<string, string>;
                    "type": "attachment";
                    "attachmentId": string;
                } | {
                    "filename": string;
                    "attrs"?: Record<string, string>;
                    "type": "eml";
                    "source": {
                        "messageId": string;
                    };
                } | {
                    "filename": string;
                    "attrs"?: Record<string, string>;
                    "type": "xml";
                    "content": XmlNodeDefinition;
                }) | {
                    "filename": string;
                    "attrs"?: Record<string, string>;
                    "type": "zip";
                    "files": (({
                        "filename": string;
                        "attrs"?: Record<string, string>;
                        "type": "attachment";
                        "attachmentId": string;
                    } | {
                        "filename": string;
                        "attrs"?: Record<string, string>;
                        "type": "eml";
                        "source": {
                            "messageId": string;
                        };
                    } | {
                        "filename": string;
                        "attrs"?: Record<string, string>;
                        "type": "xml";
                        "content": XmlNodeDefinition;
                    }) | ({
                        "array": string;
                    } & ({
                        "each": {
                            "if"?: Condition;
                            "item": {
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "attachment";
                                "attachmentId": string;
                            } | {
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "eml";
                                "source": {
                                    "messageId": string;
                                };
                            } | {
                                "filename": string;
                                "attrs"?: Record<string, string>;
                                "type": "xml";
                                "content": XmlNodeDefinition;
                            };
                        };
                    })))[];
                }) & {
                    "bucket": string;
                    "key"?: string;
                };
                "review"?: {
                    "view": {
                        "body": UniversalAgentUIComponent[];
                        "footer"?: UniversalAgentUIComponent[];
                    };
                };
            };
        };
    } | null;
    "Search universal agent results": {
        "data": {
            "id": `wfr_${string}`;
            "createdAt": string;
            "result": {
                "meta": {
                    "traceId": string;
                };
                "result": SerializableObject;
            };
        };
        "references": {
            "type": string;
            "value": string;
        }[];
    }[];
};
export type BigNumberValue = string | number | BigNumber;
export type OrderConfirmationProcessingResultSchema = {
    "poId": string;
    "poNumber": string;
    "poDate": string;
    "ocNumber": string;
    "ocDate": string;
    "currency": string;
    "head": {
        "deliveryTerms": {
            "incoterms1": {
                "orderValue": string | null;
                "confirmedValue": string | null;
            };
            "incoterms2": {
                "orderValue": string | null;
                "confirmedValue": string | null;
            };
        };
        "paymentTerms": {
            "orderId": string | null;
            "confirmationId": string | null;
        };
        "supplierId": string | null;
    };
    "items": ({
        "type": "item_confirmed";
        "poItemId": string;
        "poPositionNumber": string;
        "ocPositionNumber": string | null;
        "ocSerialNumber": number;
        "materialId": string | null;
        "description": string;
        "supplierSku": {
            "orderValue": string | null;
            "confirmedValue": string | null;
        };
        "orderDeliverySchedules": {
            "quantity": number;
            "deliveryDate": string | null;
            "shipmentDate": string | null;
            "deliveryDateMentioned"?: boolean;
            "shipmentDateMentioned"?: boolean;
        }[];
        "confirmedDeliverySchedules": {
            "quantity": number;
            "deliveryDate": string | null;
            "shipmentDate": string | null;
            "deliveryDateMentioned"?: boolean;
            "shipmentDateMentioned"?: boolean;
        }[];
        "deliveryAddress": {
            "orderValue": string | null;
            "confirmedValue": string | null;
            "requiresUpdate": boolean;
        };
        "costStructure": {
            "orderQuantity": number | null;
            "orderUnitPrice": number | null;
            "orderPriceUnit": number | null;
            "orderTotalPrice": number | null;
            "orderQuantityUom": string | null;
            "orderPriceUom": string | null;
            "orderUomConversionCoefficient": number;
            "confirmedQuantity": number | null;
            "confirmedUnitPrice": number | null;
            "confirmedPriceUnit": number | null;
            "confirmedTotalPrice": number | null;
            "confirmedQuantityUom": string | null;
            "confirmedPriceUom": string | null;
            "uomConversionRequired": boolean;
            "convertedConfirmedQuantity": number | null;
            "convertedConfirmedUnitPrice": number | null;
            "convertedConfirmedPriceUnit": number | null;
            "convertedConfirmedTotalPrice": number | null;
        };
        "poExtra": {
            "id": string;
            "externalId": {
                "type": "Sap";
                "PricingDocument": string;
                "PricingDocumentItem": string | null;
                "PricingProcedureCounter": string;
                "PricingProcedureStep": string;
                "ConditionType": string;
            };
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
        }[];
        "ocExtra": {
            "serialNumber": number;
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "description": string;
            "amount": number;
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
        }[];
        "confirmations": {
            "positionNumber": string;
            "serialNumber": number;
            "deliveryDate": string | null;
            "quantity": number | null;
            "reference": string | null;
        }[];
    } | {
        "type": "new_item";
        "number": string | null;
        "serialNumber": number;
        "materialId": string | null;
        "description": string | null;
        "supplierSku": string | null;
        "deliverySchedules": {
            "quantity": number;
            "deliveryDate": string | null;
            "shipmentDate": string | null;
            "deliveryDateMentioned"?: boolean;
            "shipmentDateMentioned"?: boolean;
        }[];
        "deliveryAddress": string | null;
        "quantity": BigNumberValue | null;
        "quantityUom": string | null;
        "unitPrice": BigNumberValue | null;
        "priceUom": string | null;
        "priceUnit": BigNumberValue | null;
        "totalPrice": BigNumberValue | null;
        "extra": {
            "serialNumber": number;
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "description": string;
            "amount": number;
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
        }[];
    } | {
        "type": "missing_item";
        "id": string;
        "number": string;
        "materialId": string | null;
        "description": string;
        "supplierSku": string | null;
        "deliverySchedules": {
            "quantity": number;
            "deliveryDate": string | null;
            "shipmentDate": string | null;
            "deliveryDateMentioned"?: boolean;
            "shipmentDateMentioned"?: boolean;
        }[];
        "deliveryAddress": string | null;
        "quantity": BigNumberValue | null;
        "quantityUom": string | null;
        "unitPrice": BigNumberValue | null;
        "priceUom": string | null;
        "priceUnit": BigNumberValue | null;
        "uomConversionCoefficient": number;
        "totalPrice": BigNumberValue | null;
        "confirmations": {
            "positionNumber": string;
            "serialNumber": number;
            "deliveryDate": string | null;
            "quantity": number | null;
            "reference": string | null;
        }[];
        "extra": {
            "id": string;
            "externalId": {
                "type": "Sap";
                "PricingDocument": string;
                "PricingDocumentItem": string | null;
                "PricingProcedureCounter": string;
                "PricingProcedureStep": string;
                "ConditionType": string;
            };
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
        }[];
    })[];
    "poHeaderExtra": {
        "id": string;
        "externalId": {
            "type": "Sap";
            "PricingDocument": string;
            "PricingDocumentItem": string | null;
            "PricingProcedureCounter": string;
            "PricingProcedureStep": string;
            "ConditionType": string;
        };
        "calculation": {
            "class": "percentage";
            "rate": BigNumberValue | null;
            "appliedTo": "gross" | "net";
        } | {
            "class": "fixedAmount";
            "amount": BigNumberValue | null;
            "distributed": boolean;
        } | {
            "class": "quantityBased";
            "quantity": BigNumberValue;
            "quantityUom": string;
            "unitPrice": BigNumberValue;
            "priceUom": string;
            "perAmount": BigNumberValue;
            "uomConversionCoefficient": BigNumberValue;
        };
    }[];
    "ocHeaderExtra": {
        "serialNumber": number;
        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
        "description": string;
        "amount": number;
        "calculation": {
            "class": "percentage";
            "rate": BigNumberValue | null;
            "appliedTo": "gross" | "net";
        } | {
            "class": "fixedAmount";
            "amount": BigNumberValue | null;
            "distributed": boolean;
        } | {
            "class": "quantityBased";
            "quantity": BigNumberValue;
            "quantityUom": string;
            "unitPrice": BigNumberValue;
            "priceUom": string;
            "perAmount": BigNumberValue;
            "uomConversionCoefficient": BigNumberValue;
        };
    }[];
    "arbitrary"?: Record<string, Serializable>;
};
export type AssistantDecisionSchema = {
    "type": "accepted" | "edited";
    "approval": {
        "state": "no" | "pending" | "approved" | "declined";
        "comment"?: string;
        "includeInEmail"?: boolean;
        "origin": string;
    };
    "value": AssistantFieldValueSchema;
    "origin": string;
} | {
    "type": "rejected";
    "value": AssistantFieldValueSchema;
    "includeInEmail"?: boolean;
    "comment"?: string;
    "origin": string;
} | {
    "type": "flagged";
    "value"?: AssistantFieldValueSchema;
    "reason"?: "turianError" | "partialConfirmation";
    "origin": string;
} | {
    "type": "ignored";
    "value"?: AssistantFieldValueSchema;
    "origin": string;
};
export type MasterDataQuery = {
    "type": "ENTITY_SOURCE";
    "entity": string;
    "idValues": string[];
    "return"?: string;
} | {
    "type": "ABSTRACT_ENTITY";
    "id": `abs_${string}`;
    "identity": Record<string, string>;
    "return"?: string;
} | {
    "type": "ODATA";
    "name": string;
    "path"?: string;
    "query"?: Record<string, string>;
    "return"?: string | string[];
} | {
    "type": "ENDPOINT";
    "name": string;
    "params": {
        "method": "GET";
        "path"?: string;
        "headers"?: Record<string, string>;
        "cookies"?: Record<string, string>;
        "query"?: Record<string, string>;
    } | {
        "method": "POST";
        "path"?: string;
        "headers"?: Record<string, string>;
        "cookies"?: Record<string, string>;
        "query"?: Record<string, string>;
        "body": {
            "type": "JSON";
            "json": Serializable;
        } | {
            "type": "TEXT";
            "text": string;
        };
    };
    "return"?: string | string[];
};
export type UniversalAgentNode = {
    "schema": {
        "nullable"?: boolean;
        "description": string;
    } & ({
        "type": "string";
    } | {
        "type": "number";
    } | {
        "type": "boolean";
    } | {
        "type": "source_id";
        "source": string;
    } | {
        "type": "enum";
        "values": string[];
    } | {
        "type": "masterData";
        "name": string;
    } | {
        "type": "binding";
        "to": {
            "type": "masterData";
            "relativePath": (string | number)[];
            "value": string;
        };
    } | {
        "type": "object";
        "props": Record<string, UniversalAgentNode>;
    } | {
        "type": "array";
        "items": Record<string, UniversalAgentNode>;
    });
    "prompt"?: string;
    "validators"?: (("date") | {
        "name": "condition";
        "config": {
            "condition": Condition;
            "error": string;
            "validateNull"?: boolean;
        };
    } | {
        "name": "attachment";
        "config": {
            "attachmentId": string;
            "condition": Condition;
            "error": string;
        };
    })[];
    "itemValidators"?: (("date") | {
        "name": "condition";
        "config": {
            "condition": Condition;
            "error": string;
            "validateNull"?: boolean;
        };
    } | {
        "name": "attachment";
        "config": {
            "attachmentId": string;
            "condition": Condition;
            "error": string;
        };
    })[];
};
export type XmlNodeDefinition = {
    "name": string;
    "if"?: Condition;
    "attrs"?: Record<string, XmlAttrDefinition>;
    "children"?: XmlNodeDefinition[] | {
        "array": string;
        "each": {
            "item": XmlNodeDefinition;
        };
    } | string;
};
export type UniversalAgentUIComponent = {
    "type": "text";
    "label": string;
    "dataPath": (string | number)[];
} | {
    "type": "numeric";
    "label": string;
    "dataPath": (string | number)[];
    "precision"?: number;
    "currency"?: string;
} | {
    "type": "date";
    "label": string;
    "dataPath": (string | number)[];
    "format"?: string;
} | {
    "type": "button";
    "label": string;
    "action": "submitReview";
} | {
    "type": "section";
    "title": string;
    "icon"?: string;
} | {
    "type": "group";
    "element": UniversalAgentUIComponent[];
} | {
    "type": "collection";
    "itemsPath": (string | number)[];
    "element": UniversalAgentUIComponent[];
};
export type PurchaseOrderComparisonSchema = {
    "paymentTerms": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": string | null;
        "nextValue": string | null;
    };
    "incoterms1": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": string | null;
        "nextValue": string | null;
    };
    "incoterms2": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": string | null;
        "nextValue": string | null;
    };
    "items": (({
        "result": "missing";
        "orderId": string;
        "confirmationId": null;
    } | {
        "result": "new";
        "orderId": null;
        "confirmationId": number;
    } | {
        "result": "matched";
        "orderId": string;
        "confirmationId": number;
    }) & {
        "datapoints": {
            "unitPrice": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": BigNumberValue | null;
                "nextValue": BigNumberValue | null;
            };
            "netUnitPrice": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": BigNumberValue | null;
                "nextValue": BigNumberValue | null;
            };
            "quantity": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": BigNumberValue | null;
                "nextValue": BigNumberValue | null;
            };
            "deliveryAddress": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": string | null;
                "nextValue": string | null;
            };
            "supplierSku": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": string | null;
                "nextValue": string | null;
            };
            "confirmations": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": {
                    "quantity": BigNumberValue;
                    "deliveryDate": string | null;
                    "reference": string | null;
                }[] | null;
                "nextValue": {
                    "quantity": BigNumberValue;
                    "deliveryDate": string | null;
                    "reference": string | null;
                }[] | null;
            };
            "grossTotalPrice": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": BigNumberValue | null;
                "nextValue": BigNumberValue | null;
            };
            "netTotalPrice": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": BigNumberValue | null;
                "nextValue": BigNumberValue | null;
            };
            "extra": {
                "result": "equal" | "different" | "missing" | "new";
                "prevValue": {
                    "calculation": {
                        "class": "percentage";
                        "rate": BigNumberValue | null;
                        "appliedTo": "gross" | "net";
                    } | {
                        "class": "fixedAmount";
                        "amount": BigNumberValue | null;
                        "distributed": boolean;
                    } | {
                        "class": "quantityBased";
                        "quantity": BigNumberValue;
                        "quantityUom": string;
                        "unitPrice": BigNumberValue;
                        "priceUom": string;
                        "perAmount": BigNumberValue;
                        "uomConversionCoefficient": BigNumberValue;
                    };
                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                    "name"?: string;
                    "description"?: string;
                }[] | null;
                "nextValue": {
                    "calculation": {
                        "class": "percentage";
                        "rate": BigNumberValue | null;
                        "appliedTo": "gross" | "net";
                    } | {
                        "class": "fixedAmount";
                        "amount": BigNumberValue | null;
                        "distributed": boolean;
                    } | {
                        "class": "quantityBased";
                        "quantity": BigNumberValue;
                        "quantityUom": string;
                        "unitPrice": BigNumberValue;
                        "priceUom": string;
                        "perAmount": BigNumberValue;
                        "uomConversionCoefficient": BigNumberValue;
                    };
                    "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                    "name"?: string;
                    "description"?: string;
                }[] | null;
            };
            "scheduleLines": {
                "quantity": BigNumberValue;
                "deliveryDate": string | null;
            }[];
            "ignoredExtra": {
                "calculation": {
                    "class": "percentage";
                    "rate": BigNumberValue | null;
                    "appliedTo": "gross" | "net";
                } | {
                    "class": "fixedAmount";
                    "amount": BigNumberValue | null;
                    "distributed": boolean;
                } | {
                    "class": "quantityBased";
                    "quantity": BigNumberValue;
                    "quantityUom": string;
                    "unitPrice": BigNumberValue;
                    "priceUom": string;
                    "perAmount": BigNumberValue;
                    "uomConversionCoefficient": BigNumberValue;
                };
                "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
                "name"?: string;
                "description"?: string;
            }[];
        };
    })[];
    "grossTotalPrice": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": BigNumberValue | null;
        "nextValue": BigNumberValue | null;
    };
    "netTotalPrice": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": BigNumberValue | null;
        "nextValue": BigNumberValue | null;
    };
    "extra": {
        "result": "equal" | "different" | "missing" | "new";
        "prevValue": {
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "name"?: string;
            "description"?: string;
        }[] | null;
        "nextValue": {
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "name"?: string;
            "description"?: string;
        }[] | null;
    };
    "ignoredExtra": {
        "calculation": {
            "class": "percentage";
            "rate": BigNumberValue | null;
            "appliedTo": "gross" | "net";
        } | {
            "class": "fixedAmount";
            "amount": BigNumberValue | null;
            "distributed": boolean;
        } | {
            "class": "quantityBased";
            "quantity": BigNumberValue;
            "quantityUom": string;
            "unitPrice": BigNumberValue;
            "priceUom": string;
            "perAmount": BigNumberValue;
            "uomConversionCoefficient": BigNumberValue;
        };
        "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
        "name"?: string;
        "description"?: string;
    }[];
    "arbitrary": Record<string, Serializable>;
};
export type AssistantFieldValueSchema = {
    "type": "string";
    "value": string | null;
} | {
    "type": "boolean";
    "value": boolean | null;
} | {
    "type": "date";
    "value": string | null;
} | {
    "type": "price";
    "value": {
        "amount": {
            "amount": BigNumberValue | null;
            "currency": string | null;
        } | null;
        "quantity": {
            "amount": BigNumberValue | null;
            "unit": string | null;
        } | null;
        "extra"?: {
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
            "name"?: string;
        }[];
    };
} | {
    "type": "money";
    "value": {
        "amount": BigNumberValue | null;
        "currency": string | null;
    };
} | {
    "type": "quantity";
    "value": {
        "amount": BigNumberValue | null;
        "unit": string | null;
    };
} | {
    "type": "poStatus";
    "value": ("open" | "partiallyConfirmed" | "confirmed") | null;
} | {
    "type": "registry";
    "value": {
        "id": string | null;
        "source": "paymentTerms";
    };
} | {
    "type": "deliveryTerms";
    "value": {
        "incoterms1": string | null;
        "incoterms2": string | null;
    };
} | {
    "type": "scheduleLines";
    "value": {
        "schedules": {
            "quantity": {
                "amount": BigNumberValue | null;
                "unit": string | null;
            };
            "deliveryDate": string | null;
            "reference": string | null;
            "status"?: string;
        }[];
        "combineMode"?: "partial" | "reduction";
        "confirmations": {
            "quantity": {
                "amount": BigNumberValue | null;
                "unit": string | null;
            };
            "deliveryDate": string | null;
            "reference": string | null;
        }[];
    };
} | {
    "type": "extra";
    "value": {
        "lines": {
            "type": "unknown" | "discount" | "packing" | "freight" | "metalSurcharge" | "silverSurcharge" | "goldSurcharge" | "copperSurcharge" | "brassSurcharge" | "platinumSurcharge" | "aluminumSurcharge" | "leadSurcharge" | "tungstenSurcharge" | "alloySurcharge" | "paintingSurcharge" | "expressSurcharge" | "lowValueSurcharge" | "lowQuantitySurcharge" | "setupCostSurcharge" | "sampleCostSurcharge" | "inflationSurcharge" | "additionalPartsSurcharge" | "modelCostSurcharge" | "generalSurcharge";
            "calculation": {
                "class": "percentage";
                "rate": BigNumberValue | null;
                "appliedTo": "gross" | "net";
            } | {
                "class": "fixedAmount";
                "amount": BigNumberValue | null;
                "distributed": boolean;
            } | {
                "class": "quantityBased";
                "quantity": BigNumberValue;
                "quantityUom": string;
                "unitPrice": BigNumberValue;
                "priceUom": string;
                "perAmount": BigNumberValue;
                "uomConversionCoefficient": BigNumberValue;
            };
            "name"?: string;
        }[];
    };
};
export type XmlAttrDefinition = string | {
    "type": "date";
    "value": string;
    "sourceFormat": string;
    "targetFormat": string;
} | {
    "array": string;
    "one": {
        "if": Condition;
        "value": XmlAttrDefinition;
    };
};
